/**
 * JSON Structured PDF Service
 * 
 * This service handles the export of structured JSON reports to various formats (PDF, Word).
 * It's used by the UniversalJsonRenderer component for client-side export functionality.
 * 
 * EXPORT FLOW:
 * 1. User clicks export button in UniversalJsonRenderer
 * 2. UniversalJsonRenderer calls handleExport() which prepares metadata
 * 3. Based on format type, it calls either:
 *    - exportUniversalJsonToWord() for DOCX files
 *    - exportUniversalJsonToTextPDF() for PDF files (default)
 * 4. This service receives the complete structured JSON data from the report
 * 5. It formats and generates the document entirely client-side
 * 6. The file is downloaded directly to the user's device
 * 
 * KEY FEATURES:
 * - Fully client-side (no server calls)
 * - Preserves all report sections and formatting
 * - Handles citations and complex data structures
 * - Supports both text-based PDFs (selectable text) and image-based PDFs
 * 
 * SUPPORTED REPORT TYPES:
 * - Policy Comparison (with matrices, findings, recommendations)
 * - Loss Run Analysis (with risk management, trends, benchmarking, etc.)
 * - Research Analysis
 * - Risk Assessment
 * - Policy Analysis
 * 
 * @module jsonStructuredPdfService
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, AlignmentType, HeadingLevel, BorderStyle, WidthType, ShadingType } from 'docx';

// Import types from UniversalJsonRenderer
interface QuoteCitation {
  quote?: string;
  text?: string;
  source?: string;
  confidence?: number;
  docId?: string;
}

interface CitationText {
  text: string;
  citations: QuoteCitation[];
}

interface UniversalJsonResponse {
  responseMetadata: {
    workflowType: 'policy_comparison' | 'policy_analysis' | 'loss_run_analysis' | 'research_analysis' | 'risk_assessment' | 'insurance_requirements' | 'loss_run_analyzer' | 'commercial_lines_exec_renewal' | 'financial_analysis' | 'financial_analysis_comparative' | 'client_renewal_presentation' | 'rate_analysis' | 'portfolio_analyzer' | 'homeowners_comparison' | 'personal_umbrella';
    generatedAt: string;
    documentsAnalyzed: Array<{
      id: string;
      name: string;
      type?: string;
      policyType?: string;
    }>;
    processingTime?: number;
    confidence?: 'High' | 'Medium' | 'Low';
  };
  
  reportHighlights?: string[];
  
  executiveSummary: {
    paragraphs: Array<{
      content: CitationText | string;
    }>;
  };
  
  analysisMatrices: Array<{
    title: string;
    name?: string;
    type?: string;
    description?: string;
    headers: string[];
    rows: Array<Array<CitationText | string | any> | Record<string, any>>;
    data?: Array<any>;
    summary?: string;
    insights?: string[];
    includeIf?: string;
  }>;
  
  keyFindings: {
    summary: CitationText | string;
    findings: Array<{
      title?: string;
      category: string;
      finding?: CitationText | {
        observation?: CitationText;
        implication?: CitationText;
        recommendation?: CitationText;
        businessImpact?: CitationText;
      };
      description?: CitationText | string;
      impact?: 'Positive' | 'Negative' | 'Neutral' | 'Critical';
      impactLevel?: string;
      confidence?: 'High' | 'Medium' | 'Low';
      priority?: 'High' | 'Medium' | 'Low' | 'Immediate';
      riskLevel?: 'Critical' | 'High' | 'Medium' | 'Low';
      lossReductionPotential?: string;
    }>;
  };
  
  recommendations?: Array<{
    priority: 'Critical' | 'High' | 'Medium' | 'Low';
    category: string;
    action: CitationText | string;
    rationale: CitationText | string;
    timeline?: string;
  }>;
  
  strategicConsiderations?: Array<{
    consideration: string;
    analysis: string;
    options: string[];
    recommendedApproach: string;
    citations: Array<{
      text: string;
      source: string;
      confidence: number;
    }>;
  }>;
  
  riskMitigationStrategies?: Array<{
    risk: string;
    strategy: string;
    implementation: string;
    expectedReduction: string;
  }>;
  
  citationIndex?: {
    totalCitations: number;
    documentSources: Array<{
      docId: string;
      name: string;
      citationCount: number;
      baseUrl: string;
    }>;
  };
  
  // Loss run analysis specific sections
  riskManagementMatrix?: {
    title: string;
    recommendations: Array<{
      riskCategory: string;
      keyObservation: CitationText;
      businessImpact: CitationText;
      actionableRecommendation: CitationText;
      implementationSteps: string[];
      expectedOutcome: string;
      priorityLevel: string;
      estimatedCost: string;
      timeline: string;
    }>;
  };
  
  trendAnalysis?: {
    overallTrend: string;
    keyMetrics: {
      frequencyTrend: {
        direction: string;
        percentageChange: string;
        analysis: string;
      };
      severityTrend: {
        direction: string;
        percentageChange: string;
        analysis: string;
      };
      lossRatioTrend: {
        currentRatio: string;
        historicalAverage: string;
        analysis: string;
      };
    };
    emergingRisks: Array<{
      risk: string;
      indicators: string[];
      potentialImpact: string;
      mitigationStrategy: string;
    }>;
  };
  
  benchmarkingInsights?: {
    industryComparison: {
      performanceVsBenchmark: string;
      keyDifferentiators: string[];
      improvementOpportunities: string[];
    };
    internalComparison: {
      bestPerformingAreas: string[];
      worstPerformingAreas: string[];
      lessonsLearned: string[];
    };
  };
  
  costBenefitAnalysis?: Array<{
    recommendation: string;
    implementationCost: string;
    potentialSavings: string;
    paybackPeriod: string;
    nonFinancialBenefits: string[];
    implementationComplexity: string;
  }>;
  
  dataQualityAssessment?: {
    completenessScore?: string;
    dataGaps?: string[];           // Loss run format
    missingElements?: string[];    // Policy comparison format
    assumptionsMade?: string[];    // Make optional
    recommendedDataCollection?: string[];  // Loss run format
    recommendedFollowUp?: string[];        // Policy comparison format
    confidenceInAnalysis?: string;         // Optional for policy comparison
  };
}

export interface ExportOptions {
  filename?: string;
  includeMetadata?: boolean;
  pageOrientation?: 'portrait' | 'landscape';
  stripCitations?: boolean;
  includeTimestamp?: boolean;
  includeCoverPage?: boolean;
  customHeader?: string;
  formatType?: 'pdf' | 'docx' | 'html';
  dpi?: 96 | 150 | 300; // PDF export DPI options: standard (96), high (150), print quality (300)
}

/**
 * Main service class for handling structured JSON report exports
 * 
 * This class provides static methods for exporting reports to different formats.
 * All methods are client-side and do not require server communication.
 * 
 * TWO EXPORT SYSTEMS EXPLAINED:
 * 
 * 1. HTML-BASED EXPORT (exportUniversalJsonToPDF):
 *    - Uses html2canvas to render DOM elements to images
 *    - Creates image-based PDFs (non-selectable text)
 *    - Better for complex layouts and visual fidelity
 *    - Larger file sizes due to images
 *    - DEPRECATED: Legacy method maintained for compatibility
 * 
 * 2. TEXT-BASED EXPORT (exportUniversalJsonToTextPDF):
 *    - Uses jsPDF text API to generate native PDF text
 *    - Creates text-based PDFs with selectable text
 *    - Smaller file sizes, better accessibility
 *    - Currently ACTIVE method used by UniversalJsonRenderer
 *    - Handles all loss run analysis sections (fixed in recent updates)
 * 
 * HOW THE ACTIVE SYSTEM WORKS:
 * 1. User clicks "Export PDF" in UniversalJsonRenderer
 * 2. UniversalJsonRenderer calls exportUniversalJsonToTextPDF()
 * 3. Method receives complete structured JSON data from AI analysis
 * 4. Processes each section sequentially (Executive Summary → Analysis Matrices → etc.)
 * 5. Generates selectable text PDF using jsPDF library
 * 6. File downloads directly to user's device
 * 
 * RECENT FIXES:
 * - Added all missing loss run analysis sections (Risk Management, Trend Analysis, etc.)
 * - Fixed {value: 0, citations: []} object handling in matrices
 * - Enhanced documentation and inline comments
 */
export class JsonStructuredPdfService {
  
  /**
   * Export UniversalJsonRenderer output using the proven html2canvas approach from documentation
   */
  static async exportUniversalJsonToPDF(
    data: UniversalJsonResponse,
    metadata: any,
    options: ExportOptions = {}
  ): Promise<void> {
    const {
      filename = `comparison-report-${Date.now()}.pdf`,
      includeMetadata = true,
      pageOrientation = 'portrait',
      stripCitations = true, // Default to true - no citations in PDF
      includeTimestamp = true,
      includeCoverPage = false,
      customHeader
    } = options;

    try {
      console.log('🔥 PDF EXPORT STARTED - Using proven html2canvas approach');
      console.log('📊 Export options:', options);
      console.log('📋 Full data structure keys:', Object.keys(data));
      
      // 1. Generate HTML content using the proven approach
      const htmlContent = this.buildHtmlFromJson_DEPRECATED(data, stripCitations, customHeader);
      
      // 2. Create temporary HTML element for rendering with proper content width
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      tempDiv.style.width = '170mm'; // A4 width minus margins (210mm - 40mm)
      tempDiv.style.padding = '0'; // No padding here, margins handled in PDF
      tempDiv.style.backgroundColor = '#ffffff';
      tempDiv.style.fontFamily = 'Arial, sans-serif';
      tempDiv.style.fontSize = '12px';
      tempDiv.style.lineHeight = '1.5';
      tempDiv.style.overflow = 'visible';
      tempDiv.style.wordWrap = 'break-word';
      tempDiv.style.overflowWrap = 'break-word';
      document.body.appendChild(tempDiv);

      // 3. Render HTML to canvas using html2canvas (proven working approach)
      const canvas = await html2canvas(tempDiv, {
        scale: 2,              // High resolution as per documentation
        useCORS: true,         // Handle cross-origin images
        logging: false,        // Disable debug logging
        allowTaint: false,     // Strict security
        backgroundColor: '#ffffff',
        width: tempDiv.scrollWidth,
        height: tempDiv.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: tempDiv.scrollWidth,
        windowHeight: tempDiv.scrollHeight
      });

      // 4. Create PDF and add canvas content with proper page breaking
      const pdf = new jsPDF({
        orientation: pageOrientation,
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20; // 20mm margins
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);
      
      // Convert canvas to image data
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      
      // Calculate scaling to fit content within margins
      const canvasAspectRatio = canvas.width / canvas.height;
      const imgWidth = contentWidth;
      const imgHeight = contentWidth / canvasAspectRatio;
      
      // Create pages with proper content splitting
      let currentY = 0;
      let pageNumber = 1;
      
      while (currentY < imgHeight) {
        if (pageNumber > 1) {
          pdf.addPage();
        }
        
        // Calculate the portion of the image to show on this page
        const remainingHeight = imgHeight - currentY;
        const pageImageHeight = Math.min(remainingHeight, contentHeight);
        
        // Create a temporary canvas for this page's content
        const pageCanvas = document.createElement('canvas');
        const pageCtx = pageCanvas.getContext('2d');
        
        // Set canvas size for this page
        pageCanvas.width = canvas.width;
        pageCanvas.height = (canvas.height * pageImageHeight) / imgHeight;
        
        // Draw the appropriate portion of the original canvas
        const sourceY = (canvas.height * currentY) / imgHeight;
        const sourceHeight = (canvas.height * pageImageHeight) / imgHeight;
        
        pageCtx?.drawImage(
          canvas,
          0, sourceY, canvas.width, sourceHeight,  // Source rectangle
          0, 0, pageCanvas.width, pageCanvas.height // Destination rectangle
        );
        
        // Add this page's image to PDF
        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
        pdf.addImage(
          pageImgData,
          'JPEG',
          margin,
          margin,
          imgWidth,
          pageImageHeight
        );
        
        // Add page number
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          `Page ${pageNumber}`, 
          pageWidth - margin - 20, 
          pageHeight - 10
        );
        
        currentY += pageImageHeight;
        pageNumber++;
      }

      // 5. Add metadata footer if requested
      if (includeMetadata) {
        this.addMetadataFooter(pdf, metadata, includeTimestamp);
      }

      // 6. Clean up temporary element
      document.body.removeChild(tempDiv);

      console.log('✅ PDF EXPORT COMPLETED using html2canvas approach');
      pdf.save(filename);

    } catch (error) {
      console.error('❌ PDF export failed:', error);
      throw new Error(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Build HTML content from UniversalJsonResponse structure (DEPRECATED - Legacy method not used by native PDF export)
   */
  private static buildHtmlFromJson_DEPRECATED(data: UniversalJsonResponse, stripCitations: boolean, customHeader?: string): string {
    let html = `
      <style>
        @page {
          margin: 1in;
          size: letter;
        }
        
        * {
          word-wrap: break-word;
          overflow-wrap: break-word;
          hyphens: auto;
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        
        body {
          font-family: 'Arial', sans-serif;
          font-size: 12px;
          line-height: 1.6;
          color: #000000;
          background: #ffffff;
          margin: 0;
          padding: 15px;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
          orphans: 3;
          widows: 3;
        }
        
        p, div, span {
          word-break: normal;
          overflow-wrap: break-word;
          hyphens: auto;
          line-height: 1.6;
          margin: 8px 0;
          word-spacing: normal;
          letter-spacing: normal;
        }
        
        h1, h2, h3, h4, h5, h6 {
          page-break-after: avoid !important;
          break-after: avoid !important;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          word-wrap: break-word;
          overflow-wrap: break-word;
          margin: 16px 0 12px 0;
          line-height: 1.3;
        }
        
        h1 { font-size: 24px; font-weight: bold; }
        h2 { font-size: 20px; font-weight: bold; }
        h3 { font-size: 16px; font-weight: bold; }
        
        table {
          table-layout: fixed;
          width: 100% !important;
          border-collapse: collapse;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin: 12px 0;
          border: 1px solid #e5e7eb;
        }
        
        /* Keep table headers with at least one row */
        thead {
          display: table-header-group;
        }
        
        th, td {
          word-break: normal !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          hyphens: auto !important;
          border: 1px solid #e5e7eb;
          padding: 8px;
          vertical-align: top;
          font-size: 11px;
          line-height: 1.4;
          white-space: normal;
        }
        
        th {
          background: #f3f4f6;
          font-weight: bold;
          text-align: left;
        }
        
        tr {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        
        /* Ensure table rows stay together */
        tbody tr {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        
        .matrix-container {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin-bottom: 20px;
        }
        
        .executive-summary {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin-bottom: 20px;
        }
        
        .key-finding {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin-bottom: 15px;
          padding: 12px;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          background: #ffffff;
        }
        
        .recommendation {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin-bottom: 15px;
          padding: 12px;
          border: 1px solid #fbbf24;
          border-radius: 4px;
          background: #fffbeb;
        }
        
        .content-block {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin-bottom: 15px;
          word-wrap: break-word;
          overflow-wrap: break-word;
          position: relative;
        }
        
        /* Force page break before large sections to keep them together */
        .force-page-break {
          page-break-before: always !important;
          break-before: always !important;
        }
        
        /* Keep small boxes together */
        .keep-together {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          display: inline-block;
          width: 100%;
        }
        
        /* Ensure long words break properly only when necessary */
        .long-text {
          word-break: break-word;
          overflow-wrap: break-word;
          hyphens: auto;
        }
        
        /* Better spacing for lists */
        ul, ol {
          margin: 8px 0;
          padding-left: 20px;
        }
        
        li {
          margin: 4px 0;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
      </style>
    `;

    // Header
    html += `
      <div style="text-align: center; margin-bottom: 40px; page-break-inside: avoid;">
        <h1 style="color: #000000; font-size: 32px; margin-bottom: 12px; font-weight: bold; line-height: 1.2;">
          ${customHeader || this.getWorkflowTypeTitle(data.responseMetadata.workflowType)}
        </h1>
        <p style="color: #4b5563; font-size: 16px; margin: 0;">Generated: ${new Date(data.responseMetadata.generatedAt).toLocaleString()}</p>
      </div>
    `;

    // Analysis Overview removed as requested

    // Report Highlights (if exists)
    if (data.reportHighlights && Array.isArray(data.reportHighlights)) {
      html += `
        <div style="margin-bottom: 30px;">
          <h2 style="color: #000000; font-size: 24px; margin-bottom: 20px; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px; font-weight: bold;">Key Highlights</h2>
          <div style="background: #eff6ff; padding: 20px; border-radius: 8px; border: 1px solid #dbeafe;">
            ${data.reportHighlights.map((highlight: string) => 
              `<p style="color: #1e40af; font-size: 16px; line-height: 1.8; margin: 12px 0;">• ${highlight}</p>`
            ).join('')}
          </div>
        </div>
      `;
    }

    // Executive Summary
    if (data.executiveSummary?.paragraphs && data.executiveSummary.paragraphs.length > 0) {
      html += `
        <div class="executive-summary content-block keep-together" style="margin-bottom: 25px; page-break-inside: avoid !important; break-inside: avoid !important;">
          <h2 style="color: #000000; font-size: 22px; margin-bottom: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">Executive Summary</h2>
          ${data.executiveSummary.paragraphs.map((paragraph: any) => 
            `<div class="content-block" style="background: #f0f9ff; padding: 12px; border-left: 3px solid #3b82f6; margin: 10px 0; border-radius: 0 4px 4px 0; page-break-inside: avoid !important; break-inside: avoid !important;">
              <p style="color: #000000; font-size: 15px; line-height: 1.6; margin: 0;">
                ${this.processCitationText(paragraph.content, stripCitations)}
              </p>
            </div>`
          ).join('')}
        </div>
      `;
    }

    // Analysis Matrices
    if (data.analysisMatrices?.length) {
      html += `
        <div style="margin-bottom: 25px;">
          <h2 style="color: #000000; font-size: 22px; margin-bottom: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; font-weight: bold;">Detailed Analysis</h2>
          ${data.analysisMatrices.map((matrix: any) => this.buildMatrixHtml(matrix, stripCitations)).join('')}
        </div>
      `;
    }

    // Key Findings
    if (data.keyFindings?.findings?.length) {
      html += `
        <div style="margin-bottom: 25px;">
          <h2 style="color: #000000; font-size: 22px; margin-bottom: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; font-weight: bold; page-break-after: avoid;">Key Findings</h2>
          ${data.keyFindings.summary ? 
            `<div style="background: #f9fafb; padding: 10px; border-radius: 4px; margin-bottom: 15px; page-break-inside: avoid;">
              <p style="color: #374151; font-size: 14px; line-height: 1.6; font-style: italic; margin: 0;">
                ${this.processCitationText(data.keyFindings.summary, stripCitations)}
              </p>
            </div>` : ''
          }
          ${data.keyFindings.findings.map((finding, index) => 
            `<div class="key-finding content-block keep-together" style="margin: 10px 0; padding: 12px; border: 1px solid #e5e7eb; border-radius: 4px; background: #ffffff; page-break-inside: avoid !important; break-inside: avoid !important;">
              <div style="margin-bottom: 8px;">
                <h3 style="color: #000000; font-size: 16px; margin: 0 0 10px 0; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">
                  ${index + 1}. ${finding.title || finding.category}
                </h3>
              </div>
              <p style="color: #1f2937; font-size: 14px; line-height: 1.6; margin: 0;">
                ${this.processCitationText(finding.finding || finding.description || '', stripCitations)}
              </p>
            </div>`
          ).join('')}
        </div>
      `;
    }

    // Strategic Considerations
    if (data.strategicConsiderations?.length) {
      html += `
        <div class="content-block" style="margin-bottom: 25px; page-break-inside: avoid !important; break-inside: avoid !important;">
          <h2 style="color: #000000; font-size: 22px; margin-bottom: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">Strategic Considerations</h2>
          ${data.strategicConsiderations.map((consideration, index) => 
            `<div class="consideration content-block keep-together" style="margin: 10px 0; padding: 12px; border: 1px solid #a855f7; border-radius: 4px; background: #faf5ff; page-break-inside: avoid !important; break-inside: avoid !important;">
              <h3 style="color: #7c3aed; font-size: 16px; margin: 0 0 10px 0; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">
                ${consideration.consideration}
              </h3>
              <div style="margin-bottom: 8px;">
                <p style="color: #581c87; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Analysis:</p>
                <p style="color: #6b21a8; font-size: 14px; line-height: 1.6; margin: 0 0 8px 0;">
                  ${consideration.analysis}
                </p>
              </div>
              ${consideration.options?.length ? `
                <div style="margin-bottom: 8px;">
                  <p style="color: #581c87; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Options:</p>
                  <ul style="color: #6b21a8; font-size: 14px; line-height: 1.6; margin: 0 0 8px 0; padding-left: 20px;">
                    ${consideration.options.map((option: string) => `<li>${option}</li>`).join('')}
                  </ul>
                </div>` : ''}
              <div>
                <p style="color: #581c87; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Recommended Approach:</p>
                <p style="color: #6b21a8; font-size: 14px; line-height: 1.6; margin: 0; font-weight: 500;">
                  ${consideration.recommendedApproach}
                </p>
              </div>
            </div>`
          ).join('')}
        </div>
      `;
    }

    // Risk Mitigation Strategies
    if (data.riskMitigationStrategies?.length) {
      html += `
        <div class="content-block" style="margin-bottom: 25px; page-break-inside: avoid !important; break-inside: avoid !important;">
          <h2 style="color: #000000; font-size: 22px; margin-bottom: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">Risk Mitigation Strategies</h2>
          ${data.riskMitigationStrategies.map((strategy, index) => 
            `<div class="strategy content-block keep-together" style="margin: 10px 0; padding: 12px; border: 1px solid #ef4444; border-radius: 4px; background: #fef2f2; page-break-inside: avoid !important; break-inside: avoid !important;">
              <h3 style="color: #dc2626; font-size: 16px; margin: 0 0 10px 0; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">
                ${strategy.risk}
              </h3>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 8px;">
                <div>
                  <p style="color: #7f1d1d; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Strategy:</p>
                  <p style="color: #991b1b; font-size: 14px; line-height: 1.6; margin: 0;">
                    ${strategy.strategy}
                  </p>
                </div>
                <div>
                  <p style="color: #7f1d1d; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Implementation:</p>
                  <p style="color: #991b1b; font-size: 14px; line-height: 1.6; margin: 0;">
                    ${strategy.implementation}
                  </p>
                </div>
              </div>
              <div style="border-top: 1px solid #fecaca; padding-top: 8px;">
                <p style="color: #7f1d1d; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Expected Risk Reduction:</p>
                <p style="color: #991b1b; font-size: 14px; line-height: 1.6; margin: 0; font-weight: 500;">
                  ${strategy.expectedReduction}
                </p>
              </div>
            </div>`
          ).join('')}
        </div>
      `;
    }

    // Recommendations
    if (data.recommendations?.length) {
      html += `
        <div class="content-block" style="margin-bottom: 25px; page-break-inside: avoid !important; break-inside: avoid !important;">
          <h2 style="color: #000000; font-size: 22px; margin-bottom: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important; page-break-inside: avoid !important; break-inside: avoid !important;">Recommendations</h2>
          ${data.recommendations.map((rec, index) => 
            `<div class="recommendation content-block keep-together" style="margin: 10px 0; padding: 12px; border: 1px solid #fbbf24; border-radius: 4px; background: #fffbeb; page-break-inside: avoid !important; break-inside: avoid !important;">
              <div style="margin-bottom: 8px;">
                <h3 style="color: #92400e; font-size: 16px; margin: 0 0 10px 0; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">
                  ${index + 1}. ${rec.category}
                </h3>
              </div>
              <div style="margin-bottom: 8px;">
                <p style="color: #78350f; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Action:</p>
                <p style="color: #92400e; font-size: 14px; line-height: 1.6; margin: 0;">
                  ${this.processCitationText(rec.action, stripCitations)}
                </p>
              </div>
              <div>
                <p style="color: #78350f; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Rationale:</p>
                <p style="color: #92400e; font-size: 14px; line-height: 1.6; margin: 0;">
                  ${this.processCitationText(rec.rationale, stripCitations)}
                </p>
              </div>
            </div>`
          ).join('')}
        </div>
      `;
    }

    // Risk Management Matrix
    console.log('🔧 PDF Export - Processing Risk Management Matrix:', {
      exists: !!data.riskManagementMatrix,
      hasRecommendations: !!data.riskManagementMatrix?.recommendations,
      recommendationsLength: data.riskManagementMatrix?.recommendations?.length || 0
    });
    if (data.riskManagementMatrix?.recommendations?.length) {
      html += `
        <div class="content-block" style="margin-bottom: 25px; page-break-inside: avoid !important; break-inside: avoid !important;">
          <h2 style="color: #000000; font-size: 22px; margin-bottom: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">${data.riskManagementMatrix.title || 'Risk Management Matrix'}</h2>
          ${data.riskManagementMatrix.recommendations.map((rec, index) => 
            `<div class="risk-management content-block keep-together" style="margin: 10px 0; padding: 12px; border: 1px solid #ef4444; border-radius: 4px; background: #fef2f2; page-break-inside: avoid !important; break-inside: avoid !important;">
              <h3 style="color: #dc2626; font-size: 16px; margin: 0 0 10px 0; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">
                ${rec.riskCategory}
              </h3>
              <div style="margin-bottom: 8px;">
                <p style="color: #991b1b; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Key Observation:</p>
                <p style="color: #dc2626; font-size: 14px; line-height: 1.6; margin: 0 0 8px 0;">
                  ${this.processCitationText(rec.keyObservation, stripCitations)}
                </p>
              </div>
              <div style="margin-bottom: 8px;">
                <p style="color: #991b1b; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Business Impact:</p>
                <p style="color: #dc2626; font-size: 14px; line-height: 1.6; margin: 0 0 8px 0;">
                  ${this.processCitationText(rec.businessImpact, stripCitations)}
                </p>
              </div>
              <div style="margin-bottom: 8px;">
                <p style="color: #991b1b; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Actionable Recommendation:</p>
                <p style="color: #dc2626; font-size: 14px; line-height: 1.6; margin: 0 0 8px 0;">
                  ${this.processCitationText(rec.actionableRecommendation, stripCitations)}
                </p>
              </div>
              ${rec.implementationSteps?.length ? `
                <div style="margin-bottom: 8px;">
                  <p style="color: #991b1b; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Implementation Steps:</p>
                  <ol style="color: #dc2626; font-size: 14px; line-height: 1.6; margin: 0 0 8px 0; padding-left: 20px;">
                    ${rec.implementationSteps.map((step: string) => `<li>${step}</li>`).join('')}
                  </ol>
                </div>` : ''}
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 8px; padding-top: 8px; border-top: 1px solid #fca5a5;">
                <div>
                  <p style="color: #991b1b; font-size: 12px; font-weight: bold; margin: 0 0 2px 0;">Expected Outcome:</p>
                  <p style="color: #dc2626; font-size: 13px; margin: 0;">${rec.expectedOutcome}</p>
                </div>
                <div>
                  <p style="color: #991b1b; font-size: 12px; font-weight: bold; margin: 0 0 2px 0;">Estimated Cost:</p>
                  <p style="color: #dc2626; font-size: 13px; margin: 0;">${rec.estimatedCost}</p>
                </div>
              </div>
            </div>`
          ).join('')}
        </div>
      `;
    }

    // Trend Analysis
    console.log('🔧 PDF Export - Processing Trend Analysis:', {
      exists: !!data.trendAnalysis,
      hasOverallTrend: !!data.trendAnalysis?.overallTrend,
      hasKeyMetrics: !!data.trendAnalysis?.keyMetrics
    });
    if (data.trendAnalysis) {
      html += `
        <div class="content-block" style="margin-bottom: 25px; page-break-inside: avoid !important; break-inside: avoid !important;">
          <h2 style="color: #000000; font-size: 22px; margin-bottom: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">Trend Analysis</h2>
          <div class="trend-overview" style="margin: 16px 0; padding: 12px; border: 1px solid #3b82f6; border-radius: 4px; background: #eff6ff;">
            <h3 style="color: #1d4ed8; font-size: 16px; margin: 0 0 8px 0; font-weight: bold;">Overall Trend: ${data.trendAnalysis.overallTrend}</h3>
          </div>
          ${data.trendAnalysis.keyMetrics ? `
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 16px 0;">
              ${data.trendAnalysis.keyMetrics.frequencyTrend ? `
                <div style="padding: 12px; border: 1px solid #d1d5db; border-radius: 4px; background: #f9fafb;">
                  <h4 style="color: #374151; font-size: 14px; font-weight: bold; margin: 0 0 8px 0;">Frequency Trend</h4>
                  <p style="color: #3b82f6; font-size: 18px; font-weight: bold; margin: 0 0 4px 0;">${data.trendAnalysis.keyMetrics.frequencyTrend.direction}</p>
                  <p style="color: #6b7280; font-size: 14px; margin: 0 0 8px 0;">${data.trendAnalysis.keyMetrics.frequencyTrend.percentageChange}</p>
                  <p style="color: #4b5563; font-size: 12px; margin: 0;">${data.trendAnalysis.keyMetrics.frequencyTrend.analysis}</p>
                </div>` : ''}
              ${data.trendAnalysis.keyMetrics.severityTrend ? `
                <div style="padding: 12px; border: 1px solid #d1d5db; border-radius: 4px; background: #f9fafb;">
                  <h4 style="color: #374151; font-size: 14px; font-weight: bold; margin: 0 0 8px 0;">Severity Trend</h4>
                  <p style="color: #10b981; font-size: 18px; font-weight: bold; margin: 0 0 4px 0;">${data.trendAnalysis.keyMetrics.severityTrend.direction}</p>
                  <p style="color: #6b7280; font-size: 14px; margin: 0 0 8px 0;">${data.trendAnalysis.keyMetrics.severityTrend.percentageChange}</p>
                  <p style="color: #4b5563; font-size: 12px; margin: 0;">${data.trendAnalysis.keyMetrics.severityTrend.analysis}</p>
                </div>` : ''}
              ${data.trendAnalysis.keyMetrics.lossRatioTrend ? `
                <div style="padding: 12px; border: 1px solid #d1d5db; border-radius: 4px; background: #f9fafb;">
                  <h4 style="color: #374151; font-size: 14px; font-weight: bold; margin: 0 0 8px 0;">Loss Ratio</h4>
                  <p style="color: #f59e0b; font-size: 18px; font-weight: bold; margin: 0 0 4px 0;">${data.trendAnalysis.keyMetrics.lossRatioTrend.currentRatio}</p>
                  <p style="color: #6b7280; font-size: 12px; margin: 0 0 8px 0;">Historical: ${data.trendAnalysis.keyMetrics.lossRatioTrend.historicalAverage}</p>
                  <p style="color: #4b5563; font-size: 12px; margin: 0;">${data.trendAnalysis.keyMetrics.lossRatioTrend.analysis}</p>
                </div>` : ''}
            </div>` : ''}
          ${data.trendAnalysis.emergingRisks?.length ? `
            <div style="margin-top: 16px;">
              <h3 style="color: #374151; font-size: 16px; font-weight: bold; margin: 0 0 12px 0;">Emerging Risks</h3>
              ${data.trendAnalysis.emergingRisks.map(risk => `
                <div style="margin: 8px 0; padding: 12px; border: 1px solid #f59e0b; border-radius: 4px; background: #fffbeb;">
                  <h4 style="color: #d97706; font-size: 14px; font-weight: bold; margin: 0 0 8px 0;">${risk.risk}</h4>
                  <p style="color: #92400e; font-size: 12px; margin: 0 0 4px 0;"><strong>Indicators:</strong> ${risk.indicators.join(', ')}</p>
                  <p style="color: #92400e; font-size: 12px; margin: 0 0 4px 0;"><strong>Potential Impact:</strong> ${risk.potentialImpact}</p>
                  <p style="color: #92400e; font-size: 12px; margin: 0;"><strong>Mitigation:</strong> ${risk.mitigationStrategy}</p>
                </div>
              `).join('')}
            </div>` : ''}
        </div>
      `;
    }

    // Benchmarking Insights
    console.log('🔧 PDF Export - Processing Benchmarking Insights:', {
      exists: !!data.benchmarkingInsights,
      hasIndustryComparison: !!data.benchmarkingInsights?.industryComparison,
      hasInternalComparison: !!data.benchmarkingInsights?.internalComparison
    });
    if (data.benchmarkingInsights) {
      html += `
        <div class="content-block" style="margin-bottom: 25px; page-break-inside: avoid !important; break-inside: avoid !important;">
          <h2 style="color: #000000; font-size: 22px; margin-bottom: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">Benchmarking Insights</h2>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
            ${data.benchmarkingInsights.industryComparison ? `
              <div style="padding: 12px; border: 1px solid #10b981; border-radius: 4px; background: #ecfdf5;">
                <h3 style="color: #047857; font-size: 16px; font-weight: bold; margin: 0 0 12px 0;">Industry Comparison</h3>
                <div style="margin-bottom: 8px;">
                  <p style="color: #065f46; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Performance vs Benchmark:</p>
                  <p style="color: #047857; font-size: 14px; margin: 0 0 8px 0;">${data.benchmarkingInsights.industryComparison.performanceVsBenchmark}</p>
                </div>
                <div style="margin-bottom: 8px;">
                  <p style="color: #065f46; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Key Differentiators:</p>
                  <ul style="color: #047857; font-size: 14px; margin: 0 0 8px 0; padding-left: 20px;">
                    ${data.benchmarkingInsights.industryComparison.keyDifferentiators.map(item => `<li>${item}</li>`).join('')}
                  </ul>
                </div>
                <div>
                  <p style="color: #065f46; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Improvement Opportunities:</p>
                  <ul style="color: #047857; font-size: 14px; margin: 0; padding-left: 20px;">
                    ${data.benchmarkingInsights.industryComparison.improvementOpportunities.map(item => `<li>${item}</li>`).join('')}
                  </ul>
                </div>
              </div>` : ''}
            ${data.benchmarkingInsights.internalComparison ? `
              <div style="padding: 12px; border: 1px solid #8b5cf6; border-radius: 4px; background: #f5f3ff;">
                <h3 style="color: #7c3aed; font-size: 16px; font-weight: bold; margin: 0 0 12px 0;">Internal Comparison</h3>
                <div style="margin-bottom: 8px;">
                  <p style="color: #6b21a8; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Best Performing Areas:</p>
                  <ul style="color: #7c3aed; font-size: 14px; margin: 0 0 8px 0; padding-left: 20px;">
                    ${data.benchmarkingInsights.internalComparison.bestPerformingAreas.map(item => `<li>${item}</li>`).join('')}
                  </ul>
                </div>
                <div style="margin-bottom: 8px;">
                  <p style="color: #6b21a8; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Worst Performing Areas:</p>
                  <ul style="color: #7c3aed; font-size: 14px; margin: 0 0 8px 0; padding-left: 20px;">
                    ${data.benchmarkingInsights.internalComparison.worstPerformingAreas.map(item => `<li>${item}</li>`).join('')}
                  </ul>
                </div>
                <div>
                  <p style="color: #6b21a8; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Lessons Learned:</p>
                  <ul style="color: #7c3aed; font-size: 14px; margin: 0; padding-left: 20px;">
                    ${data.benchmarkingInsights.internalComparison.lessonsLearned.map(item => `<li>${item}</li>`).join('')}
                  </ul>
                </div>
              </div>` : ''}
          </div>
        </div>
      `;
    }

    // Cost-Benefit Analysis
    if (data.costBenefitAnalysis?.length) {
      html += `
        <div class="content-block" style="margin-bottom: 25px; page-break-inside: avoid !important; break-inside: avoid !important;">
          <h2 style="color: #000000; font-size: 22px; margin-bottom: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">Cost-Benefit Analysis</h2>
          ${data.costBenefitAnalysis.map(analysis => `
            <div style="margin: 16px 0; padding: 12px; border: 1px solid #f59e0b; border-radius: 4px; background: #fffbeb;">
              <h3 style="color: #d97706; font-size: 16px; font-weight: bold; margin: 0 0 12px 0;">${analysis.recommendation}</h3>
              <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 12px;">
                <div>
                  <p style="color: #92400e; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Implementation Cost:</p>
                  <p style="color: #dc2626; font-size: 16px; font-weight: bold; margin: 0;">${analysis.implementationCost}</p>
                </div>
                <div>
                  <p style="color: #92400e; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Potential Savings:</p>
                  <p style="color: #059669; font-size: 16px; font-weight: bold; margin: 0;">${analysis.potentialSavings}</p>
                </div>
                <div>
                  <p style="color: #92400e; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Payback Period:</p>
                  <p style="color: #2563eb; font-size: 16px; font-weight: bold; margin: 0;">${analysis.paybackPeriod}</p>
                </div>
              </div>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                <div>
                  <p style="color: #92400e; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Non-Financial Benefits:</p>
                  <ul style="color: #d97706; font-size: 14px; margin: 0; padding-left: 20px;">
                    ${analysis.nonFinancialBenefits.map(benefit => `<li>${benefit}</li>`).join('')}
                  </ul>
                </div>
                <div>
                  <p style="color: #92400e; font-size: 13px; font-weight: bold; margin: 0 0 4px 0;">Implementation Complexity:</p>
                  <p style="color: ${analysis.implementationComplexity === 'Low' ? '#059669' : analysis.implementationComplexity === 'Medium' ? '#d97706' : '#dc2626'}; font-size: 14px; font-weight: bold; margin: 0;">${analysis.implementationComplexity}</p>
                </div>
              </div>
            </div>
          `).join('')}
        </div>
      `;
    }

    // Data Quality Assessment
    if (data.dataQualityAssessment) {
      const gaps = (data.dataQualityAssessment as any).dataGaps || (data.dataQualityAssessment as any).missingElements || [];
      const followUp = (data.dataQualityAssessment as any).recommendedDataCollection || (data.dataQualityAssessment as any).recommendedFollowUp || [];
      const assumptions = (data.dataQualityAssessment as any).assumptionsMade || [];
      const hasConfidence = !!(data.dataQualityAssessment as any).confidenceInAnalysis;
      
      html += `
        <div class="content-block" style="margin-bottom: 25px; page-break-inside: avoid !important; break-inside: avoid !important;">
          <h2 style="color: #000000; font-size: 22px; margin-bottom: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">Data Quality Assessment</h2>
          <div style="margin: 16px 0; padding: 12px; border: 1px solid #6b7280; border-radius: 4px; background: #f9fafb;">
            ${(data.dataQualityAssessment as any).completenessScore || hasConfidence ? `
            <div style="display: grid; grid-template-columns: ${hasConfidence ? '1fr 1fr' : '1fr'}; gap: 16px; margin-bottom: 16px;">
              ${(data.dataQualityAssessment as any).completenessScore ? `
              <div>
                <h3 style="color: #374151; font-size: 16px; font-weight: bold; margin: 0 0 8px 0;">Completeness Score</h3>
                <p style="color: #2563eb; font-size: 24px; font-weight: bold; margin: 0;">${(data.dataQualityAssessment as any).completenessScore}</p>
              </div>` : ''}
              ${hasConfidence ? `
              <div>
                <h3 style="color: #374151; font-size: 16px; font-weight: bold; margin: 0 0 8px 0;">Confidence in Analysis</h3>
                <p style="color: ${(data.dataQualityAssessment as any).confidenceInAnalysis === 'High' ? '#059669' : (data.dataQualityAssessment as any).confidenceInAnalysis === 'Medium' ? '#d97706' : '#dc2626'}; font-size: 20px; font-weight: bold; margin: 0;">${(data.dataQualityAssessment as any).confidenceInAnalysis}</p>
              </div>` : ''}
            </div>` : ''}
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px;">
              <div>
                <h4 style="color: #4b5563; font-size: 14px; font-weight: bold; margin: 0 0 8px 0;">${(data.dataQualityAssessment as any).dataGaps ? 'Data Gaps:' : 'Missing Elements:'}</h4>
                <ul style="color: #6b7280; font-size: 13px; margin: 0; padding-left: 20px;">
                  ${gaps.map((item: string) => `<li>${item}</li>`).join('')}
                </ul>
              </div>
              <div>
                <h4 style="color: #4b5563; font-size: 14px; font-weight: bold; margin: 0 0 8px 0;">Assumptions Made:</h4>
                <ul style="color: #6b7280; font-size: 13px; margin: 0; padding-left: 20px;">
                  ${assumptions.map((assumption: string) => `<li>${assumption}</li>`).join('')}
                </ul>
              </div>
            </div>
            ${followUp.length > 0 ? `
            <div>
              <h4 style="color: #4b5563; font-size: 14px; font-weight: bold; margin: 0 0 8px 0;">${(data.dataQualityAssessment as any).recommendedDataCollection ? 'Recommended Data Collection:' : 'Recommended Follow-up:'}</h4>
              <ul style="color: #6b7280; font-size: 13px; margin: 0; padding-left: 20px;">
                ${followUp.map((recommendation: string) => `<li>${recommendation}</li>`).join('')}
              </ul>
            </div>` : ''}
          </div>
        </div>
      `;
    }

    return html;
  }

  /**
   * Build HTML for analysis matrix tables
   */
  private static buildMatrixHtml(matrix: any, stripCitations: boolean): string {
    const hasData = matrix.data && Array.isArray(matrix.data);
    const rows = hasData ? matrix.data : matrix.rows;
    
    if (!rows || !Array.isArray(rows)) return '';

    // Calculate column width percentage based on number of headers
    const columnCount = matrix.headers?.length || 4;
    const columnWidth = `${(100 / columnCount).toFixed(2)}%`;
    
    // Add keep-together class for smaller matrices
    const rowCount = rows.length;
    const keepTogetherClass = rowCount <= 10 ? 'keep-together' : '';

    let html = `
      <div class="matrix-container content-block ${keepTogetherClass}" style="margin: 15px 0; page-break-inside: avoid !important; break-inside: avoid !important;">
        <h3 style="color: #000000; font-size: 18px; margin-bottom: 10px; font-weight: bold; page-break-after: avoid !important; break-after: avoid !important;">${matrix.title || matrix.name}</h3>
        ${matrix.description ? `<p style="color: #4b5563; font-size: 14px; margin-bottom: 12px;">${matrix.description}</p>` : ''}
        <table style="width: 100% !important; table-layout: fixed; border-collapse: collapse; margin: 8px 0; background: white; border: 1px solid #e5e7eb; page-break-inside: avoid !important; break-inside: avoid !important;">
          <thead>
            <tr style="background: #f3f4f6; page-break-inside: avoid !important; break-inside: avoid !important;">
              ${matrix.headers?.map((header: string) => 
                `<th style="border: 1px solid #e5e7eb; padding: 10px; text-align: left; font-size: 14px; color: #000000; font-weight: bold; width: ${columnWidth}; page-break-inside: avoid !important; break-inside: avoid !important;">${header}</th>`
              ).join('') || ''}
            </tr>
          </thead>
          <tbody>
            ${rows.map((row: any, rowIndex: number) => 
              `<tr style="background: ${rowIndex % 2 === 0 ? '#ffffff' : '#f9fafb'}; page-break-inside: avoid !important; break-inside: avoid !important;">
                ${Array.isArray(row) ? 
                  row.map(cell => this.buildCellHtml(cell, stripCitations, columnWidth)).join('') :
                  (row && typeof row === 'object' ? Object.values(row).map(cell => this.buildCellHtml(cell, stripCitations, columnWidth)).join('') : '')
                }
              </tr>`
            ).join('')}
          </tbody>
        </table>
      </div>
    `;

    return html;
  }

  /**
   * Build HTML for table cells with citation support
   */
  private static buildCellHtml(cell: any, stripCitations: boolean, columnWidth?: string): string {
    const baseStyle = `border: 1px solid #e5e7eb; padding: 8px; font-size: 13px; color: #000000; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word; page-break-inside: avoid !important; break-inside: avoid !important;${columnWidth ? ` width: ${columnWidth} !important;` : ''}`;
    
    // Handle string cells
    if (typeof cell === 'string') {
      return `<td style="${baseStyle}">${cell}</td>`;
    }

    // Handle CitationText objects and research data objects
    if (cell && typeof cell === 'object') {
      let content = '';
      
      if (cell.text) {
        content = cell.text;
        
        // Strip citations by default since stripCitations is true by default
        // Only add citation indicators if explicitly requested (stripCitations = false)
        if (!stripCitations && cell.citations && Array.isArray(cell.citations) && cell.citations.length > 0) {
          const citationIndicators = cell.citations.map((cite: any) => `[${cite.quote?.substring(0, 15)}...]`).join(' ');
          content += ` <span style="color: #3b82f6; font-size: 9px;">${citationIndicators}</span>`;
        }
      } else if (cell.value) {
        // Handle research data with value property
        content = String(cell.value);
      } else {
        // Fallback to string conversion
        content = String(cell);
      }

      return `<td style="${baseStyle}">${content}</td>`;
    }

    // Handle other types
    return `<td style="${baseStyle}">${String(cell)}</td>`;
  }

  /**
   * Process citation text - handle both string and CitationText objects
   */
  private static processCitationText(text: any, stripCitations: boolean): string {
    // Handle string
    if (typeof text === 'string') {
      if (stripCitations) {
        // Remove [CITE: "..."] patterns for clean PDF
        return text.replace(/\[CITE:\s*"[^"]+"\]/g, '');
      }
      return text;
    }

    // Handle CitationText object
    if (text && typeof text === 'object' && text.text) {
      let content = text.text;
      
      if (!stripCitations && text.citations && Array.isArray(text.citations) && text.citations.length > 0) {
        // Add superscript citation numbers
        text.citations.forEach((cite: any, index: number) => {
          content += `<sup style="color: #3b82f6; font-size: 10px;">[${index + 1}]</sup>`;
        });
      }
      
      return content;
    }

    return String(text);
  }

  /**
   * Get workflow type display title
   */
  private static getWorkflowTypeTitle(workflowType: string): string {
    const titles: Record<string, string> = {
      'policy_comparison': 'Policy Comparison Report',
      'policy_analysis': 'Policy Analysis Report',
      'loss_run_analysis': 'Loss Run Analysis Report',
      'research_analysis': 'Research Analysis Report',
      'risk_assessment': 'Risk Assessment Report',
      'insurance_requirements': 'Insurance Requirements Analysis Report',
      'loss_run_analyzer': 'Loss Run Analysis Report',
      'yoy_renewal': 'Year Over Year Renewal Analysis',
      'personal_lines_yoy_renewal': 'Personal Lines Year Over Year Renewal Analysis',
      'commercial_lines_exec_renewal': 'Commercial Lines Executive Renewal Report',
      'homeowners_comparison': 'Homeowners Insurance Comparison Report',
      'personal_umbrella': 'Personal Umbrella / Excess Liability Analysis Report'
    };

    return titles[workflowType] || 'Analysis Report';
  }

  // Priority and impact colors removed from PDF export for cleaner formatting

  /**
   * Add metadata footer to PDF
   */
  private static addMetadataFooter(pdf: jsPDF, metadata: any, includeTimestamp: boolean): void {
    const pageCount = (pdf as any).internal.getNumberOfPages();
    
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(128, 128, 128);
      
      // Page number
      pdf.text(
        `Page ${i} of ${pageCount}`, 
        pdf.internal.pageSize.getWidth() - 20, 
        pdf.internal.pageSize.getHeight() - 10,
        { align: 'right' }
      );
      
      // Generated by text
      pdf.text(
        'Generated by Risk Ninja',
        20, 
        pdf.internal.pageSize.getHeight() - 10
      );
      
      // Timestamp on first page
      if (i === 1 && includeTimestamp) {
        pdf.text(
          `Generated on ${new Date().toLocaleString()}`,
          pdf.internal.pageSize.getWidth() / 2,
          pdf.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }
    }
  }

  /**
   * Export comparison result directly (simplified method)
   */
  static async exportComparisonToPDF(
    data: UniversalJsonResponse,
    filename?: string
  ): Promise<void> {
    return this.exportUniversalJsonToPDF(
      data,
      {
        generatedAt: data.responseMetadata.generatedAt || new Date().toISOString(),
        comparisonType: data.responseMetadata.workflowType || 'analysis',
        documentsAnalyzed: data.responseMetadata.documentsAnalyzed || [],
        confidence: data.responseMetadata.confidence || 'Medium'
      },
      {
        filename: filename || `${data.responseMetadata.workflowType || 'analysis'}-${Date.now()}.pdf`,
        includeMetadata: true,
        stripCitations: true,
        includeTimestamp: true
      }
    );
  }

  /**
   * Export to Word document format using proper docx library
   */
  static async exportUniversalJsonToWord(
    data: UniversalJsonResponse,
    metadata: any,
    options: ExportOptions = {}
  ): Promise<void> {
    // Ensure responseMetadata exists with defaults
    if (!data.responseMetadata) {
      data.responseMetadata = {
        workflowType: 'analysis' as any,
        generatedAt: new Date().toISOString(),
        documentsAnalyzed: [],
        confidence: 'Medium' as any
      };
    }

    // DEBUG: Log the data structure for export
    console.log('📦 EXPORT DEBUG - Word export data structure:', {
      workflowType: data.responseMetadata?.workflowType,
      hasExecutiveSummary: !!(data as any).executiveSummary,
      hasOverallStatus: !!(data as any).executiveSummary?.overallStatus,
      hasComplianceMatrix: !!(data as any).complianceMatrix,
      complianceMatrixLength: (data as any).complianceMatrix?.length || 0,
      hasInterconnectedAnalysis: !!(data as any).interconnectedAnalysis,
      hasDecisionPathway: !!(data as any).decisionPathway,
      hasBlindSpotAcknowledgement: !!(data as any).blindSpotAcknowledgement,
      hasProfessionalDisclaimer: !!(data as any).professionalDisclaimer,
      hasConfidenceAssessment: !!(data as any).confidenceAssessment,
      // Commercial lines specific
      hasExecutiveRenewalSummary: !!(data as any).executiveRenewalSummary,
      hasRiskIntelligenceReport: !!(data as any).riskIntelligenceReport,
      // Client renewal presentation specific
      hasCompanyContext: !!(data as any).companyContext,
      hasCoverageHighlights: !!(data as any).coverageHighlights,
      coverageHighlightsLength: (data as any).coverageHighlights?.length || 0,
      hasRestrictiveEndorsements: !!(data as any).restrictiveEndorsements,
      hasActionItems: !!(data as any).actionItems,
      // Financial analysis specific
      hasFinancialRiskAssessment: !!(data as any).financialRiskAssessment,
      hasUnderwritingRecommendation: !!(data as any).underwritingRecommendation,
      // Coverage analysis specific
      hasPolicyOverview: !!(data as any).policyOverview,
      hasCoverageMetrics: !!(data as any).coverageMetrics,
      // Portfolio analyzer specific
      hasPortfolioStructure: !!(data as any).portfolioStructure,
      hasScenarioAnalysis: !!(data as any).scenarioAnalysis,
      hasCoverageAnalysis: !!(data as any).coverageAnalysis,
      coverageAnalysisIsArray: Array.isArray((data as any).coverageAnalysis),
      coverageAnalysisLength: (data as any).coverageAnalysis?.length || 0,
      hasPortfolioSynthesis: !!(data as any).portfolioSynthesis,
      hasAssessment: !!(data as any).assessment,
      hasBlindSpots: !!(data as any).blindSpots,
      hasRecommendations: !!(data as any).recommendations,
      recommendationsIsArray: Array.isArray((data as any).recommendations),
      recommendationsLength: (data as any).recommendations?.length || 0,
      hasBottomLine: !!(data as any).executiveSummary?.bottomLine,
      hasDisclaimer: !!(data as any).disclaimer,
      // Rate analysis specific
      hasAnalysisTables: !!(data as any).analysisTables,
      hasDriverHierarchy: !!(data as any).driverHierarchy,
      topLevelKeys: Object.keys(data)
    });

    const {
      filename = `comparison-report-${Date.now()}.docx`,
      stripCitations = true,
      customHeader
    } = options;

    try {
      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            // Title
            new Paragraph({
              text: customHeader || this.getWorkflowTypeTitle(data.responseMetadata.workflowType || 'analysis'),
              heading: HeadingLevel.TITLE,
              alignment: AlignmentType.CENTER,
            }),
            
            // Metadata
            new Paragraph({
              text: `Generated: ${data.responseMetadata.generatedAt ? new Date(data.responseMetadata.generatedAt).toLocaleString() : new Date().toLocaleString()}`,
              alignment: AlignmentType.CENTER,
            }),
            
            new Paragraph({
              text: `Documents: ${data.responseMetadata.documentsAnalyzed?.length || 0} | Confidence: ${data.responseMetadata.confidence || 'Medium'}`,
              alignment: AlignmentType.CENTER,
            }),
            
            new Paragraph({ text: "" }), // Empty line

            // Client Renewal Presentation Executive Summary (specific format with text, keyHighlights, etc.)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).executiveSummary?.text ? [
              new Paragraph({
                text: "Executive Summary",
                heading: HeadingLevel.HEADING_1,
              }),
              // Main text
              new Paragraph({
                children: [new TextRun({
                  text: this.extractTextFromCitationText((data as any).executiveSummary.text, stripCitations),
                  size: 24
                })],
                spacing: { after: 200 },
              }),
              // Key Highlights
              ...((data as any).executiveSummary.keyHighlights ? [
                new Paragraph({
                  text: "Key Highlights",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data as any).executiveSummary.keyHighlights, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Overall Assessment
              ...((data as any).executiveSummary.overallAssessment ? [
                new Paragraph({
                  text: "Overall Assessment",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data as any).executiveSummary.overallAssessment, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Critical Attention Items
              ...((data as any).executiveSummary.criticalAttentionItems ? [
                new Paragraph({
                  text: "Critical Attention Items",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data as any).executiveSummary.criticalAttentionItems, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Generic Executive Summary (only if NOT a workflow with custom executive summary handling)
            // Skip for: client_renewal_presentation, insurance_requirements, portfolio_analyzer, rate_analysis, homeowners_comparison, personal_umbrella
            ...((
              // NOT client_renewal_presentation with text format
              !((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).executiveSummary?.text) &&
              // NOT insurance_requirements with overallStatus format
              !(data.responseMetadata?.workflowType === 'insurance_requirements' && (data as any).executiveSummary?.overallStatus) &&
              // NOT portfolio_analyzer (has bottomLine format)
              !((data as any).responseMetadata?.workflowType === 'portfolio_analyzer' || (data as any).portfolioStructure || (data as any).scenarioAnalysis) &&
              // NOT rate_analysis (has custom executive summary format)
              !((data as any).responseMetadata?.workflowType === 'rate_analysis' || (data as any).analysisTables || (data as any).driverHierarchy) &&
              // NOT homeowners_comparison or personal_umbrella (has tldr/keyFindings/overallAssessment format)
              !((data as any).responseMetadata?.workflowType === 'homeowners_comparison' || (data as any).responseMetadata?.workflowType === 'personal_umbrella' || (data as any).baselineExtraction || (data as any).gapSynthesis)
            ) ? [
              new Paragraph({
                text: "Executive Summary",
                heading: HeadingLevel.HEADING_1,
              })
            ] : []),

            // Handle standard paragraphs format (skip for workflows with custom executive summary handling)
            ...((
              !((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).executiveSummary?.text) &&
              !(data.responseMetadata?.workflowType === 'insurance_requirements' && (data as any).executiveSummary?.overallStatus) &&
              // NOT portfolio_analyzer
              !((data as any).responseMetadata?.workflowType === 'portfolio_analyzer' || (data as any).portfolioStructure || (data as any).scenarioAnalysis) &&
              // NOT rate_analysis
              !((data as any).responseMetadata?.workflowType === 'rate_analysis' || (data as any).analysisTables || (data as any).driverHierarchy) &&
              // NOT homeowners_comparison or personal_umbrella
              !((data as any).responseMetadata?.workflowType === 'homeowners_comparison' || (data as any).responseMetadata?.workflowType === 'personal_umbrella' || (data as any).baselineExtraction || (data as any).gapSynthesis)
            ) ?
              (data.executiveSummary?.paragraphs || []).map(paragraph =>
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText(paragraph.content, stripCitations),
                    size: 24, // 12pt font
                    italics: true,
                  })],
                  spacing: { after: 200 },
                })
              ) : []),

            // Handle YOY Renewal executive summary format
            ...((data.executiveSummary as any)?.renewalOverview || (data.executiveSummary as any)?.recommendedPathway ? [
              // Analysis Track
              ...((data.executiveSummary as any).analysisTrack ? [
                new Paragraph({
                  text: "Analysis Track",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data.executiveSummary as any).analysisTrack, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Renewal Overview
              ...((data.executiveSummary as any).renewalOverview ? [
                new Paragraph({
                  text: "Renewal Overview",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data.executiveSummary as any).renewalOverview, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Financial Summary
              ...((data.executiveSummary as any).financialSummary ? [
                new Paragraph({
                  text: "Financial Summary",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data.executiveSummary as any).financialSummary, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Top Advantages
              ...((data.executiveSummary as any).topAdvantages && (data.executiveSummary as any).topAdvantages.length > 0 ? [
                new Paragraph({
                  text: "Top Advantages",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data.executiveSummary as any).topAdvantages.map((advantage: any) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `✓ ${this.extractTextFromCitationText(advantage, stripCitations)}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                )
              ] : []),
              // Top Concerns
              ...((data.executiveSummary as any).topConcerns && (data.executiveSummary as any).topConcerns.length > 0 ? [
                new Paragraph({
                  text: "Top Concerns",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data.executiveSummary as any).topConcerns.map((concern: any) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `⚠ ${this.extractTextFromCitationText(concern, stripCitations)}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                )
              ] : []),
              // Strategic Trade-Off
              ...((data.executiveSummary as any).strategicTradeOff ? [
                new Paragraph({
                  text: "Strategic Trade-Off",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data.executiveSummary as any).strategicTradeOff, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Recommendation
              ...((data.executiveSummary as any).recommendation ? [
                new Paragraph({
                  text: "Recommendation",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data.executiveSummary as any).recommendation, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Recommended Pathway
              ...((data.executiveSummary as any).recommendedPathway ? [
                new Paragraph({
                  text: "Recommended Pathway",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: (data.executiveSummary as any).recommendedPathway,
                    size: 28,
                    bold: true
                  })],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            new Paragraph({ text: "" }), // Empty line

            // Analysis Matrices (only show if there's data)
            ...(data.analysisMatrices && data.analysisMatrices.length > 0 ? [
              new Paragraph({
                text: "Detailed Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordMatrices(data.analysisMatrices, stripCitations)
            ] : []),

            // Key Findings (only show if there's data)
            ...(data.keyFindings && (data.keyFindings.summary || data.keyFindings.findings?.length > 0) ? [
              new Paragraph({
                text: "Key Findings",
                heading: HeadingLevel.HEADING_1,
              })
            ] : []),

            // Key Findings Summary
            ...(data.keyFindings?.summary ? [
              new Paragraph({
                children: [new TextRun({
                  text: this.extractTextFromCitationText(data.keyFindings.summary, stripCitations),
                  size: 24,
                  italics: true
                })],
                spacing: { after: 300 },
              })
            ] : []),

            ...(data.keyFindings?.findings || []).map((finding, index) => {
              const paragraphs = [];
              
              // Add category heading
              paragraphs.push(new Paragraph({
                text: `${index + 1}. ${finding.category}`,
                heading: HeadingLevel.HEADING_2,
              }));
              
              // Add impact/confidence/priority if available
              const badges = [];
              if (finding.impact) badges.push(`${finding.impact} Impact`);
              if (finding.confidence) badges.push(`${finding.confidence} Confidence`);
              if (finding.priority) badges.push(`${finding.priority} Priority`);
              
              if (badges.length > 0) {
                paragraphs.push(new Paragraph({
                  children: [new TextRun({
                    text: badges.join(' • '),
                    size: 20, // 10pt font
                    color: '666666',
                    italics: true
                  })],
                  spacing: { after: 100 },
                }));
              }
              
              // Extract and format the finding text properly using the same logic as frontend
              let findingText = '';
              
              // Handle complex nested structure with observation/implication/recommendation
              if (finding.finding && typeof finding.finding === 'object' && 
                  ((finding.finding as any).observation || (finding.finding as any).implication || (finding.finding as any).recommendation)) {
                const complexFinding = finding.finding as any;
                const textParts = [];
                
                if (complexFinding.observation?.text) {
                  textParts.push(`**Observation:** ${complexFinding.observation.text}`);
                }
                if (complexFinding.implication?.text) {
                  textParts.push(`**Implication:** ${complexFinding.implication.text}`);
                }
                if (complexFinding.recommendation?.text) {
                  textParts.push(`**Recommendation:** ${complexFinding.recommendation.text}`);
                }
                
                findingText = textParts.join('\n\n');
              } else {
                // Simple text format
                findingText = this.extractTextFromCitationText(finding.finding || finding.description || '', stripCitations);
              }
              
              // Handle formatted text with observation/implication/recommendation
              if (findingText.includes('**Observation:**') || findingText.includes('**Implication:**') || findingText.includes('**Recommendation:**')) {
                // Split the text by sections and create formatted paragraphs
                const sections = findingText.split('\n\n').filter(s => s.trim());
                
                sections.forEach(section => {
                  if (section.startsWith('**Observation:**')) {
                    paragraphs.push(new Paragraph({
                      children: [
                        new TextRun({ text: 'Observation: ', bold: true, size: 24 }),
                        new TextRun({ text: section.replace('**Observation:**', '').trim(), size: 24 })
                      ],
                      spacing: { after: 100 },
                    }));
                  } else if (section.startsWith('**Implication:**')) {
                    paragraphs.push(new Paragraph({
                      children: [
                        new TextRun({ text: 'Implication: ', bold: true, size: 24 }),
                        new TextRun({ text: section.replace('**Implication:**', '').trim(), size: 24 })
                      ],
                      spacing: { after: 100 },
                    }));
                  } else if (section.startsWith('**Recommendation:**')) {
                    paragraphs.push(new Paragraph({
                      children: [
                        new TextRun({ text: 'Recommendation: ', bold: true, size: 24 }),
                        new TextRun({ text: section.replace('**Recommendation:**', '').trim(), size: 24 })
                      ],
                      spacing: { after: 200 },
                    }));
                  } else {
                    // Regular text
                    paragraphs.push(new Paragraph({
                      children: [new TextRun({ text: section, size: 24 })],
                      spacing: { after: 200 },
                    }));
                  }
                });
              } else {
                // Simple text format
                paragraphs.push(new Paragraph({
                  children: [new TextRun({
                    text: findingText,
                    size: 24 // 12pt font
                  })],
                  spacing: { after: 200 },
                }));
              }
              
              return paragraphs;
            }).flat(),
            
            // Strategic Considerations (if any)
            ...(data.strategicConsiderations && data.strategicConsiderations.length > 0 ? [
              new Paragraph({
                text: "Strategic Considerations",
                heading: HeadingLevel.HEADING_1,
              }),
              ...data.strategicConsiderations.map((consideration, index) => [
                new Paragraph({
                  text: consideration.consideration,
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: "Analysis: ",
                    bold: true,
                    size: 24 // 12pt font
                  }), new TextRun({
                    text: consideration.analysis,
                    size: 24 // 12pt font
                  })],
                  spacing: { after: 100 },
                }),
                ...(consideration.options && consideration.options.length > 0 ? [
                  new Paragraph({
                    children: [new TextRun({
                      text: "Options:",
                      bold: true,
                      size: 24 // 12pt font
                    })],
                    spacing: { after: 50 },
                  }),
                  ...consideration.options.map(option => 
                    new Paragraph({
                      children: [new TextRun({
                        text: `• ${option}`,
                        size: 24 // 12pt font
                      })],
                      spacing: { after: 50 },
                    })
                  )
                ] : []),
                new Paragraph({
                  children: [new TextRun({
                    text: "Recommended Approach: ",
                    bold: true,
                    size: 24 // 12pt font
                  }), new TextRun({
                    text: consideration.recommendedApproach,
                    size: 24 // 12pt font
                  })],
                  spacing: { after: 200 },
                })
              ]).flat()
            ] : []),
            
            // Risk Mitigation Strategies (if any)
            ...(data.riskMitigationStrategies && data.riskMitigationStrategies.length > 0 ? [
              new Paragraph({
                text: "Risk Mitigation Strategies",
                heading: HeadingLevel.HEADING_1,
              }),
              ...data.riskMitigationStrategies.map((strategy, index) => [
                new Paragraph({
                  text: strategy.risk,
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: "Strategy: ",
                    bold: true,
                    size: 24 // 12pt font
                  }), new TextRun({
                    text: strategy.strategy,
                    size: 24 // 12pt font
                  })],
                  spacing: { after: 100 },
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: "Implementation: ",
                    bold: true,
                    size: 24 // 12pt font
                  }), new TextRun({
                    text: strategy.implementation,
                    size: 24 // 12pt font
                  })],
                  spacing: { after: 100 },
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: "Expected Risk Reduction: ",
                    bold: true,
                    size: 24 // 12pt font
                  }), new TextRun({
                    text: strategy.expectedReduction,
                    size: 24 // 12pt font
                  })],
                  spacing: { after: 200 },
                })
              ]).flat()
            ] : []),
            
            // Recommendations (if any) - skip for portfolio_analyzer and rate_analysis which have custom formats
            ...(data.recommendations && data.recommendations.length > 0 &&
              // NOT portfolio_analyzer
              !((data as any).responseMetadata?.workflowType === 'portfolio_analyzer' || (data as any).portfolioStructure || (data as any).scenarioAnalysis) &&
              // NOT rate_analysis
              !((data as any).responseMetadata?.workflowType === 'rate_analysis' || (data as any).analysisTables || (data as any).driverHierarchy)
            ? [
              new Paragraph({
                text: "Recommendations",
                heading: HeadingLevel.HEADING_1,
              }),
              ...data.recommendations.map((rec, index) => [
                new Paragraph({
                  text: `${index + 1}. ${rec.category}`,
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: "Action: ",
                    bold: true,
                    size: 24 // 12pt font
                  }), new TextRun({
                    text: this.extractTextFromCitationText(rec.action, stripCitations),
                    size: 24 // 12pt font
                  })],
                  spacing: { after: 100 },
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: "Rationale: ",
                    bold: true,
                    size: 24 // 12pt font
                  }), new TextRun({
                    text: this.extractTextFromCitationText(rec.rationale, stripCitations),
                    size: 24 // 12pt font
                  })],
                  spacing: { after: 200 },
                })
              ]).flat()
            ] : []),

            // ===== INSURANCE REQUIREMENTS v2.0 =====

            // Insurance Requirements - Executive Summary (v2.0)
            ...(data.responseMetadata?.workflowType === 'insurance_requirements' && (data as any).executiveSummary?.overallStatus ? [
              new Paragraph({
                text: "Executive Summary",
                heading: HeadingLevel.HEADING_1,
              }),
              // Overall Status
              new Paragraph({
                children: [new TextRun({
                  text: this.extractTextFromCitationText((data as any).executiveSummary.overallStatus, stripCitations),
                  size: 24
                })],
                spacing: { after: 200 },
              }),
              // Recommendation
              ...((data as any).executiveSummary.recommendation ? [
                new Paragraph({
                  children: [new TextRun({
                    text: "Recommendation: ",
                    bold: true,
                    size: 24
                  }), new TextRun({
                    text: (data as any).executiveSummary.recommendation,
                    size: 24
                  })],
                  spacing: { after: 150 },
                })
              ] : []),
              // Recommendation Rationale
              ...((data as any).executiveSummary.recommendationRationale ? [
                new Paragraph({
                  children: [new TextRun({
                    text: "Rationale: ",
                    bold: true,
                    size: 24
                  }), new TextRun({
                    text: this.extractTextFromCitationText((data as any).executiveSummary.recommendationRationale, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Critical Deficiencies
              ...((data as any).executiveSummary.criticalDeficiencies && Array.isArray((data as any).executiveSummary.criticalDeficiencies) && (data as any).executiveSummary.criticalDeficiencies.length > 0 ? [
                new Paragraph({
                  text: "Critical Deficiencies",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).executiveSummary.criticalDeficiencies.map((deficiency: any, index: number) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `${index + 1}. ${this.extractTextFromCitationText(deficiency, stripCitations)}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                )
              ] : [])
            ] : []),

            // Insurance Requirements - Compliance Matrix (v2.0)
            ...((data as any).complianceMatrix && Array.isArray((data as any).complianceMatrix) && (data as any).complianceMatrix.length > 0 ? [
              new Paragraph({
                text: "Compliance Matrix",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).complianceMatrix.map((item: any) => [
                new Paragraph({
                  text: `Requirement ${item.requirementNumber || ''}: ${item.complianceStatus || ''}`,
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: "Requirement: ",
                    bold: true,
                    size: 24
                  }), new TextRun({
                    text: item.requirement || '',
                    size: 24
                  })],
                  spacing: { after: 100 },
                }),
                ...(item.policyProvision ? [
                  new Paragraph({
                    children: [new TextRun({
                      text: "Policy Provision: ",
                      bold: true,
                      size: 24
                    }), new TextRun({
                      text: item.policyProvision,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                ] : []),
                ...(item.rationale ? [
                  new Paragraph({
                    children: [new TextRun({
                      text: "Rationale: ",
                      bold: true,
                      size: 24
                    }), new TextRun({
                      text: this.extractTextFromCitationText(item.rationale, stripCitations),
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                ] : []),
                ...(item.actionRequired ? [
                  new Paragraph({
                    children: [new TextRun({
                      text: "Action Required: ",
                      bold: true,
                      size: 24
                    }), new TextRun({
                      text: item.actionRequired,
                      size: 24
                    })],
                    spacing: { after: 200 },
                  })
                ] : [])
              ]).flat()
            ] : []),

            // Insurance Requirements - Interconnected Analysis (v2.0)
            ...((data as any).interconnectedAnalysis ? [
              new Paragraph({
                text: "Interconnected Risk Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              // Operating Model Identified
              ...((data as any).interconnectedAnalysis.operatingModelIdentified ? [
                new Paragraph({
                  text: "Operating Model Identified",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data as any).interconnectedAnalysis.operatingModelIdentified, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Deficiency Synthesis
              ...((data as any).interconnectedAnalysis.deficiencySynthesis && Array.isArray((data as any).interconnectedAnalysis.deficiencySynthesis) ? [
                new Paragraph({
                  text: "Deficiency Synthesis",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).interconnectedAnalysis.deficiencySynthesis.map((item: any, index: number) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `${index + 1}. ${this.extractTextFromCitationText(item, stripCitations)}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                )
              ] : []),
              // Risk Escalations
              ...((data as any).interconnectedAnalysis.riskEscalations && Array.isArray((data as any).interconnectedAnalysis.riskEscalations) ? [
                new Paragraph({
                  text: "Risk Escalations",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).interconnectedAnalysis.riskEscalations.map((item: any) => [
                  new Paragraph({
                    children: [new TextRun({
                      text: `Deficiency: ${item.deficiency || ''}`,
                      bold: true,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  }),
                  new Paragraph({
                    children: [new TextRun({
                      text: `Original Risk: ${item.originalRisk || ''} → Compound Risk: ${item.compoundRisk || ''}`,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  }),
                  new Paragraph({
                    children: [new TextRun({
                      text: `Reasoning: ${item.reasoning || ''}`,
                      size: 24,
                      italics: true
                    })],
                    spacing: { after: 150 },
                  })
                ]).flat()
              ] : []),
              // Coverage Philosophy Assessment
              ...((data as any).interconnectedAnalysis.coveragePhilosophyAssessment ? [
                new Paragraph({
                  text: "Coverage Philosophy Assessment",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data as any).interconnectedAnalysis.coveragePhilosophyAssessment, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Synthesis Conclusion
              ...((data as any).interconnectedAnalysis.synthesisConclusion ? [
                new Paragraph({
                  text: "Synthesis Conclusion",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data as any).interconnectedAnalysis.synthesisConclusion, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Insurance Requirements - Decision Pathway (v2.0)
            ...((data as any).decisionPathway ? [
              new Paragraph({
                text: "Decision Pathway",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [new TextRun({
                  text: `Pathway: ${(data as any).decisionPathway.pathway || ''} - ${(data as any).decisionPathway.pathwayName || ''}`,
                  bold: true,
                  size: 24
                })],
                spacing: { after: 150 },
              }),
              ...((data as any).decisionPathway.rationale ? [
                new Paragraph({
                  children: [new TextRun({
                    text: "Rationale: ",
                    bold: true,
                    size: 24
                  }), new TextRun({
                    text: this.extractTextFromCitationText((data as any).decisionPathway.rationale, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 150 },
                })
              ] : []),
              // Specific Actions
              ...((data as any).decisionPathway.specificActions && Array.isArray((data as any).decisionPathway.specificActions) ? [
                new Paragraph({
                  text: "Specific Actions",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).decisionPathway.specificActions.map((action: any, index: number) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `${index + 1}. ${this.extractTextFromCitationText(action, stripCitations)}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                )
              ] : [])
            ] : []),

            // Insurance Requirements - Blind Spot Acknowledgement (v2.0)
            ...((data as any).blindSpotAcknowledgement ? [
              new Paragraph({
                text: "Limitations & Blind Spots",
                heading: HeadingLevel.HEADING_1,
              }),
              // Document Completeness Assessment
              ...((data as any).blindSpotAcknowledgement.documentCompletenessAssessment ? [
                new Paragraph({
                  text: "Document Completeness Assessment",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data as any).blindSpotAcknowledgement.documentCompletenessAssessment, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 150 },
                })
              ] : []),
              // Limitations Due to Missing Info
              ...((data as any).blindSpotAcknowledgement.limitationsDueToMissingInfo ? [
                new Paragraph({
                  text: "Limitations Due to Missing Information",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data as any).blindSpotAcknowledgement.limitationsDueToMissingInfo, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 150 },
                })
              ] : []),
              // Missing Documents
              ...((data as any).blindSpotAcknowledgement.missingDocuments && Array.isArray((data as any).blindSpotAcknowledgement.missingDocuments) && (data as any).blindSpotAcknowledgement.missingDocuments.length > 0 ? [
                new Paragraph({
                  text: "Missing Documents",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).blindSpotAcknowledgement.missingDocuments.map((doc: string, index: number) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `${index + 1}. ${doc}`,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  })
                )
              ] : []),
              // Caveat
              ...((data as any).blindSpotAcknowledgement.caveat ? [
                new Paragraph({
                  children: [new TextRun({
                    text: (data as any).blindSpotAcknowledgement.caveat,
                    size: 24,
                    italics: true
                  })],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Insurance Requirements - Confidence Assessment (v2.0)
            ...((data as any).confidenceAssessment ? [
              new Paragraph({
                text: "Confidence Assessment",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [new TextRun({
                  text: `Confidence Level: ${(data as any).confidenceAssessment.percentage || ''}`,
                  bold: true,
                  size: 24
                })],
                spacing: { after: 150 },
              }),
              ...((data as any).confidenceAssessment.sourceOfConfidence ? [
                new Paragraph({
                  children: [new TextRun({
                    text: "Source of Confidence: ",
                    bold: true,
                    size: 24
                  }), new TextRun({
                    text: this.extractTextFromCitationText((data as any).confidenceAssessment.sourceOfConfidence, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Insurance Requirements - Professional Disclaimer (v2.0)
            ...((data as any).professionalDisclaimer ? [
              new Paragraph({
                text: "Professional Disclaimer",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [new TextRun({
                  text: this.extractTextFromCitationText((data as any).professionalDisclaimer, stripCitations),
                  size: 20,
                  italics: true
                })],
                spacing: { after: 200 },
              })
            ] : []),

            // ===== COMMERCIAL LINES EXECUTIVE RENEWAL =====
            // Detect commercial_lines_exec_renewal by workflowType OR presence of unique data structures
            // This ensures Word export works even if AI model doesn't set workflowType correctly

            // Commercial Lines - Executive Renewal Summary
            ...((data.responseMetadata?.workflowType === 'commercial_lines_exec_renewal' || ((data as any).executiveRenewalSummary && (data as any).riskIntelligenceReport)) && (data as any).executiveRenewalSummary ? [
              new Paragraph({
                text: "Executive Renewal Summary",
                heading: HeadingLevel.HEADING_1,
              }),
              // Executive Summary of Changes
              ...((data as any).executiveRenewalSummary.executiveSummaryOfChanges ? [
                new Paragraph({
                  text: "Summary of Changes",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data as any).executiveRenewalSummary.executiveSummaryOfChanges, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Key Changes
              ...((data as any).executiveRenewalSummary.keyChanges && Array.isArray((data as any).executiveRenewalSummary.keyChanges) ? [
                new Paragraph({
                  text: "Key Changes",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).executiveRenewalSummary.keyChanges.map((item: any, index: number) => [
                  new Paragraph({
                    children: [new TextRun({
                      text: `${index + 1}. ${item.change || ''}`,
                      bold: true,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  }),
                  ...(item.impact ? [
                    new Paragraph({
                      children: [new TextRun({
                        text: `Impact: ${item.impact}`,
                        size: 24
                      }), ...(item.priority ? [new TextRun({
                        text: ` (Priority: ${item.priority})`,
                        size: 24,
                        italics: true
                      })] : [])],
                      spacing: { after: 100 },
                    })
                  ] : [])
                ]).flat()
              ] : []),
              // Program Trajectory
              ...((data as any).executiveRenewalSummary.programTrajectory ? [
                new Paragraph({
                  text: "Program Trajectory",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: `Assessment: ${(data as any).executiveRenewalSummary.programTrajectory.assessment || ''}`,
                    bold: true,
                    size: 24
                  })],
                  spacing: { after: 100 },
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: (data as any).executiveRenewalSummary.programTrajectory.rationale || '',
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Renewal Program Matrix
              ...((data as any).executiveRenewalSummary.renewalProgramMatrix && Array.isArray((data as any).executiveRenewalSummary.renewalProgramMatrix) ? [
                new Paragraph({
                  text: "Renewal Program Matrix",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).executiveRenewalSummary.renewalProgramMatrix.map((item: any) => [
                  new Paragraph({
                    children: [new TextRun({
                      text: `${item.coverageLine || ''} - ${item.carrier || ''}`,
                      bold: true,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  }),
                  ...(item.expiringLimit || item.renewalLimit ? [
                    new Paragraph({
                      children: [new TextRun({
                        text: `Limits: ${item.expiringLimit || 'N/A'} → ${item.renewalLimit || 'N/A'}`,
                        size: 24
                      })],
                      spacing: { after: 50 },
                    })
                  ] : []),
                  ...(item.expiringDeductible || item.renewalDeductible ? [
                    new Paragraph({
                      children: [new TextRun({
                        text: `Deductibles: ${item.expiringDeductible || 'N/A'} → ${item.renewalDeductible || 'N/A'}`,
                        size: 24
                      })],
                      spacing: { after: 50 },
                    })
                  ] : []),
                  ...(item.expiringPremium || item.renewalPremium ? [
                    new Paragraph({
                      children: [new TextRun({
                        text: `Premium: ${item.expiringPremium || 'N/A'} → ${item.renewalPremium || 'N/A'}`,
                        size: 24
                      }), ...(item.premiumChangePercent ? [new TextRun({
                        text: ` (${item.premiumChangePercent})`,
                        size: 24,
                        bold: true
                      })] : [])],
                      spacing: { after: 50 },
                    })
                  ] : []),
                  ...(item.keyChanges ? [
                    new Paragraph({
                      children: [new TextRun({
                        text: `Key Changes: ${item.keyChanges}`,
                        size: 24,
                        italics: true
                      })],
                      spacing: { after: 150 },
                    })
                  ] : [])
                ]).flat()
              ] : []),
              // Total Program Summary
              ...((data as any).executiveRenewalSummary.totalProgramSummary ? [
                new Paragraph({
                  text: "Total Program Summary",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: `Total Premium: ${(data as any).executiveRenewalSummary.totalProgramSummary.expiringTotalPremium || 'N/A'} → ${(data as any).executiveRenewalSummary.totalProgramSummary.renewalTotalPremium || 'N/A'}`,
                    size: 24
                  }), ...(data as any).executiveRenewalSummary.totalProgramSummary.totalChangePercent ? [new TextRun({
                    text: ` (${(data as any).executiveRenewalSummary.totalProgramSummary.totalChangePercent})`,
                    size: 24,
                    bold: true
                  })] : []],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Commercial Lines - Risk Intelligence Report
            ...((data.responseMetadata?.workflowType === 'commercial_lines_exec_renewal' || ((data as any).executiveRenewalSummary && (data as any).riskIntelligenceReport)) && (data as any).riskIntelligenceReport ? [
              new Paragraph({
                text: "Risk Intelligence Report",
                heading: HeadingLevel.HEADING_1,
              }),
              // Entity Overview
              ...((data as any).riskIntelligenceReport.entityOverview ? [
                new Paragraph({
                  text: "Entity Overview",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...Object.entries((data as any).riskIntelligenceReport.entityOverview)
                  .filter(([_, value]) => value)
                  .map(([key, value]) =>
                    new Paragraph({
                      children: [new TextRun({
                        text: `${key.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase())}: `,
                        bold: true,
                        size: 24
                      }), new TextRun({
                        text: String(value),
                        size: 24
                      })],
                      spacing: { after: 50 },
                    })
                  )
              ] : []),
              // Research Methodology
              ...((data as any).riskIntelligenceReport.researchMethodology ? [
                new Paragraph({
                  text: "Research Methodology",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...Object.entries((data as any).riskIntelligenceReport.researchMethodology)
                  .filter(([_, value]) => value)
                  .map(([key, value]) =>
                    new Paragraph({
                      children: [new TextRun({
                        text: `${key.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase())}: `,
                        bold: true,
                        size: 24
                      }), new TextRun({
                        text: String(value),
                        size: 24
                      })],
                      spacing: { after: 50 },
                    })
                  )
              ] : []),
              // Critical Path Priorities
              ...((data as any).riskIntelligenceReport.criticalPathPriorities && Array.isArray((data as any).riskIntelligenceReport.criticalPathPriorities) ? [
                new Paragraph({
                  text: "Critical Path Priorities",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).riskIntelligenceReport.criticalPathPriorities.map((item: any) => [
                  new Paragraph({
                    children: [new TextRun({
                      text: `#${item.priorityRank || ''} ${item.domain || ''} (${item.priorityLevel || item.priorityScore || ''})`,
                      bold: true,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  }),
                  new Paragraph({
                    children: [new TextRun({
                      text: `Why Critical: ${item.whyCritical || ''}`,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  }),
                  ...(item.immediateAction ? [
                    new Paragraph({
                      children: [new TextRun({
                        text: `Immediate Action: ${item.immediateAction}`,
                        size: 24,
                        italics: true
                      })],
                      spacing: { after: 150 },
                    })
                  ] : [])
                ]).flat()
              ] : []),
              // Comprehensive Risk Assessment
              ...((data as any).riskIntelligenceReport.comprehensiveRiskAssessment && Array.isArray((data as any).riskIntelligenceReport.comprehensiveRiskAssessment) ? [
                new Paragraph({
                  text: "Comprehensive Risk Assessment",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).riskIntelligenceReport.comprehensiveRiskAssessment.map((item: any) => [
                  new Paragraph({
                    children: [new TextRun({
                      text: `${item.domain || ''} - ${item.inherentRiskLevel || ''}`,
                      bold: true,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  }),
                  ...(item.applicability ? [
                    new Paragraph({
                      children: [new TextRun({
                        text: `Applicability: ${item.applicability}`,
                        size: 24
                      })],
                      spacing: { after: 50 },
                    })
                  ] : []),
                  new Paragraph({
                    children: [new TextRun({
                      text: `Rationale: ${item.rationale || ''}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  }),
                  ...(item.connectedRisks ? [
                    new Paragraph({
                      children: [new TextRun({
                        text: `Connected Risks: ${item.connectedRisks}`,
                        size: 24,
                        italics: true
                      })],
                      spacing: { after: 150 },
                    })
                  ] : [])
                ]).flat()
              ] : []),
              // Coverage Applicability Analysis
              ...((data as any).riskIntelligenceReport.coverageApplicabilityAnalysis ? [
                new Paragraph({
                  text: "Coverage Applicability Analysis",
                  heading: HeadingLevel.HEADING_2,
                }),
                // Risks Adequately Addressed
                ...((data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.risksAdequatelyAddressed && Array.isArray((data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.risksAdequatelyAddressed) ? [
                  new Paragraph({
                    text: "Risks Adequately Addressed",
                    heading: HeadingLevel.HEADING_3,
                  }),
                  ...(data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.risksAdequatelyAddressed.map((item: any) =>
                    new Paragraph({
                      children: [new TextRun({
                        text: `• ${item.riskDomain || ''}: ${item.coveredBy || ''} - ${item.assessment || ''}`,
                        size: 24
                      })],
                      spacing: { after: 100 },
                    })
                  )
                ] : []),
                // Risks Partially Covered
                ...((data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.risksPartiallyCovered && Array.isArray((data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.risksPartiallyCovered) ? [
                  new Paragraph({
                    text: "Risks Partially Covered",
                    heading: HeadingLevel.HEADING_3,
                  }),
                  ...(data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.risksPartiallyCovered.map((item: any) => [
                    new Paragraph({
                      children: [new TextRun({
                        text: `${item.riskDomain || ''}: ${item.limitation || ''}`,
                        bold: true,
                        size: 24
                      })],
                      spacing: { after: 50 },
                    }),
                    ...(item.recommendation ? [
                      new Paragraph({
                        children: [new TextRun({
                          text: `Recommendation: ${item.recommendation}`,
                          size: 24
                        })],
                        spacing: { after: 100 },
                      })
                    ] : [])
                  ]).flat()
                ] : []),
                // Coverage Gaps Identified
                ...((data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.coverageGapsIdentified && Array.isArray((data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.coverageGapsIdentified) ? [
                  new Paragraph({
                    text: "Coverage Gaps Identified",
                    heading: HeadingLevel.HEADING_3,
                  }),
                  ...(data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.coverageGapsIdentified.map((item: any) => [
                    new Paragraph({
                      children: [new TextRun({
                        text: `${item.riskDomain || ''}: ${item.gapType || ''} (${item.priorityScore || ''})`,
                        bold: true,
                        size: 24
                      })],
                      spacing: { after: 50 },
                    }),
                    new Paragraph({
                      children: [new TextRun({
                        text: `Why Critical: ${item.whyCritical || ''}`,
                        size: 24
                      })],
                      spacing: { after: 50 },
                    }),
                    ...(item.recommendedApproach ? [
                      new Paragraph({
                        children: [new TextRun({
                          text: `Recommended Approach: ${item.recommendedApproach}`,
                          size: 24,
                          italics: true
                        })],
                        spacing: { after: 100 },
                      })
                    ] : [])
                  ]).flat()
                ] : [])
              ] : []),
              // Risk Mitigation Framework
              ...((data as any).riskIntelligenceReport.riskMitigationFramework && Array.isArray((data as any).riskIntelligenceReport.riskMitigationFramework) ? [
                new Paragraph({
                  text: "Risk Mitigation Framework",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).riskIntelligenceReport.riskMitigationFramework.map((item: any) => [
                  new Paragraph({
                    children: [new TextRun({
                      text: `${item.topic || ''} (${item.priority || ''})`,
                      bold: true,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  }),
                  new Paragraph({
                    children: [new TextRun({
                      text: `Talking Points: ${item.talkingPoints || ''}`,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  }),
                  ...(item.recommendedActions ? [
                    new Paragraph({
                      children: [new TextRun({
                        text: `Actions: ${item.recommendedActions}`,
                        size: 24,
                        italics: true
                      })],
                      spacing: { after: 150 },
                    })
                  ] : [])
                ]).flat()
              ] : []),
              // Risk Correlation Map
              ...((data as any).riskIntelligenceReport.riskCorrelationMap && Array.isArray((data as any).riskIntelligenceReport.riskCorrelationMap) ? [
                new Paragraph({
                  text: "Risk Correlation Map",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).riskIntelligenceReport.riskCorrelationMap.map((item: any) => [
                  new Paragraph({
                    children: [new TextRun({
                      text: `Scenario: ${item.scenario || ''}`,
                      bold: true,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  }),
                  new Paragraph({
                    children: [new TextRun({
                      text: `Primary Risk: ${item.primaryRisk || ''} → Connected: ${item.connectedRisks || ''}`,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  }),
                  ...(item.cascade ? [
                    new Paragraph({
                      children: [new TextRun({
                        text: `Cascade: ${item.cascade}`,
                        size: 24
                      })],
                      spacing: { after: 50 },
                    })
                  ] : []),
                  ...(item.insuranceCoordination ? [
                    new Paragraph({
                      children: [new TextRun({
                        text: `Insurance Coordination: ${item.insuranceCoordination}`,
                        size: 24,
                        italics: true
                      })],
                      spacing: { after: 150 },
                    })
                  ] : [])
                ]).flat()
              ] : [])
            ] : []),

            // Commercial Lines - Confidence and Limitations
            ...((data.responseMetadata?.workflowType === 'commercial_lines_exec_renewal' || ((data as any).executiveRenewalSummary && (data as any).riskIntelligenceReport)) && (data as any).confidenceAndLimitations ? [
              new Paragraph({
                text: "Confidence & Limitations",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).confidenceAndLimitations.overallConfidence ? [
                new Paragraph({
                  children: [new TextRun({
                    text: `Overall Confidence: ${(data as any).confidenceAndLimitations.overallConfidence}`,
                    bold: true,
                    size: 24
                  })],
                  spacing: { after: 150 },
                })
              ] : []),
              // Web Research Quality
              ...((data as any).confidenceAndLimitations.webResearchQuality ? [
                new Paragraph({
                  text: "Web Research Quality",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...Object.entries((data as any).confidenceAndLimitations.webResearchQuality)
                  .filter(([_, value]) => value)
                  .map(([key, value]) =>
                    new Paragraph({
                      children: [new TextRun({
                        text: `${key.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase())}: ${value}`,
                        size: 24
                      })],
                      spacing: { after: 50 },
                    })
                  )
              ] : []),
              // Insurance Document Quality
              ...((data as any).confidenceAndLimitations.insuranceDocumentQuality ? [
                new Paragraph({
                  text: "Insurance Document Quality",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...Object.entries((data as any).confidenceAndLimitations.insuranceDocumentQuality)
                  .filter(([_, value]) => value)
                  .map(([key, value]) =>
                    new Paragraph({
                      children: [new TextRun({
                        text: `${key.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase())}: ${value}`,
                        size: 24
                      })],
                      spacing: { after: 50 },
                    })
                  )
              ] : []),
              // Limitations
              ...((data as any).confidenceAndLimitations.limitations && Array.isArray((data as any).confidenceAndLimitations.limitations) ? [
                new Paragraph({
                  text: "Limitations",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).confidenceAndLimitations.limitations.map((limitation: string, index: number) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `${index + 1}. ${limitation}`,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  })
                )
              ] : []),
              // Recommendations for Improvement
              ...((data as any).confidenceAndLimitations.recommendationsForImprovement && Array.isArray((data as any).confidenceAndLimitations.recommendationsForImprovement) ? [
                new Paragraph({
                  text: "Recommendations for Improvement",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).confidenceAndLimitations.recommendationsForImprovement.map((rec: string, index: number) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `${index + 1}. ${rec}`,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  })
                )
              ] : [])
            ] : []),

            // Commercial Lines - Professional Disclaimer
            ...((data.responseMetadata?.workflowType === 'commercial_lines_exec_renewal' || ((data as any).executiveRenewalSummary && (data as any).riskIntelligenceReport)) && (data as any).professionalDisclaimer ? [
              new Paragraph({
                text: "Professional Disclaimer",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [new TextRun({
                  text: this.extractTextFromCitationText((data as any).professionalDisclaimer, stripCitations),
                  size: 20,
                  italics: true
                })],
                spacing: { after: 200 },
              })
            ] : []),

            // === Loss Run Analyzer Sections ===

            // Executive Summary / Portfolio Snapshot (for loss_run_analyzer - moved to top to match report)
            ...((data as any).responseMetadata?.workflowType === 'loss_run_analyzer' && (data as any).executiveSummary?.portfolioSnapshot ? [
              new Paragraph({
                text: "Portfolio Snapshot",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).executiveSummary.portfolioSnapshot.totalCarriers !== undefined ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Total Carriers: ", bold: true, size: 24 }),
                    new TextRun({ text: String((data as any).executiveSummary.portfolioSnapshot.totalCarriers), size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).executiveSummary.portfolioSnapshot.totalPolicies !== undefined ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Total Policies: ", bold: true, size: 24 }),
                    new TextRun({ text: String((data as any).executiveSummary.portfolioSnapshot.totalPolicies), size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              new Paragraph({
                children: [
                  new TextRun({ text: "Total Claims: ", bold: true, size: 24 }),
                  new TextRun({ text: String((data as any).executiveSummary.portfolioSnapshot.totalClaims || 0), size: 24 })
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Total Incurred: ", bold: true, size: 24 }),
                  new TextRun({ text: (data as any).executiveSummary.portfolioSnapshot.totalIncurred || 'N/A', size: 24 })
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Open Claims: ", bold: true, size: 24 }),
                  new TextRun({ text: String((data as any).executiveSummary.portfolioSnapshot.openClaimsCount || 0), size: 24 })
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Closed Claims: ", bold: true, size: 24 }),
                  new TextRun({ text: String((data as any).executiveSummary.portfolioSnapshot.closedClaimsCount || 0), size: 24 })
                ],
                spacing: { after: 100 },
              }),
              ...((data as any).executiveSummary.portfolioSnapshot.dateRange ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Date Range: ", bold: true, size: 24 }),
                    new TextRun({ text: String((data as any).executiveSummary.portfolioSnapshot.dateRange), size: 24 })
                  ],
                  spacing: { after: 200 },
                })
              ] : []),
              // v4.1: trend label is no longer surfaced inside the Portfolio Snapshot.
              // It appears only in the dedicated Trend Analysis section emitted later
              // (after Severity / Geographic / Top Losses), with the synthesis as the
              // closing element of that section.
            ] : []),

            // Key Findings (loss_run_analyzer)
            ...((data as any).responseMetadata?.workflowType === 'loss_run_analyzer' && Array.isArray((data as any).executiveSummary?.keyFindings) && (data as any).executiveSummary.keyFindings.length > 0 ? [
              new Paragraph({
                text: "Key Findings",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).executiveSummary.keyFindings.flatMap((kf: any, idx: number) => {
                const findingText = typeof kf?.finding === 'object'
                  ? (kf.finding?.text || '')
                  : String(kf?.finding || '');
                return [
                  new Paragraph({
                    children: [
                      new TextRun({ text: `${idx + 1}. `, bold: true, size: 22 }),
                      new TextRun({ text: this.processCitationText(findingText, stripCitations), size: 22 }),
                      ...(kf?.impact ? [new TextRun({ text: `  [${kf.impact} impact]`, size: 20, italics: true, color: "6B7280" })] : []),
                    ],
                    spacing: { after: 80 },
                  })
                ];
              })
            ] : []),

            // Loss Drivers (loss_run_analyzer)
            ...((data as any).responseMetadata?.workflowType === 'loss_run_analyzer' && Array.isArray((data as any).executiveSummary?.lossDrivers) && (data as any).executiveSummary.lossDrivers.length > 0 ? [
              new Paragraph({
                text: "Loss Drivers",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).executiveSummary.lossDrivers.map((driver: string) =>
                new Paragraph({
                  children: [new TextRun({ text: `• ${driver}`, size: 22 })],
                  spacing: { after: 50 },
                })
              )
            ] : []),

            // Document Manifest
            ...((data as any).documentManifest && Array.isArray((data as any).documentManifest) && (data as any).documentManifest.length > 0 ? [
              new Paragraph({
                text: "Analyzed Documents",
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordTableFromArray((data as any).documentManifest, [
                'documentId', 'carrier', 'policyNumber', 'lineOfBusiness', 'claimCount'
              ], stripCitations)
            ] : []),

            // All Claims (v4.1: expanded columns including state attribution + multi-line evidence)
            ...((data as any).claims && Array.isArray((data as any).claims) && (data as any).claims.length > 0 ? [
              new Paragraph({
                text: `All Claims (${(data as any).claims.length})`,
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordTableFromArray((data as any).claims, [
                'claimNumber', 'claimStatus', 'dateOfLoss', 'dateReported', 'lossState',
                'lob', 'description', 'totalIncurred', 'multiLineClaim', 'sourceDocument'
              ], stripCitations)
            ] : []),

            // Validation Results
            ...((data as any).validationResults && Array.isArray((data as any).validationResults) && (data as any).validationResults.length > 0 ? [
              new Paragraph({
                text: "Cross-Foot Validation Results",
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordTableFromArray((data as any).validationResults, [
                'documentId', 'claimNumberCensus', 'extractedRecordCount',
                'sourceTotalIncurred', 'extractedSum', 'financialVariance', 'status'
              ], stripCitations)
            ] : []),

            // Claim Development (multi-period claim tracking)
            ...((data as any).claimDevelopment && Array.isArray((data as any).claimDevelopment) && (data as any).claimDevelopment.length > 0 ? [
              new Paragraph({
                text: "Claim Development",
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordTableFromArray((data as any).claimDevelopment, [
                'claimNumber', 'dateOfLoss', 'periodsTracked', 'totalDevelopment', 'percentageChange'
              ], stripCitations)
            ] : []),

            // Frequency Analysis
            ...((data as any).frequencyAnalysis ? [
              new Paragraph({
                text: "Frequency Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              // Claims per period (v4.1: was missing entirely from Word export)
              ...((data as any).frequencyAnalysis.claimsPerPeriod && Array.isArray((data as any).frequencyAnalysis.claimsPerPeriod) && (data as any).frequencyAnalysis.claimsPerPeriod.length > 0 ? [
                new Paragraph({
                  text: "Claims per Period",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...this.buildWordTableFromArray((data as any).frequencyAnalysis.claimsPerPeriod, [
                  'period', 'count'
                ], stripCitations)
              ] : []),
              ...((data as any).frequencyAnalysis.claimsByLOB && Array.isArray((data as any).frequencyAnalysis.claimsByLOB) ? [
                new Paragraph({
                  text: "Claims by Line of Business",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...this.buildWordTableFromArray((data as any).frequencyAnalysis.claimsByLOB, [
                  'lob', 'count', 'percentage', 'coverageMinorCodes'
                ], stripCitations)
              ] : []),
              ...((data as any).frequencyAnalysis.claimsByState && Array.isArray((data as any).frequencyAnalysis.claimsByState) ? [
                new Paragraph({
                  text: "Claims by State",
                  heading: HeadingLevel.HEADING_2,
                }),
                // v4.1 attribution coverage statement
                ...((data as any).frequencyAnalysis.attributionCoverage ? [
                  new Paragraph({
                    children: [new TextRun({ text: (data as any).frequencyAnalysis.attributionCoverage, italics: true, size: 22, color: "555555" })],
                    spacing: { after: 100 },
                  })
                ] : []),
                ...this.buildWordTableFromArray((data as any).frequencyAnalysis.claimsByState, [
                  'state', 'count', 'percentage', 'stateSourceBreakdown'
                ], stripCitations),
                // v4.1 § 9.3 — per-state sample source quotes
                ...(((data as any).frequencyAnalysis.claimsByState as any[])
                  .filter(s => Array.isArray(s.sampleSourceQuotes) && s.sampleSourceQuotes.length > 0)
                  .flatMap((s: any) => [
                    new Paragraph({
                      children: [new TextRun({ text: `Source quotes for ${s.state}`, bold: true, size: 22 })],
                      spacing: { before: 120, after: 50 },
                    }),
                    ...s.sampleSourceQuotes.map((q: any) =>
                      new Paragraph({
                        children: [new TextRun({
                          text: `• ${q.claimNumber}: "${q.sourceField || ''}"${q.sourceLocation ? ` (${q.sourceLocation})` : ''}`,
                          size: 22,
                        })],
                        spacing: { after: 40 },
                      })
                    ),
                  ]))
              ] : [])
            ] : []),

            // Severity Analysis
            ...((data as any).severityAnalysis ? [
              new Paragraph({
                text: "Severity Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Average Claim Size: ", bold: true, size: 24 }),
                  new TextRun({ text: (data as any).severityAnalysis.averageClaimSize || 'N/A', size: 24 })
                ],
                spacing: { after: 100 },
              }),
              ...((data as any).severityAnalysis.medianClaimSize ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Median Claim Size: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).severityAnalysis.medianClaimSize, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              // v4.1 § 11.3 — Median evidence audit trail (forced-emission gate output)
              ...((data as any).severityAnalysis.medianEvidence ? [
                new Paragraph({
                  children: [new TextRun({ text: "Median & Mean Audit Trail:", bold: true, size: 22, color: "B45309" })],
                  spacing: { before: 80, after: 50 },
                }),
                new Paragraph({
                  children: [new TextRun({ text: (data as any).severityAnalysis.medianEvidence, size: 20, font: "Courier New" })],
                  spacing: { after: 150 },
                })
              ] : []),
              ...((data as any).severityAnalysis.distribution && Array.isArray((data as any).severityAnalysis.distribution) ? [
                new Paragraph({
                  text: "Severity Distribution",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...this.buildWordTableFromArray((data as any).severityAnalysis.distribution, [
                  'category', 'count', 'totalIncurred', 'percentage', 'claimNumbers'
                ], stripCitations)
              ] : [])
            ] : []),

            // Top Claims (v4.1: expanded with sourceDocument + multi-line evidence)
            ...((data as any).topClaims && Array.isArray((data as any).topClaims) && (data as any).topClaims.length > 0 ? [
              new Paragraph({
                text: `Top ${(data as any).topClaims.length} Largest Claims`,
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordTableFromArray((data as any).topClaims, [
                'rank', 'claimNumber', 'dateOfLoss', 'lob', 'description',
                'totalIncurred', 'multiLineClaim', 'grossIndemnityPaid', 'status', 'sourceDocument'
              ], stripCitations)
            ] : []),

            // v4.1 — Trend Analysis section. Renders only after the analytical sections
            // above (severity, geographic, top losses) so the synthesis label is downstream
            // of its evidence components per § 12 / § 15.1.
            ...((data as any).executiveSummary?.trendAnalysis ? [
              new Paragraph({
                text: "Trend Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((typeof (data as any).executiveSummary.trendAnalysis === 'object' && (data as any).executiveSummary.trendAnalysis.text)
                ? this.processCitationText((data as any).executiveSummary.trendAnalysis.text, stripCitations).split('\n').map((line: string) =>
                    new Paragraph({
                      children: [new TextRun({ text: line, size: 22 })],
                      spacing: { after: 60 },
                    })
                  )
                : []),
              ...((data as any).executiveSummary.trendDirection ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Synthesis: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).executiveSummary.trendDirection, bold: true, size: 24 })
                  ],
                  spacing: { before: 120, after: 200 },
                })
              ] : [])
            ] : []),

            // Analyst Notes (loss_run_analyzer)
            ...((data as any).responseMetadata?.workflowType === 'loss_run_analyzer' && (data as any).executiveSummary?.analystNotes ? [
              new Paragraph({
                text: "Analyst Notes",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(() => {
                const notes = (data as any).executiveSummary.analystNotes;
                const text = typeof notes === 'object' ? (notes?.text || '') : String(notes || '');
                return this.processCitationText(text, stripCitations).split('\n').map((line: string) =>
                  new Paragraph({
                    children: [new TextRun({ text: line, size: 22 })],
                    spacing: { after: 60 },
                  })
                );
              })()
            ] : []),

            // Data Quality
            ...((data as any).dataQuality ? [
              new Paragraph({
                text: "Data Validation",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "All Records Extracted: ", bold: true, size: 24 }),
                  new TextRun({ text: (data as any).dataQuality.allRecordsExtracted ? 'Yes' : 'No', size: 24 })
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Financial Cross-Foot Valid: ", bold: true, size: 24 }),
                  new TextRun({ text: (data as any).dataQuality.financialCrossFootValid ? 'Yes' : 'No', size: 24 })
                ],
                spacing: { after: 100 },
              }),
              ...((data as any).dataQuality.issues && Array.isArray((data as any).dataQuality.issues) && (data as any).dataQuality.issues.length > 0 ? [
                new Paragraph({
                  children: [new TextRun({ text: "Issues:", bold: true, size: 24 })],
                  spacing: { after: 50 },
                }),
                ...(data as any).dataQuality.issues.map((issue: string) =>
                  new Paragraph({
                    children: [new TextRun({ text: `• ${issue}`, size: 24 })],
                    spacing: { after: 50 },
                  })
                )
              ] : [])
            ] : []),

            // Strategic Action Plan (YOY Renewal)
            ...((data as any).strategicActionPlan ? [
              new Paragraph({
                text: "Strategic Action Plan",
                heading: HeadingLevel.HEADING_1,
              }),
              // Selected Pathway
              ...((data as any).strategicActionPlan.selectedPathway ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Selected Pathway: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).strategicActionPlan.selectedPathway, size: 24, bold: true })
                  ],
                  spacing: { after: 200 },
                })
              ] : []),
              // Immediate Actions
              ...((data as any).strategicActionPlan.immediateActions && (data as any).strategicActionPlan.immediateActions.length > 0 ? [
                new Paragraph({
                  text: "Immediate Actions",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).strategicActionPlan.immediateActions.map((item: any, idx: number) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `${idx + 1}. ${item.action}${item.owner ? ` (Owner: ${item.owner})` : ''}${item.deadline ? ` - ${item.deadline}` : ''}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                )
              ] : []),
              // Short-Term Actions
              ...((data as any).strategicActionPlan.shortTermActions && (data as any).strategicActionPlan.shortTermActions.length > 0 ? [
                new Paragraph({
                  text: "Short-Term Actions",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).strategicActionPlan.shortTermActions.map((item: any, idx: number) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `${idx + 1}. ${item.action}${item.owner ? ` (Owner: ${item.owner})` : ''}${item.deadline ? ` - ${item.deadline}` : ''}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                )
              ] : []),
              // Medium-Term Actions
              ...((data as any).strategicActionPlan.mediumTermActions && (data as any).strategicActionPlan.mediumTermActions.length > 0 ? [
                new Paragraph({
                  text: "Medium-Term Actions",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).strategicActionPlan.mediumTermActions.map((item: any, idx: number) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `${idx + 1}. ${item.action}${item.owner ? ` (Owner: ${item.owner})` : ''}${item.deadline ? ` - ${item.deadline}` : ''}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                )
              ] : []),
              // Long-Term Initiatives
              ...((data as any).strategicActionPlan.longTermInitiatives && (data as any).strategicActionPlan.longTermInitiatives.length > 0 ? [
                new Paragraph({
                  text: "Long-Term Initiatives",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).strategicActionPlan.longTermInitiatives.map((item: any, idx: number) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `${idx + 1}. ${item.action}${item.owner ? ` (Owner: ${item.owner})` : ''}${item.deadline ? ` - ${item.deadline}` : ''}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                )
              ] : []),
              // Success Metrics
              ...((data as any).strategicActionPlan.successMetrics && (data as any).strategicActionPlan.successMetrics.length > 0 ? [
                new Paragraph({
                  text: "Success Metrics",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).strategicActionPlan.successMetrics.map((metric: string) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `• ${metric}`,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  })
                )
              ] : []),
              // Next Review Date
              ...((data as any).strategicActionPlan.nextReviewDate ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Next Review Date: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).strategicActionPlan.nextReviewDate, size: 24 })
                  ],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Confidence Statement (YOY Renewal)
            ...((data as any).confidenceStatement ? [
              new Paragraph({
                text: "Confidence Statement",
                heading: HeadingLevel.HEADING_1,
              }),
              // Overall Confidence
              ...((data as any).confidenceStatement.overallConfidence ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Overall Confidence: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).confidenceStatement.overallConfidence, size: 24, bold: true })
                  ],
                  spacing: { after: 200 },
                })
              ] : []),
              // Basis for Confidence
              ...((data as any).confidenceStatement.basisForConfidence ? [
                new Paragraph({
                  text: "Basis for Confidence",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data as any).confidenceStatement.basisForConfidence, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Specific Limitations
              ...((data as any).confidenceStatement.specificLimitations && (data as any).confidenceStatement.specificLimitations.length > 0 ? [
                new Paragraph({
                  text: "Specific Limitations",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).confidenceStatement.specificLimitations.map((limitation: any) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `• ${this.extractTextFromCitationText(limitation, stripCitations)}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                )
              ] : []),
              // Verification Needed
              ...((data as any).confidenceStatement.verificationNeeded && (data as any).confidenceStatement.verificationNeeded.length > 0 ? [
                new Paragraph({
                  text: "Verification Needed",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).confidenceStatement.verificationNeeded.map((item: string) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `• ${item}`,
                      size: 24
                    })],
                    spacing: { after: 50 },
                  })
                )
              ] : [])
            ] : []),

            // ============================================================
            // YOY Renewal opt-in add-on sections (Report Options step)
            // ============================================================
            // Schedules, Coverage Details, Compliance & Contractual — each
            // produced by the separate yoyAddOnsService AI call and merged
            // into the saved report under reportAddOns. The base
            // yoyRenewalSchema is at Vertex's complexity ceiling so adding
            // these to the base schema itself was rejected with INVALID_ARGUMENT.
            ...this.buildYoyAddOnsWordSections(data, stripCitations),

            // ===============================================
            // FINANCIAL ANALYSIS WORKFLOW SECTIONS
            // ===============================================

            // Financial Risk Assessment (financial_analysis)
            ...((((data as any).responseMetadata?.workflowType === 'financial_analysis') || ((data as any).financialRiskAssessment && (data as any).underwritingRecommendation)) && (data as any).financialRiskAssessment ? [
              new Paragraph({
                text: "Financial Risk Assessment",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Overall Score: ", bold: true, size: 24 }),
                  new TextRun({ text: (data as any).financialRiskAssessment.overallScore || 'N/A', size: 24, bold: true })
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Risk Category: ", bold: true, size: 24 }),
                  new TextRun({ text: (data as any).financialRiskAssessment.riskCategory || 'N/A', size: 24 })
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Risk Trajectory: ", bold: true, size: 24 }),
                  new TextRun({ text: (data as any).financialRiskAssessment.riskTrajectory || 'N/A', size: 24 })
                ],
                spacing: { after: 200 },
              }),
              // Risk Category Breakdown
              ...((data as any).financialRiskAssessment.liquidityRisk ? [
                new Paragraph({
                  text: "Liquidity Risk",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: `Score: ${(data as any).financialRiskAssessment.liquidityRisk.score || 'N/A'} | Category: ${(data as any).financialRiskAssessment.liquidityRisk.category || 'N/A'}`, size: 24 })
                  ],
                  spacing: { after: 50 },
                }),
                ...((data as any).financialRiskAssessment.liquidityRisk.rationale ? [
                  new Paragraph({
                    children: [new TextRun({ text: (data as any).financialRiskAssessment.liquidityRisk.rationale, size: 22, italics: true })],
                    spacing: { after: 150 },
                  })
                ] : [])
              ] : []),
              ...((data as any).financialRiskAssessment.profitabilityRisk ? [
                new Paragraph({
                  text: "Profitability Risk",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: `Score: ${(data as any).financialRiskAssessment.profitabilityRisk.score || 'N/A'} | Category: ${(data as any).financialRiskAssessment.profitabilityRisk.category || 'N/A'}`, size: 24 })
                  ],
                  spacing: { after: 50 },
                }),
                ...((data as any).financialRiskAssessment.profitabilityRisk.rationale ? [
                  new Paragraph({
                    children: [new TextRun({ text: (data as any).financialRiskAssessment.profitabilityRisk.rationale, size: 22, italics: true })],
                    spacing: { after: 150 },
                  })
                ] : [])
              ] : []),
              ...((data as any).financialRiskAssessment.leverageRisk ? [
                new Paragraph({
                  text: "Leverage Risk",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: `Score: ${(data as any).financialRiskAssessment.leverageRisk.score || 'N/A'} | Category: ${(data as any).financialRiskAssessment.leverageRisk.category || 'N/A'}`, size: 24 })
                  ],
                  spacing: { after: 50 },
                }),
                ...((data as any).financialRiskAssessment.leverageRisk.rationale ? [
                  new Paragraph({
                    children: [new TextRun({ text: (data as any).financialRiskAssessment.leverageRisk.rationale, size: 22, italics: true })],
                    spacing: { after: 150 },
                  })
                ] : [])
              ] : []),
              ...((data as any).financialRiskAssessment.trajectoryRationale ? [
                new Paragraph({
                  text: "Trajectory Rationale",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({ text: (data as any).financialRiskAssessment.trajectoryRationale, size: 24 })],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Detailed Financial Analysis (financial_analysis)
            ...((((data as any).responseMetadata?.workflowType === 'financial_analysis') || ((data as any).financialRiskAssessment && (data as any).underwritingRecommendation)) && (data as any).detailedAnalysis ? [
              new Paragraph({
                text: "Detailed Financial Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              // Revenue Analysis
              ...((data as any).detailedAnalysis.revenueAnalysis?.text ? [
                new Paragraph({ text: "Revenue Analysis", heading: HeadingLevel.HEADING_2 }),
                new Paragraph({
                  children: [new TextRun({ text: this.extractTextFromCitationText((data as any).detailedAnalysis.revenueAnalysis, stripCitations), size: 24 })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Profitability Analysis
              ...((data as any).detailedAnalysis.profitabilityAnalysis?.text ? [
                new Paragraph({ text: "Profitability Analysis", heading: HeadingLevel.HEADING_2 }),
                new Paragraph({
                  children: [new TextRun({ text: this.extractTextFromCitationText((data as any).detailedAnalysis.profitabilityAnalysis, stripCitations), size: 24 })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Liquidity Analysis
              ...((data as any).detailedAnalysis.liquidityAnalysis?.text ? [
                new Paragraph({ text: "Liquidity Analysis", heading: HeadingLevel.HEADING_2 }),
                new Paragraph({
                  children: [new TextRun({ text: this.extractTextFromCitationText((data as any).detailedAnalysis.liquidityAnalysis, stripCitations), size: 24 })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Leverage Analysis
              ...((data as any).detailedAnalysis.leverageAnalysis?.text ? [
                new Paragraph({ text: "Leverage Analysis", heading: HeadingLevel.HEADING_2 }),
                new Paragraph({
                  children: [new TextRun({ text: this.extractTextFromCitationText((data as any).detailedAnalysis.leverageAnalysis, stripCitations), size: 24 })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Cash Flow Analysis
              ...((data as any).detailedAnalysis.cashFlowAnalysis?.text ? [
                new Paragraph({ text: "Cash Flow Analysis", heading: HeadingLevel.HEADING_2 }),
                new Paragraph({
                  children: [new TextRun({ text: this.extractTextFromCitationText((data as any).detailedAnalysis.cashFlowAnalysis, stripCitations), size: 24 })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Industry-Specific Analysis
              ...((data as any).detailedAnalysis.industrySpecificAnalysis?.text ? [
                new Paragraph({ text: `Industry Analysis${(data as any).detailedAnalysis.industrySpecificAnalysis.industry ? ` (${(data as any).detailedAnalysis.industrySpecificAnalysis.industry})` : ''}`, heading: HeadingLevel.HEADING_2 }),
                new Paragraph({
                  children: [new TextRun({ text: this.extractTextFromCitationText((data as any).detailedAnalysis.industrySpecificAnalysis, stripCitations), size: 24 })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Footnote Analysis
              ...((data as any).detailedAnalysis.footnoteAnalysis?.text ? [
                new Paragraph({ text: "Footnote Analysis", heading: HeadingLevel.HEADING_2 }),
                new Paragraph({
                  children: [new TextRun({ text: this.extractTextFromCitationText((data as any).detailedAnalysis.footnoteAnalysis, stripCitations), size: 24 })],
                  spacing: { after: 100 },
                }),
                ...((data as any).detailedAnalysis.footnoteAnalysis.criticalFindings?.length > 0 ? [
                  new Paragraph({ children: [new TextRun({ text: "Critical Findings:", bold: true, size: 24 })], spacing: { after: 50 } }),
                  ...(data as any).detailedAnalysis.footnoteAnalysis.criticalFindings.map((f: string) =>
                    new Paragraph({ children: [new TextRun({ text: `• ${f}`, size: 22 })], spacing: { after: 50 } })
                  )
                ] : []),
                ...((data as any).detailedAnalysis.footnoteAnalysis.redFlags?.length > 0 ? [
                  new Paragraph({ children: [new TextRun({ text: "Red Flags:", bold: true, size: 24 })], spacing: { after: 50 } }),
                  ...(data as any).detailedAnalysis.footnoteAnalysis.redFlags.map((f: string) =>
                    new Paragraph({ children: [new TextRun({ text: `• ${f}`, size: 22 })], spacing: { after: 50 } })
                  )
                ] : [])
              ] : [])
            ] : []),

            // Period Comparison (financial_analysis)
            ...((((data as any).responseMetadata?.workflowType === 'financial_analysis') || ((data as any).financialRiskAssessment && (data as any).underwritingRecommendation)) && (data as any).periodComparison ? [
              new Paragraph({
                text: "Period-Over-Period Comparison",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).periodComparison.periodsAnalyzed?.length > 0 ? [
                new Paragraph({
                  children: [new TextRun({ text: `Periods Analyzed: ${(data as any).periodComparison.periodsAnalyzed.join(', ')}`, size: 24, italics: true })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Revenue Metrics Table
              ...((data as any).periodComparison.revenueMetrics?.length > 0 ? [
                new Paragraph({ text: "Revenue Metrics", heading: HeadingLevel.HEADING_2 }),
                ...this.buildWordTableFromArray((data as any).periodComparison.revenueMetrics.map((m: any) => ({
                  metric: m.metric,
                  values: m.values?.join(' | ') || '',
                  trend: m.trend || '',
                  change: m.percentChange || ''
                })), ['metric', 'values', 'trend', 'change'], stripCitations)
              ] : []),
              // Profitability Metrics Table
              ...((data as any).periodComparison.profitabilityMetrics?.length > 0 ? [
                new Paragraph({ text: "Profitability Metrics", heading: HeadingLevel.HEADING_2 }),
                ...this.buildWordTableFromArray((data as any).periodComparison.profitabilityMetrics.map((m: any) => ({
                  metric: m.metric,
                  values: m.values?.join(' | ') || '',
                  trend: m.trend || '',
                  change: m.percentChange || ''
                })), ['metric', 'values', 'trend', 'change'], stripCitations)
              ] : []),
              // Liquidity Metrics Table
              ...((data as any).periodComparison.liquidityMetrics?.length > 0 ? [
                new Paragraph({ text: "Liquidity Metrics", heading: HeadingLevel.HEADING_2 }),
                ...this.buildWordTableFromArray((data as any).periodComparison.liquidityMetrics.map((m: any) => ({
                  metric: m.metric,
                  values: m.values?.join(' | ') || '',
                  trend: m.trend || '',
                  benchmark: m.benchmark || '',
                  status: m.status || ''
                })), ['metric', 'values', 'trend', 'benchmark', 'status'], stripCitations)
              ] : []),
              // Leverage Metrics Table
              ...((data as any).periodComparison.leverageMetrics?.length > 0 ? [
                new Paragraph({ text: "Leverage Metrics", heading: HeadingLevel.HEADING_2 }),
                ...this.buildWordTableFromArray((data as any).periodComparison.leverageMetrics.map((m: any) => ({
                  metric: m.metric,
                  values: m.values?.join(' | ') || '',
                  trend: m.trend || '',
                  benchmark: m.benchmark || '',
                  status: m.status || ''
                })), ['metric', 'values', 'trend', 'benchmark', 'status'], stripCitations)
              ] : [])
            ] : []),

            // ERM Risk Mapping (financial_analysis)
            ...((((data as any).responseMetadata?.workflowType === 'financial_analysis') || ((data as any).financialRiskAssessment && (data as any).underwritingRecommendation)) && (data as any).ermRiskMapping ? [
              new Paragraph({
                text: "ERM Risk Mapping",
                heading: HeadingLevel.HEADING_1,
              }),
              // Strategic Risks
              ...((data as any).ermRiskMapping.strategicRisks?.length > 0 ? [
                new Paragraph({ text: "Strategic Risks", heading: HeadingLevel.HEADING_2 }),
                ...this.buildWordTableFromArray((data as any).ermRiskMapping.strategicRisks, ['risk', 'indicator', 'severity'], stripCitations)
              ] : []),
              // Operational Risks
              ...((data as any).ermRiskMapping.operationalRisks?.length > 0 ? [
                new Paragraph({ text: "Operational Risks", heading: HeadingLevel.HEADING_2 }),
                ...this.buildWordTableFromArray((data as any).ermRiskMapping.operationalRisks, ['risk', 'indicator', 'severity'], stripCitations)
              ] : []),
              // Financial Risks
              ...((data as any).ermRiskMapping.financialRisks?.length > 0 ? [
                new Paragraph({ text: "Financial Risks", heading: HeadingLevel.HEADING_2 }),
                ...this.buildWordTableFromArray((data as any).ermRiskMapping.financialRisks, ['risk', 'indicator', 'severity'], stripCitations)
              ] : []),
              // Compliance Risks
              ...((data as any).ermRiskMapping.complianceRisks?.length > 0 ? [
                new Paragraph({ text: "Compliance Risks", heading: HeadingLevel.HEADING_2 }),
                ...this.buildWordTableFromArray((data as any).ermRiskMapping.complianceRisks, ['risk', 'indicator', 'severity'], stripCitations)
              ] : [])
            ] : []),

            // Underwriting Recommendation (financial_analysis)
            ...((((data as any).responseMetadata?.workflowType === 'financial_analysis') || ((data as any).financialRiskAssessment && (data as any).underwritingRecommendation)) && (data as any).underwritingRecommendation ? [
              new Paragraph({
                text: "Underwriting Recommendation",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Decision: ", bold: true, size: 24 }),
                  new TextRun({ text: (data as any).underwritingRecommendation.decision || 'N/A', size: 28, bold: true })
                ],
                spacing: { after: 150 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Rationale: ", bold: true, size: 24 }),
                  new TextRun({ text: (data as any).underwritingRecommendation.rationale || '', size: 24 })
                ],
                spacing: { after: 200 },
              }),
              ...((data as any).underwritingRecommendation.conditions?.length > 0 ? [
                new Paragraph({ text: "Conditions", heading: HeadingLevel.HEADING_2 }),
                ...(data as any).underwritingRecommendation.conditions.map((c: string) =>
                  new Paragraph({ children: [new TextRun({ text: `• ${c}`, size: 24 })], spacing: { after: 50 } })
                )
              ] : []),
              ...((data as any).underwritingRecommendation.pricingAdjustments ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Pricing Adjustments: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).underwritingRecommendation.pricingAdjustments, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).underwritingRecommendation.monitoringRequirements ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Monitoring Requirements: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).underwritingRecommendation.monitoringRequirements, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).underwritingRecommendation.metricsToTrack?.length > 0 ? [
                new Paragraph({ text: "Metrics to Track", heading: HeadingLevel.HEADING_2 }),
                ...(data as any).underwritingRecommendation.metricsToTrack.map((m: string) =>
                  new Paragraph({ children: [new TextRun({ text: `• ${m}`, size: 24 })], spacing: { after: 50 } })
                )
              ] : [])
            ] : []),

            // ===============================================
            // CLIENT RENEWAL PRESENTATION WORKFLOW SECTIONS
            // ===============================================

            // Company Context (client_renewal_presentation)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).companyContext ? [
              new Paragraph({
                text: "Company Context",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).companyContext.entityName ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Entity Name: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).companyContext.entityName, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).companyContext.primaryBusiness ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Primary Business: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).companyContext.primaryBusiness, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).companyContext.industrySector ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Industry Sector: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).companyContext.industrySector, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).companyContext.keyOperations ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Key Operations: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).companyContext.keyOperations, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).companyContext.geographicFootprint ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Geographic Footprint: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).companyContext.geographicFootprint, size: 24 })
                  ],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Document Inventory (client_renewal_presentation)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).documentInventory?.length > 0 ? [
              new Paragraph({
                text: "Document Inventory",
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordTableFromArray((data as any).documentInventory, ['documentType', 'policyLine', 'carrier', 'policyPeriod', 'completenessLevel'], stripCitations)
            ] : []),

            // Program Overview (client_renewal_presentation)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).programOverview ? [
              new Paragraph({
                text: "Program Overview",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).programOverview.totalPolicies ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Total Policies: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).programOverview.totalPolicies, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).programOverview.totalPremium || (data as any).programOverview.renewalPremium ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Total Premium: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).programOverview.totalPremium || (data as any).programOverview.renewalPremium || 'N/A', size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).programOverview.premiumChange ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Premium Change: ", bold: true, size: 24 }),
                    new TextRun({ text: `${(data as any).programOverview.premiumChange}${(data as any).programOverview.premiumChangePercent ? ` (${(data as any).programOverview.premiumChangePercent})` : ''}`, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).programOverview.carriersInvolved ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Carriers: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).programOverview.carriersInvolved, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).programOverview.overallAssessment ? [
                new Paragraph({
                  children: [new TextRun({ text: (data as any).programOverview.overallAssessment, size: 24, italics: true })],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Coverage Highlights (client_renewal_presentation)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).coverageHighlights?.length > 0 ? [
              new Paragraph({
                text: "Coverage Highlights",
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordTableFromArray((data as any).coverageHighlights, ['coverageName', 'carrier', 'limits', 'deductible', 'premium', 'whatItProtects'], stripCitations)
            ] : []),

            // Restrictive Endorsements (client_renewal_presentation)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).restrictiveEndorsements ? [
              new Paragraph({
                text: "Restrictive Endorsements",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).restrictiveEndorsements.totalRestrictionsIdentified ? [
                new Paragraph({
                  children: [new TextRun({ text: `Total Restrictions Identified: ${(data as any).restrictiveEndorsements.totalRestrictionsIdentified}`, size: 24, bold: true })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Critical Restrictions
              ...((data as any).restrictiveEndorsements.criticalRestrictions?.length > 0 ? [
                new Paragraph({ text: "Critical Restrictions", heading: HeadingLevel.HEADING_2 }),
                ...this.buildWordTableFromArray((data as any).restrictiveEndorsements.criticalRestrictions, ['restrictionName', 'category', 'priority', 'whatIsNotCovered', 'businessImpact'], stripCitations)
              ] : []),
              // Important Restrictions
              ...((data as any).restrictiveEndorsements.importantRestrictions?.length > 0 ? [
                new Paragraph({ text: "Important Restrictions", heading: HeadingLevel.HEADING_2 }),
                ...this.buildWordTableFromArray((data as any).restrictiveEndorsements.importantRestrictions, ['restrictionName', 'category', 'whatIsNotCovered', 'businessImpact'], stripCitations)
              ] : []),
              // Positive Endorsements
              ...((data as any).restrictiveEndorsements.positiveEndorsements?.length > 0 ? [
                new Paragraph({ text: "Positive Endorsements", heading: HeadingLevel.HEADING_2 }),
                ...this.buildWordTableFromArray((data as any).restrictiveEndorsements.positiveEndorsements, ['endorsementName', 'whatItAdds', 'businessBenefit'], stripCitations)
              ] : []),
              // Subcontractor Warning
              ...((data as any).restrictiveEndorsements.subcontractorWarningPresent ? [
                new Paragraph({
                  children: [new TextRun({ text: "⚠ Subcontractor Warning Present", size: 24, bold: true })],
                  spacing: { after: 100 },
                }),
                ...((data as any).restrictiveEndorsements.subcontractorWarningDetails ? [
                  new Paragraph({
                    children: [new TextRun({ text: (data as any).restrictiveEndorsements.subcontractorWarningDetails, size: 24 })],
                    spacing: { after: 200 },
                  })
                ] : [])
              ] : [])
            ] : []),

            // Premium Analysis (client_renewal_presentation)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).premiumAnalysis ? [
              new Paragraph({
                text: "Premium Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Total Expiring: ", bold: true, size: 24 }),
                  new TextRun({ text: (data as any).premiumAnalysis.totalExpiringPremium || 'N/A', size: 24 }),
                  new TextRun({ text: "  |  ", size: 24 }),
                  new TextRun({ text: "Total Renewal: ", bold: true, size: 24 }),
                  new TextRun({ text: (data as any).premiumAnalysis.totalRenewalPremium || 'N/A', size: 24 })
                ],
                spacing: { after: 100 },
              }),
              ...((data as any).premiumAnalysis.totalChange ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Total Change: ", bold: true, size: 24 }),
                    new TextRun({ text: `${(data as any).premiumAnalysis.totalChange}${(data as any).premiumAnalysis.totalChangePercent ? ` (${(data as any).premiumAnalysis.totalChangePercent})` : ''}`, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).premiumAnalysis.primaryDrivers ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Primary Drivers: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).premiumAnalysis.primaryDrivers, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).premiumAnalysis.valueAssessment ? [
                new Paragraph({
                  children: [new TextRun({ text: (data as any).premiumAnalysis.valueAssessment, size: 24, italics: true })],
                  spacing: { after: 150 },
                })
              ] : []),
              // Policy Breakdown Table
              ...((data as any).premiumAnalysis.policyBreakdown?.length > 0 ? [
                new Paragraph({ text: "Policy Breakdown", heading: HeadingLevel.HEADING_2 }),
                ...this.buildWordTableFromArray((data as any).premiumAnalysis.policyBreakdown, ['coverageName', 'expiringPremium', 'renewalPremium', 'change', 'changePercent', 'driver'], stripCitations)
              ] : [])
            ] : []),

            // Coverage Changes (client_renewal_presentation)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).coverageChanges?.length > 0 ? [
              new Paragraph({
                text: "Coverage Changes",
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordTableFromArray((data as any).coverageChanges, ['coverageName', 'changeType', 'changeDescription', 'expiringValue', 'renewalValue', 'clientImpact', 'assessment'], stripCitations)
            ] : []),

            // Red Flags (client_renewal_presentation)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).redFlags?.length > 0 ? [
              new Paragraph({
                text: "Red Flags",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).redFlags.map((flag: any, idx: number) => [
                new Paragraph({
                  children: [
                    new TextRun({ text: `${idx + 1}. ${flag.category || 'Issue'}`, bold: true, size: 24 }),
                    flag.severity ? new TextRun({ text: ` [${flag.severity}]`, size: 22, italics: true }) : new TextRun({ text: '' })
                  ],
                  spacing: { after: 50 },
                }),
                new Paragraph({
                  children: [new TextRun({ text: flag.description || '', size: 24 })],
                  spacing: { after: 50 },
                }),
                new Paragraph({
                  children: [new TextRun({ text: `Client Impact: ${flag.clientTranslation || 'N/A'}`, size: 22, italics: true })],
                  spacing: { after: 150 },
                })
              ]).flat()
            ] : []),

            // Positive Signals (client_renewal_presentation)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).positiveSignals?.length > 0 ? [
              new Paragraph({
                text: "Positive Signals",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).positiveSignals.map((signal: any, idx: number) => [
                new Paragraph({
                  children: [new TextRun({ text: `${idx + 1}. ${signal.category || 'Positive'}`, bold: true, size: 24 })],
                  spacing: { after: 50 },
                }),
                new Paragraph({
                  children: [new TextRun({ text: signal.description || '', size: 24 })],
                  spacing: { after: 50 },
                }),
                ...(signal.clientBenefit ? [
                  new Paragraph({
                    children: [new TextRun({ text: `Client Benefit: ${signal.clientBenefit}`, size: 22, italics: true })],
                    spacing: { after: 150 },
                  })
                ] : [])
              ]).flat()
            ] : []),

            // Action Items (client_renewal_presentation)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).actionItems?.length > 0 ? [
              new Paragraph({
                text: "Action Items",
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordTableFromArray((data as any).actionItems, ['priority', 'category', 'action', 'deadline', 'responsibleParty', 'details'], stripCitations)
            ] : []),

            // Conversation Points (client_renewal_presentation)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).conversationPoints?.length > 0 ? [
              new Paragraph({
                text: "Conversation Points",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).conversationPoints.map((point: any, idx: number) => [
                new Paragraph({
                  children: [new TextRun({ text: `${idx + 1}. ${point.topic || 'Topic'}`, bold: true, size: 24 })],
                  spacing: { after: 50 },
                }),
                ...(point.context ? [
                  new Paragraph({
                    children: [new TextRun({ text: point.context, size: 24 })],
                    spacing: { after: 50 },
                  })
                ] : []),
                new Paragraph({
                  children: [new TextRun({ text: `Considerations: ${point.considerations || 'N/A'}`, size: 22, italics: true })],
                  spacing: { after: 50 },
                }),
                ...(point.questionsToAsk ? [
                  new Paragraph({
                    children: [new TextRun({ text: `Questions: ${point.questionsToAsk}`, size: 22 })],
                    spacing: { after: 150 },
                  })
                ] : [])
              ]).flat()
            ] : []),

            // Glossary (client_renewal_presentation)
            ...((((data as any).responseMetadata?.workflowType === 'client_renewal_presentation') || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (data as any).glossary?.length > 0 ? [
              new Paragraph({
                text: "Glossary",
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordTableFromArray((data as any).glossary, ['term', 'definition', 'whyItMatters'], stripCitations)
            ] : []),

            // ===============================================
            // COVERAGE ANALYSIS WORKFLOW SECTIONS
            // ===============================================

            // Policy Overview (coverage_analysis)
            ...((((data as any).responseMetadata?.workflowType === 'coverage_analysis') || ((data as any).policyOverview && (data as any).coverageMetrics)) && (data as any).policyOverview ? [
              new Paragraph({
                text: "Policy Overview",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).policyOverview.policyType ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Policy Type: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).policyOverview.policyType, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).policyOverview.namedInsured ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Named Insured: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).policyOverview.namedInsured, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).policyOverview.policyPeriod ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Policy Period: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).policyOverview.policyPeriod, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).policyOverview.coverageLines?.length > 0 ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Coverage Lines: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).policyOverview.coverageLines.join(', '), size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).policyOverview.primaryLimits ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Primary Limits: ", bold: true, size: 24 }),
                    new TextRun({ text: `Occurrence: ${(data as any).policyOverview.primaryLimits.occurrence || 'N/A'} | Aggregate: ${(data as any).policyOverview.primaryLimits.aggregate || 'N/A'}`, size: 24 })
                  ],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Coverage Recommendations (coverage_analysis)
            ...((((data as any).responseMetadata?.workflowType === 'coverage_analysis') || ((data as any).policyOverview && (data as any).coverageMetrics)) && (data as any).coverageRecommendations?.length > 0 ? [
              new Paragraph({
                text: "Coverage Recommendations",
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordTableFromArray((data as any).coverageRecommendations, ['priority', 'category', 'recommendation', 'rationale'], stripCitations)
            ] : []),

            // Coverage Gaps & Opportunities (coverage_analysis)
            ...((((data as any).responseMetadata?.workflowType === 'coverage_analysis') || ((data as any).policyOverview && (data as any).coverageMetrics)) && (data as any).coverageGapsAndOpportunities ? [
              new Paragraph({
                text: "Coverage Gaps & Opportunities",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).coverageGapsAndOpportunities.summary ? [
                new Paragraph({
                  children: [new TextRun({ text: (data as any).coverageGapsAndOpportunities.summary, size: 24, italics: true })],
                  spacing: { after: 200 },
                })
              ] : []),
              ...((data as any).coverageGapsAndOpportunities.gaps?.length > 0 ? [
                ...this.buildWordTableFromArray((data as any).coverageGapsAndOpportunities.gaps, ['gapType', 'description', 'impact', 'recommendation'], stripCitations)
              ] : [])
            ] : []),

            // Scenario Testing (coverage_analysis)
            ...((((data as any).responseMetadata?.workflowType === 'coverage_analysis') || ((data as any).policyOverview && (data as any).coverageMetrics)) && (data as any).scenarioTesting ? [
              new Paragraph({
                text: "Scenario Testing",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).scenarioTesting.summary ? [
                new Paragraph({
                  children: [new TextRun({ text: (data as any).scenarioTesting.summary, size: 24, italics: true })],
                  spacing: { after: 200 },
                })
              ] : []),
              ...((data as any).scenarioTesting.scenarios?.length > 0 ? [
                ...this.buildWordTableFromArray((data as any).scenarioTesting.scenarios, ['scenario', 'coverage', 'outcome'], stripCitations)
              ] : [])
            ] : []),

            // Coverage Metrics (coverage_analysis)
            ...((((data as any).responseMetadata?.workflowType === 'coverage_analysis') || ((data as any).policyOverview && (data as any).coverageMetrics)) && (data as any).coverageMetrics ? [
              new Paragraph({
                text: "Coverage Metrics",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Overall Coverage Rating: ", bold: true, size: 24 }),
                  new TextRun({ text: (data as any).coverageMetrics.overallCoverageRating || 'N/A', size: 28, bold: true })
                ],
                spacing: { after: 100 },
              }),
              ...((data as any).coverageMetrics.coverageCompletenessScore !== undefined ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Completeness Score: ", bold: true, size: 24 }),
                    new TextRun({ text: `${(data as any).coverageMetrics.coverageCompletenessScore}%`, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).coverageMetrics.recommendedAction ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Recommended Action: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).coverageMetrics.recommendedAction, size: 24 })
                  ],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Meta Analysis (coverage_analysis)
            ...((((data as any).responseMetadata?.workflowType === 'coverage_analysis') || ((data as any).policyOverview && (data as any).coverageMetrics)) && (data as any).metaAnalysis ? [
              new Paragraph({
                text: "Meta Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).metaAnalysis.general ? [
                new Paragraph({
                  children: [new TextRun({ text: (data as any).metaAnalysis.general, size: 24 })],
                  spacing: { after: 200 },
                })
              ] : []),
              ...((data as any).metaAnalysis.antiPatterns ? [
                new Paragraph({ text: (data as any).metaAnalysis.antiPatterns.title || "Anti-Patterns", heading: HeadingLevel.HEADING_2 }),
                new Paragraph({
                  children: [new TextRun({ text: (data as any).metaAnalysis.antiPatterns.analysis || '', size: 24 })],
                  spacing: { after: 200 },
                })
              ] : []),
              ...((data as any).metaAnalysis.blindSpotAcknowledgment ? [
                new Paragraph({ text: (data as any).metaAnalysis.blindSpotAcknowledgment.title || "Blind Spot Acknowledgment", heading: HeadingLevel.HEADING_2 }),
                new Paragraph({
                  children: [new TextRun({ text: (data as any).metaAnalysis.blindSpotAcknowledgment.acknowledgment || '', size: 24 })],
                  spacing: { after: 200 },
                })
              ] : []),
              ...((data as any).metaAnalysis.aiConfidenceMandate ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "AI Confidence: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).metaAnalysis.aiConfidenceMandate, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).metaAnalysis.professionalDisclaimer ? [
                new Paragraph({ text: "Professional Disclaimer", heading: HeadingLevel.HEADING_2 }),
                new Paragraph({
                  children: [new TextRun({ text: (data as any).metaAnalysis.professionalDisclaimer, size: 20, italics: true })],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // ===============================================
            // RATE ANALYSIS WORKFLOW SECTIONS
            // ===============================================

            // Rate Analysis Executive Summary
            ...(((data as any).responseMetadata?.workflowType === 'rate_analysis' || (data as any).analysisTables || (data as any).driverHierarchy) && (data as any).executiveSummary ? [
              new Paragraph({
                text: "Executive Summary",
                heading: HeadingLevel.HEADING_1,
              }),
              // TL;DR
              ...((data as any).executiveSummary.tldr ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "TL;DR: ", bold: true, size: 24 }),
                    new TextRun({ text: this.extractTextFromCitationText((data as any).executiveSummary.tldr, stripCitations), size: 24 })
                  ],
                  spacing: { after: 200 },
                })
              ] : []),
              // Analysis Tier
              ...((data as any).executiveSummary.analysisTier ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Analysis Tier: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).executiveSummary.analysisTier, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              // Periods Analyzed
              ...((data as any).executiveSummary.periodsAnalyzed ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Periods Analyzed: ", bold: true, size: 24 }),
                    new TextRun({ text: Array.isArray((data as any).executiveSummary.periodsAnalyzed) ? (data as any).executiveSummary.periodsAnalyzed.join(', ') : (data as any).executiveSummary.periodsAnalyzed, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              // Policy Identification
              ...((data as any).executiveSummary.policyIdentification ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Policy Identification: ", bold: true, size: 24 }),
                    new TextRun({ text: this.extractTextFromCitationText((data as any).executiveSummary.policyIdentification, stripCitations), size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              // Total Premium Change
              ...((data as any).executiveSummary.totalPremiumChange ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Total Premium Change: ", bold: true, size: 24 }),
                    new TextRun({
                      text: `${(data as any).executiveSummary.totalPremiumChange.direction === 'increase' ? '↑' : (data as any).executiveSummary.totalPremiumChange.direction === 'decrease' ? '↓' : '→'} ${(data as any).executiveSummary.totalPremiumChange.percentage}% ($${(data as any).executiveSummary.totalPremiumChange.amount?.toLocaleString() || 'N/A'})`,
                      size: 24,
                      bold: true
                    })
                  ],
                  spacing: { after: 200 },
                })
              ] : []),
              // Primary Driver
              ...((data as any).executiveSummary.primaryDriver ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Primary Driver: ", bold: true, size: 24 }),
                    new TextRun({ text: this.extractTextFromCitationText((data as any).executiveSummary.primaryDriver, stripCitations), size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              // Secondary Drivers
              ...((data as any).executiveSummary.secondaryDrivers && Array.isArray((data as any).executiveSummary.secondaryDrivers) && (data as any).executiveSummary.secondaryDrivers.length > 0 ? [
                new Paragraph({
                  children: [new TextRun({ text: "Secondary Drivers:", bold: true, size: 24 })],
                  spacing: { after: 50 },
                }),
                ...(data as any).executiveSummary.secondaryDrivers.map((driver: any) =>
                  new Paragraph({
                    children: [new TextRun({ text: `• ${this.extractTextFromCitationText(driver, stripCitations)}`, size: 24 })],
                    spacing: { after: 50 },
                  })
                )
              ] : []),
              // Executive Narrative
              ...((data as any).executiveSummary.executiveNarrative ? [
                new Paragraph({
                  children: [new TextRun({ text: "Executive Narrative:", bold: true, size: 24 })],
                  spacing: { after: 50 },
                }),
                new Paragraph({
                  children: [new TextRun({ text: this.extractTextFromCitationText((data as any).executiveSummary.executiveNarrative, stripCitations), size: 24 })],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Rate Analysis Tables
            ...((data as any).analysisTables && Array.isArray((data as any).analysisTables) && (data as any).analysisTables.length > 0 ? [
              new Paragraph({
                text: "Analysis Tables",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).analysisTables.flatMap((table: any) => [
                new Paragraph({
                  text: table.title || "Analysis Table",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(table.description ? [
                  new Paragraph({
                    children: [new TextRun({ text: table.description, size: 22, italics: true })],
                    spacing: { after: 100 },
                  })
                ] : []),
                ...(table.headers && table.rows ? this.buildWordTableFromArray(
                  table.rows.map((row: any) => {
                    const obj: any = {};
                    table.headers.forEach((header: string, idx: number) => {
                      obj[header] = row[header] || row[header.toLowerCase()] || Object.values(row)[idx] || '';
                    });
                    return obj;
                  }),
                  table.headers,
                  stripCitations
                ) : []),
                ...(table.insights && Array.isArray(table.insights) && table.insights.length > 0 ? [
                  new Paragraph({
                    children: [new TextRun({ text: "Key Insights:", bold: true, size: 24 })],
                    spacing: { after: 50 },
                  }),
                  ...table.insights.map((insight: string) =>
                    new Paragraph({
                      children: [new TextRun({ text: `• ${insight}`, size: 24 })],
                      spacing: { after: 50 },
                    })
                  )
                ] : [])
              ])
            ] : []),

            // Rate Analysis Driver Hierarchy
            ...((data as any).driverHierarchy ? [
              new Paragraph({
                text: "Driver Hierarchy",
                heading: HeadingLevel.HEADING_1,
              }),
              // Primary Driver
              ...((data as any).driverHierarchy.primaryDriver ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Primary Driver: ", bold: true, size: 24 }),
                    new TextRun({ text: `${(data as any).driverHierarchy.primaryDriver.component} (${(data as any).driverHierarchy.primaryDriver.percentageOfTotal}% of total)`, size: 24 })
                  ],
                  spacing: { after: 100 },
                }),
                new Paragraph({
                  children: [new TextRun({ text: this.extractTextFromCitationText((data as any).driverHierarchy.primaryDriver.impact, stripCitations), size: 24 })],
                  spacing: { after: 200 },
                })
              ] : []),
              // Secondary Drivers
              ...((data as any).driverHierarchy.secondaryDrivers && Array.isArray((data as any).driverHierarchy.secondaryDrivers) ? [
                new Paragraph({
                  children: [new TextRun({ text: "Secondary Drivers:", bold: true, size: 24 })],
                  spacing: { after: 100 },
                }),
                ...(data as any).driverHierarchy.secondaryDrivers.flatMap((driver: any) => [
                  new Paragraph({
                    children: [
                      new TextRun({ text: `${driver.component}: `, bold: true, size: 24 }),
                      new TextRun({ text: `${driver.percentageOfTotal}% - ${this.extractTextFromCitationText(driver.impact, stripCitations)}`, size: 24 })
                    ],
                    spacing: { after: 100 },
                  })
                ])
              ] : []),
              // Interaction Effects
              ...((data as any).driverHierarchy.interactionEffects ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Interaction Effects: ", bold: true, size: 24 }),
                    new TextRun({ text: this.extractTextFromCitationText((data as any).driverHierarchy.interactionEffects, stripCitations), size: 24 })
                  ],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Rate Analysis Weak Signals
            ...((data as any).weakSignals && Array.isArray((data as any).weakSignals) && (data as any).weakSignals.length > 0 ? [
              new Paragraph({
                text: "Weak Signals Detected",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).weakSignals.flatMap((signal: any, idx: number) => [
                new Paragraph({
                  children: [
                    new TextRun({ text: `${idx + 1}. `, bold: true, size: 24 }),
                    new TextRun({ text: `[${signal.category}] `, bold: true, size: 24 }),
                    new TextRun({ text: signal.indicator, size: 24, bold: true })
                  ],
                  spacing: { after: 50 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: "Pattern: ", bold: true, size: 22 }),
                    new TextRun({ text: this.extractTextFromCitationText(signal.pattern, stripCitations), size: 22 })
                  ],
                  spacing: { after: 50 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: "Significance: ", bold: true, size: 22 }),
                    new TextRun({ text: this.extractTextFromCitationText(signal.significance, stripCitations), size: 22 })
                  ],
                  spacing: { after: 150 },
                })
              ])
            ] : []),

            // Rate Analysis Trend Analysis
            ...((data as any).trendAnalysis && (data as any).responseMetadata?.workflowType === 'rate_analysis' ? [
              new Paragraph({
                text: "Trend Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              // CAGR Metrics
              ...((data as any).trendAnalysis.cagr ? [
                new Paragraph({
                  children: [new TextRun({ text: "CAGR Metrics:", bold: true, size: 24 })],
                  spacing: { after: 50 },
                }),
                ...((data as any).trendAnalysis.cagr.premium !== undefined ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "  Premium CAGR: ", bold: true, size: 24 }),
                      new TextRun({ text: typeof (data as any).trendAnalysis.cagr.premium === 'number' ? `${((data as any).trendAnalysis.cagr.premium * 100).toFixed(1)}%` : (data as any).trendAnalysis.cagr.premium, size: 24 })
                    ],
                    spacing: { after: 50 },
                  })
                ] : []),
                ...((data as any).trendAnalysis.cagr.rate !== undefined ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "  Rate CAGR: ", bold: true, size: 24 }),
                      new TextRun({ text: typeof (data as any).trendAnalysis.cagr.rate === 'number' ? `${((data as any).trendAnalysis.cagr.rate * 100).toFixed(1)}%` : (data as any).trendAnalysis.cagr.rate, size: 24 })
                    ],
                    spacing: { after: 50 },
                  })
                ] : []),
                ...((data as any).trendAnalysis.cagr.basis !== undefined ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "  Basis CAGR: ", bold: true, size: 24 }),
                      new TextRun({ text: typeof (data as any).trendAnalysis.cagr.basis === 'number' ? `${((data as any).trendAnalysis.cagr.basis * 100).toFixed(1)}%` : (data as any).trendAnalysis.cagr.basis, size: 24 })
                    ],
                    spacing: { after: 100 },
                  })
                ] : [])
              ] : []),
              // Trend Strength
              ...((data as any).trendAnalysis.trendStrength ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Trend Strength: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).trendAnalysis.trendStrength, size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              // Trajectory
              ...((data as any).trendAnalysis.trajectory ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Trajectory: ", bold: true, size: 24 }),
                    new TextRun({ text: this.extractTextFromCitationText((data as any).trendAnalysis.trajectory, stripCitations), size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              // Inflection Points
              ...((data as any).trendAnalysis.inflectionPoints && Array.isArray((data as any).trendAnalysis.inflectionPoints) && (data as any).trendAnalysis.inflectionPoints.length > 0 ? [
                new Paragraph({
                  children: [new TextRun({ text: "Inflection Points:", bold: true, size: 24 })],
                  spacing: { after: 50 },
                }),
                ...(data as any).trendAnalysis.inflectionPoints.map((point: string) =>
                  new Paragraph({
                    children: [new TextRun({ text: `• ${point}`, size: 24 })],
                    spacing: { after: 50 },
                  })
                )
              ] : [])
            ] : []),

            // Rate Analysis Key Findings
            ...((data as any).keyFindings && (data as any).responseMetadata?.workflowType === 'rate_analysis' ? [
              new Paragraph({
                text: "Key Findings",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).keyFindings.summary ? [
                new Paragraph({
                  children: [new TextRun({ text: (data as any).keyFindings.summary, size: 24, italics: true })],
                  spacing: { after: 200 },
                })
              ] : []),
              ...((data as any).keyFindings.findings && Array.isArray((data as any).keyFindings.findings) ?
                (data as any).keyFindings.findings.flatMap((finding: any, idx: number) => [
                  new Paragraph({
                    children: [
                      new TextRun({ text: `${idx + 1}. ${finding.category}`, bold: true, size: 24 }),
                      new TextRun({ text: ` [${finding.magnitude}]`, size: 22, italics: true })
                    ],
                    spacing: { after: 50 },
                  }),
                  ...(finding.finding?.observation ? [
                    new Paragraph({
                      children: [
                        new TextRun({ text: "Observation: ", bold: true, size: 22 }),
                        new TextRun({ text: this.extractTextFromCitationText(finding.finding.observation, stripCitations), size: 22 })
                      ],
                      spacing: { after: 50 },
                    })
                  ] : []),
                  ...(finding.finding?.impact ? [
                    new Paragraph({
                      children: [
                        new TextRun({ text: "Impact: ", bold: true, size: 22 }),
                        new TextRun({ text: finding.finding.impact, size: 22 })
                      ],
                      spacing: { after: 50 },
                    })
                  ] : []),
                  ...(finding.finding?.recommendation ? [
                    new Paragraph({
                      children: [
                        new TextRun({ text: "Recommendation: ", bold: true, size: 22 }),
                        new TextRun({ text: finding.finding.recommendation, size: 22 })
                      ],
                      spacing: { after: 150 },
                    })
                  ] : [])
                ])
              : [])
            ] : []),

            // Rate Analysis Recommendations
            ...((data as any).recommendations && Array.isArray((data as any).recommendations) && (data as any).recommendations.length > 0 && (data as any).responseMetadata?.workflowType === 'rate_analysis' ? [
              new Paragraph({
                text: "Recommendations",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).recommendations.flatMap((rec: any, idx: number) => [
                new Paragraph({
                  children: [
                    new TextRun({ text: `${idx + 1}. ${rec.category}`, bold: true, size: 24 }),
                    new TextRun({ text: ` [${rec.priority}]`, size: 22, italics: true })
                  ],
                  spacing: { after: 50 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: "Action: ", bold: true, size: 22 }),
                    new TextRun({ text: this.extractTextFromCitationText(rec.action, stripCitations), size: 22 })
                  ],
                  spacing: { after: 50 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: "Rationale: ", bold: true, size: 22 }),
                    new TextRun({ text: this.extractTextFromCitationText(rec.rationale, stripCitations), size: 22 })
                  ],
                  spacing: { after: 50 },
                }),
                ...(rec.expectedOutcome ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Expected Outcome: ", bold: true, size: 22 }),
                      new TextRun({ text: rec.expectedOutcome, size: 22 })
                    ],
                    spacing: { after: 150 },
                  })
                ] : [])
              ])
            ] : []),

            // ===============================================
            // PORTFOLIO ANALYZER WORKFLOW SECTIONS
            // ===============================================

            // Portfolio Analyzer Executive Summary
            ...(((data as any).responseMetadata?.workflowType === 'portfolio_analyzer' || (data as any).portfolioStructure || (data as any).scenarioAnalysis) && (data as any).executiveSummary?.bottomLine ? [
              new Paragraph({
                text: "Executive Summary",
                heading: HeadingLevel.HEADING_1,
              }),
              // TL;DR
              ...((data as any).executiveSummary.tldr ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "TL;DR: ", bold: true, size: 24 }),
                    new TextRun({ text: this.extractTextFromCitationText((data as any).executiveSummary.tldr?.text || (data as any).executiveSummary.tldr, stripCitations), size: 24 })
                  ],
                  spacing: { after: 200 },
                })
              ] : []),
              // Bottom Line Metrics
              ...((data as any).executiveSummary.bottomLine ? [
                new Paragraph({
                  text: "Bottom Line",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...((data as any).executiveSummary.bottomLine.maximumAvailableCoverage !== undefined ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Maximum Available Coverage: ", bold: true, size: 24 }),
                      new TextRun({ text: `$${(data as any).executiveSummary.bottomLine.maximumAvailableCoverage.toLocaleString()}`, size: 24 })
                    ],
                    spacing: { after: 100 },
                  })
                ] : []),
                ...((data as any).executiveSummary.bottomLine.probableRecovery !== undefined ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Probable Recovery: ", bold: true, size: 24 }),
                      new TextRun({ text: `$${(data as any).executiveSummary.bottomLine.probableRecovery.toLocaleString()}`, size: 24 })
                    ],
                    spacing: { after: 100 },
                  })
                ] : []),
                ...((data as any).executiveSummary.bottomLine.insuredTotalRetention !== undefined ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Insured Total Retention: ", bold: true, size: 24 }),
                      new TextRun({ text: `$${(data as any).executiveSummary.bottomLine.insuredTotalRetention.toLocaleString()}`, size: 24 })
                    ],
                    spacing: { after: 100 },
                  })
                ] : []),
                ...((data as any).executiveSummary.bottomLine.coverageGaps !== undefined ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Coverage Gaps: ", bold: true, size: 24 }),
                      new TextRun({ text: `$${(data as any).executiveSummary.bottomLine.coverageGaps.toLocaleString()}`, size: 24 })
                    ],
                    spacing: { after: 200 },
                  })
                ] : [])
              ] : []),
              // Key Findings
              ...((data as any).executiveSummary.keyFindings && Array.isArray((data as any).executiveSummary.keyFindings) && (data as any).executiveSummary.keyFindings.length > 0 ? [
                new Paragraph({
                  text: "Key Findings",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).executiveSummary.keyFindings.map((finding: any, idx: number) =>
                  new Paragraph({
                    children: [new TextRun({ text: `${idx + 1}. ${this.extractTextFromCitationText(finding?.text || finding, stripCitations)}`, size: 24 })],
                    spacing: { after: 100 },
                  })
                )
              ] : []),
              // Critical Issues
              ...((data as any).executiveSummary.criticalIssues && Array.isArray((data as any).executiveSummary.criticalIssues) && (data as any).executiveSummary.criticalIssues.length > 0 ? [
                new Paragraph({
                  text: "Critical Issues",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).executiveSummary.criticalIssues.map((issue: any, idx: number) =>
                  new Paragraph({
                    children: [new TextRun({ text: `${idx + 1}. ${this.extractTextFromCitationText(issue?.text || issue, stripCitations)}`, size: 24, color: "B91C1C" })],
                    spacing: { after: 100 },
                  })
                )
              ] : [])
            ] : []),

            // Portfolio Structure
            ...((data as any).responseMetadata?.workflowType === 'portfolio_analyzer' || (data as any).portfolioStructure ? [
              ...((data as any).portfolioStructure ? [
                new Paragraph({
                  text: "Portfolio Structure",
                  heading: HeadingLevel.HEADING_1,
                }),
                // Vertical Towers
                ...((data as any).portfolioStructure.verticalTowers && Array.isArray((data as any).portfolioStructure.verticalTowers) ? [
                  new Paragraph({
                    text: "Coverage Towers",
                    heading: HeadingLevel.HEADING_2,
                  }),
                  ...(data as any).portfolioStructure.verticalTowers.flatMap((tower: any) => [
                    new Paragraph({
                      children: [
                        new TextRun({ text: tower.coverageLine || 'Coverage Tower', bold: true, size: 24 }),
                        ...(tower.totalVerticalLimit ? [new TextRun({ text: ` (Total: $${tower.totalVerticalLimit.toLocaleString()})`, size: 24 })] : [])
                      ],
                      spacing: { after: 100 },
                    }),
                    ...(tower.layers && Array.isArray(tower.layers) ?
                      this.buildWordTableFromArray(
                        tower.layers.map((layer: any) => ({
                          Layer: layer.layerType || `Layer ${layer.layerNumber || ''}`,
                          Carrier: layer.carrier || 'N/A',
                          Limit: layer.limit ? `$${layer.limit.toLocaleString()}` : 'N/A',
                          Attachment: layer.attachmentPoint ? `$${layer.attachmentPoint.toLocaleString()}` : 'Ground',
                          'Following Form': layer.followingForm ? 'Yes' : 'No'
                        })),
                        ['Layer', 'Carrier', 'Limit', 'Attachment', 'Following Form'],
                        stripCitations
                      )
                    : []),
                    ...(tower.gapsIdentified && tower.gapsIdentified.length > 0 ? [
                      new Paragraph({
                        children: [
                          new TextRun({ text: "Gaps Identified: ", bold: true, size: 22 }),
                          new TextRun({ text: Array.isArray(tower.gapsIdentified) ? tower.gapsIdentified.join(', ') : tower.gapsIdentified, size: 22 })
                        ],
                        spacing: { after: 150 },
                      })
                    ] : [])
                  ])
                ] : []),
                // Horizontal Coverage Lines
                ...((data as any).portfolioStructure.horizontalCoverageLines && Array.isArray((data as any).portfolioStructure.horizontalCoverageLines) ? [
                  new Paragraph({
                    text: "Coverage Lines Overview",
                    heading: HeadingLevel.HEADING_2,
                  }),
                  ...this.buildWordTableFromArray(
                    (data as any).portfolioStructure.horizontalCoverageLines.map((line: any) => ({
                      'Coverage Line': line.coverageLine || 'N/A',
                      'Carriers': line.carriers || 'N/A',
                      'Primary Limit': line.primaryLimit ? `$${line.primaryLimit.toLocaleString()}` : 'N/A',
                      'Total Available': line.totalAvailable ? `$${line.totalAvailable.toLocaleString()}` : 'N/A'
                    })),
                    ['Coverage Line', 'Carriers', 'Primary Limit', 'Total Available'],
                    stripCitations
                  )
                ] : [])
              ] : [])
            ] : []),

            // Scenario Analysis
            ...((data as any).scenarioAnalysis ? [
              new Paragraph({
                text: "Scenario Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).scenarioAnalysis.scenarioName ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Scenario: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).scenarioAnalysis.scenarioName, size: 24, bold: true })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).scenarioAnalysis.scenarioDescription ? [
                new Paragraph({
                  children: [new TextRun({ text: this.extractTextFromCitationText((data as any).scenarioAnalysis.scenarioDescription?.text || (data as any).scenarioAnalysis.scenarioDescription, stripCitations), size: 24 })],
                  spacing: { after: 150 },
                })
              ] : []),
              ...((data as any).scenarioAnalysis.components && Array.isArray((data as any).scenarioAnalysis.components) ? [
                new Paragraph({
                  text: "Scenario Components",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...this.buildWordTableFromArray(
                  (data as any).scenarioAnalysis.components.map((comp: any) => ({
                    'Component': comp.componentName || 'N/A',
                    'Exposure Type': comp.exposureType || 'N/A',
                    'Estimated Exposure': comp.estimatedExposure ? `$${comp.estimatedExposure.toLocaleString()}` : 'N/A'
                  })),
                  ['Component', 'Exposure Type', 'Estimated Exposure'],
                  stripCitations
                )
              ] : [])
            ] : []),

            // Portfolio Coverage Analysis
            ...((data as any).coverageAnalysis && Array.isArray((data as any).coverageAnalysis) && (data as any).coverageAnalysis.length > 0 && ((data as any).responseMetadata?.workflowType === 'portfolio_analyzer' || (data as any).portfolioStructure || (data as any).scenarioAnalysis) ? [
              new Paragraph({
                text: "Coverage Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).coverageAnalysis.flatMap((component: any) => [
                new Paragraph({
                  children: [new TextRun({ text: component.componentName || 'Component', bold: true, size: 24 })],
                  spacing: { after: 100 },
                }),
                ...(component.layerAnalysis && Array.isArray(component.layerAnalysis) ?
                  this.buildWordTableFromArray(
                    component.layerAnalysis.map((layer: any) => ({
                      'Layer': layer.layerName || 'N/A',
                      'Responds': layer.responds || 'N/A',
                      'Available Coverage': layer.availableCoverage ? `$${layer.availableCoverage.toLocaleString()}` : 'N/A',
                      'Probability': layer.probability ? `${layer.probability}%` : 'N/A',
                      'Rationale': this.extractTextFromCitationText(layer.rationale?.text || layer.rationale, stripCitations) || 'N/A'
                    })),
                    ['Layer', 'Responds', 'Available Coverage', 'Probability', 'Rationale'],
                    stripCitations
                  )
                : []),
                ...(component.totalVerticalCoverage ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Maximum Available: ", bold: true, size: 22 }),
                      new TextRun({ text: component.totalVerticalCoverage.maximumAvailable ? `$${component.totalVerticalCoverage.maximumAvailable.toLocaleString()}` : 'N/A', size: 22 }),
                      new TextRun({ text: "  |  Probable Recovery: ", bold: true, size: 22 }),
                      new TextRun({ text: component.totalVerticalCoverage.probableRecovery ? `$${component.totalVerticalCoverage.probableRecovery.toLocaleString()}` : 'N/A', size: 22 })
                    ],
                    spacing: { after: 200 },
                  })
                ] : [])
              ])
            ] : []),

            // Portfolio Synthesis
            ...((data as any).portfolioSynthesis ? [
              new Paragraph({
                text: "Portfolio Synthesis",
                heading: HeadingLevel.HEADING_1,
              }),
              // Total Portfolio Exposure
              ...((data as any).portfolioSynthesis.totalPortfolioExposure ? [
                new Paragraph({
                  text: "Total Portfolio Exposure",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...((data as any).portfolioSynthesis.totalPortfolioExposure.maximumAvailableCoverage !== undefined ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Maximum Available Coverage: ", bold: true, size: 24 }),
                      new TextRun({ text: `$${(data as any).portfolioSynthesis.totalPortfolioExposure.maximumAvailableCoverage.toLocaleString()}`, size: 24 })
                    ],
                    spacing: { after: 100 },
                  })
                ] : []),
                ...((data as any).portfolioSynthesis.totalPortfolioExposure.probableRecovery !== undefined ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Probable Recovery: ", bold: true, size: 24 }),
                      new TextRun({ text: `$${(data as any).portfolioSynthesis.totalPortfolioExposure.probableRecovery.toLocaleString()}`, size: 24 })
                    ],
                    spacing: { after: 100 },
                  })
                ] : []),
                ...((data as any).portfolioSynthesis.totalPortfolioExposure.totalInsuredPays !== undefined ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Total Insured Pays: ", bold: true, size: 24 }),
                      new TextRun({ text: `$${(data as any).portfolioSynthesis.totalPortfolioExposure.totalInsuredPays.toLocaleString()}`, size: 24 })
                    ],
                    spacing: { after: 100 },
                  })
                ] : []),
                ...((data as any).portfolioSynthesis.totalPortfolioExposure.totalUninsuredExposure !== undefined ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Total Uninsured Exposure: ", bold: true, size: 24 }),
                      new TextRun({ text: `$${(data as any).portfolioSynthesis.totalPortfolioExposure.totalUninsuredExposure.toLocaleString()}`, size: 24 })
                    ],
                    spacing: { after: 200 },
                  })
                ] : [])
              ] : []),
              // Coverage Gaps
              ...((data as any).portfolioSynthesis.coverageGaps && Array.isArray((data as any).portfolioSynthesis.coverageGaps) && (data as any).portfolioSynthesis.coverageGaps.length > 0 ? [
                new Paragraph({
                  text: "Coverage Gaps Identified",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).portfolioSynthesis.coverageGaps.flatMap((gap: any, idx: number) => [
                  new Paragraph({
                    children: [
                      new TextRun({ text: `${idx + 1}. `, bold: true, size: 24 }),
                      new TextRun({ text: this.extractTextFromCitationText(gap.description, stripCitations), size: 24 })
                    ],
                    spacing: { after: 50 },
                  }),
                  ...(gap.estimatedExposure ? [
                    new Paragraph({
                      children: [
                        new TextRun({ text: "   Estimated Exposure: ", bold: true, size: 22 }),
                        new TextRun({ text: `$${gap.estimatedExposure.toLocaleString()}`, size: 22 })
                      ],
                      spacing: { after: 100 },
                    })
                  ] : [])
                ])
              ] : [])
            ] : []),

            // Portfolio Assessment
            ...((data as any).assessment && ((data as any).responseMetadata?.workflowType === 'portfolio_analyzer' || (data as any).portfolioStructure || (data as any).scenarioAnalysis) ? [
              new Paragraph({
                text: "Assessment",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).assessment.portfolioCoverageCertainty ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Coverage Certainty: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).assessment.portfolioCoverageCertainty.level || 'N/A', size: 24, bold: true })
                  ],
                  spacing: { after: 50 },
                }),
                ...((data as any).assessment.portfolioCoverageCertainty.reasoning ? [
                  new Paragraph({
                    children: [new TextRun({ text: this.extractTextFromCitationText((data as any).assessment.portfolioCoverageCertainty.reasoning?.text || (data as any).assessment.portfolioCoverageCertainty.reasoning, stripCitations), size: 22 })],
                    spacing: { after: 150 },
                  })
                ] : [])
              ] : []),
              ...((data as any).assessment.portfolioDenialRisk ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Denial Risk: ", bold: true, size: 24 }),
                    new TextRun({ text: (data as any).assessment.portfolioDenialRisk.level || 'N/A', size: 24, bold: true })
                  ],
                  spacing: { after: 50 },
                }),
                ...((data as any).assessment.portfolioDenialRisk.reasoning ? [
                  new Paragraph({
                    children: [new TextRun({ text: this.extractTextFromCitationText((data as any).assessment.portfolioDenialRisk.reasoning?.text || (data as any).assessment.portfolioDenialRisk.reasoning, stripCitations), size: 22 })],
                    spacing: { after: 150 },
                  })
                ] : [])
              ] : []),
              ...((data as any).assessment.analysisConfidence ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Analysis Confidence: ", bold: true, size: 24 }),
                    new TextRun({ text: `${(data as any).assessment.analysisConfidence.percentage || 'N/A'}%`, size: 24, bold: true })
                  ],
                  spacing: { after: 50 },
                }),
                ...((data as any).assessment.analysisConfidence.reasoning ? [
                  new Paragraph({
                    children: [new TextRun({ text: this.extractTextFromCitationText((data as any).assessment.analysisConfidence.reasoning, stripCitations), size: 22 })],
                    spacing: { after: 100 },
                  })
                ] : []),
                ...((data as any).assessment.analysisConfidence.factorsReducingConfidence && Array.isArray((data as any).assessment.analysisConfidence.factorsReducingConfidence) ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Limiting Factors: ", bold: true, size: 22 }),
                      new TextRun({ text: (data as any).assessment.analysisConfidence.factorsReducingConfidence.join(', '), size: 22 })
                    ],
                    spacing: { after: 150 },
                  })
                ] : [])
              ] : [])
            ] : []),

            // Portfolio Recommendations
            ...((data as any).recommendations && Array.isArray((data as any).recommendations) && (data as any).recommendations.length > 0 && ((data as any).responseMetadata?.workflowType === 'portfolio_analyzer' || (data as any).portfolioStructure || (data as any).scenarioAnalysis) ? [
              new Paragraph({
                text: "Recommendations",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).recommendations.flatMap((rec: any, idx: number) => [
                new Paragraph({
                  children: [
                    new TextRun({ text: `${idx + 1}. ${rec.category || 'Recommendation'}`, bold: true, size: 24 }),
                    new TextRun({ text: ` [${rec.priority || 'Medium'}]`, size: 22, italics: true })
                  ],
                  spacing: { after: 50 },
                }),
                ...(rec.recommendation ? [
                  new Paragraph({
                    children: [new TextRun({ text: this.extractTextFromCitationText(rec.recommendation?.text || rec.recommendation, stripCitations), size: 24 })],
                    spacing: { after: 50 },
                  })
                ] : []),
                ...(rec.rationale ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Rationale: ", bold: true, size: 22 }),
                      new TextRun({ text: this.extractTextFromCitationText(rec.rationale?.text || rec.rationale, stripCitations), size: 22 })
                    ],
                    spacing: { after: 50 },
                  })
                ] : []),
                ...(rec.expectedBenefit ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Expected Benefit: ", bold: true, size: 22 }),
                      new TextRun({ text: this.extractTextFromCitationText(rec.expectedBenefit, stripCitations), size: 22 })
                    ],
                    spacing: { after: 150 },
                  })
                ] : [])
              ])
            ] : []),

            // ===== HOMEOWNERS COMPARISON & PERSONAL UMBRELLA =====
            // These workflows share a common schema structure with sections like
            // analysisHeader, baselineExtraction, gapSynthesis, decisionFramework, etc.

            // Executive Summary (homeowners/umbrella format: tldr, keyFindings, criticalActions, overallAssessment)
            ...(((data as any).responseMetadata?.workflowType === 'homeowners_comparison' || (data as any).responseMetadata?.workflowType === 'personal_umbrella' || ((data as any).baselineExtraction && (data as any).gapSynthesis)) && (data as any).executiveSummary?.tldr ? [
              new Paragraph({
                text: "Executive Summary",
                heading: HeadingLevel.HEADING_1,
              }),
              // TL;DR
              new Paragraph({
                children: [new TextRun({
                  text: this.extractTextFromCitationText((data as any).executiveSummary.tldr, stripCitations),
                  size: 24,
                  italics: true
                })],
                spacing: { after: 200 },
              }),
              // Key Findings
              ...((data as any).executiveSummary.keyFindings && Array.isArray((data as any).executiveSummary.keyFindings) ? [
                new Paragraph({
                  text: "Key Findings",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).executiveSummary.keyFindings.map((finding: string, idx: number) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `${idx + 1}. ${this.extractTextFromCitationText(finding, stripCitations)}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                )
              ] : []),
              // Critical Actions
              ...((data as any).executiveSummary.criticalActions && Array.isArray((data as any).executiveSummary.criticalActions) ? [
                new Paragraph({
                  text: "Critical Actions",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).executiveSummary.criticalActions.map((action: string, idx: number) =>
                  new Paragraph({
                    children: [new TextRun({
                      text: `${idx + 1}. ${this.extractTextFromCitationText(action, stripCitations)}`,
                      size: 24
                    })],
                    spacing: { after: 100 },
                  })
                )
              ] : []),
              // Overall Assessment
              ...((data as any).executiveSummary.overallAssessment ? [
                new Paragraph({
                  text: "Overall Assessment",
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText((data as any).executiveSummary.overallAssessment, stripCitations),
                    size: 24
                  })],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Analysis Header
            ...((data as any).analysisHeader ? [
              new Paragraph({
                text: "Analysis Overview",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).analysisHeader.namedInsured ? [new Paragraph({
                children: [new TextRun({ text: "Named Insured: ", bold: true, size: 24 }), new TextRun({ text: (data as any).analysisHeader.namedInsured, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).analysisHeader.propertyAddress ? [new Paragraph({
                children: [new TextRun({ text: "Property Address: ", bold: true, size: 24 }), new TextRun({ text: (data as any).analysisHeader.propertyAddress, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).analysisHeader.analysisDate ? [new Paragraph({
                children: [new TextRun({ text: "Analysis Date: ", bold: true, size: 24 }), new TextRun({ text: (data as any).analysisHeader.analysisDate, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).analysisHeader.track ? [new Paragraph({
                children: [new TextRun({ text: "Analysis Track: ", bold: true, size: 24 }), new TextRun({ text: (data as any).analysisHeader.track, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).analysisHeader.mode ? [new Paragraph({
                children: [new TextRun({ text: "Mode: ", bold: true, size: 24 }), new TextRun({ text: (data as any).analysisHeader.mode, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).analysisHeader.umbrellaType ? [new Paragraph({
                children: [new TextRun({ text: "Umbrella Type: ", bold: true, size: 24 }), new TextRun({ text: (data as any).analysisHeader.umbrellaType, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).analysisHeader.complexityTier ? [new Paragraph({
                children: [new TextRun({ text: "Complexity Tier: ", bold: true, size: 24 }), new TextRun({ text: (data as any).analysisHeader.complexityTier, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).analysisHeader.geographicRiskProfile ? [new Paragraph({
                children: [new TextRun({ text: "Geographic Risk Profile: ", bold: true, size: 24 }), new TextRun({ text: (data as any).analysisHeader.geographicRiskProfile, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).analysisHeader.documentsAnalyzed ? [new Paragraph({
                children: [new TextRun({ text: "Documents Analyzed: ", bold: true, size: 24 }), new TextRun({ text: (data as any).analysisHeader.documentsAnalyzed.join(', '), size: 24 })],
                spacing: { after: 200 },
              })] : [])
            ] : []),

            // Document Classification
            ...((data as any).documentClassification && Array.isArray((data as any).documentClassification) && (data as any).documentClassification.length > 0 ? [
              new Paragraph({
                text: "Document Classification",
                heading: HeadingLevel.HEADING_1,
              }),
              ...this.buildWordTableFromArray(
                (data as any).documentClassification,
                ['fileName', 'documentType', 'carrierName', 'keyDates', 'namedInsured'],
                stripCitations
              )
            ] : []),

            // Mode Rationale
            ...((data as any).modeRationale ? [
              new Paragraph({
                text: "Mode Rationale",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [new TextRun({
                  text: this.extractTextFromCitationText((data as any).modeRationale, stripCitations),
                  size: 24
                })],
                spacing: { after: 200 },
              })
            ] : []),

            // Temporal Verification
            ...((data as any).temporalVerification ? [
              new Paragraph({
                text: "Temporal Verification",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [new TextRun({ text: "Status: ", bold: true, size: 24 }), new TextRun({ text: (data as any).temporalVerification.status || '', size: 24 })],
                spacing: { after: 100 },
              }),
              ...((data as any).temporalVerification.dateSequence ? [new Paragraph({
                children: [new TextRun({ text: "Date Sequence: ", bold: true, size: 24 }), new TextRun({ text: (data as any).temporalVerification.dateSequence, size: 24 })],
                spacing: { after: 100 },
              })] : []),
              ...((data as any).temporalVerification.temporalClassification ? [new Paragraph({
                children: [new TextRun({ text: "Classification: ", bold: true, size: 24 }), new TextRun({ text: (data as any).temporalVerification.temporalClassification, size: 24 })],
                spacing: { after: 100 },
              })] : []),
              ...((data as any).temporalVerification.conflicts && (data as any).temporalVerification.conflicts.length > 0 ? [
                new Paragraph({
                  children: [new TextRun({ text: "Conflicts: ", bold: true, size: 24 }), new TextRun({ text: (data as any).temporalVerification.conflicts.join(', '), size: 24 })],
                  spacing: { after: 200 },
                })
              ] : [])
            ] : []),

            // Policy Architecture (personal umbrella only)
            ...((data as any).policyArchitecture ? [
              new Paragraph({
                text: "Policy Architecture",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).policyArchitecture.policyType ? [new Paragraph({
                children: [new TextRun({ text: "Policy Type: ", bold: true, size: 24 }), new TextRun({ text: (data as any).policyArchitecture.policyType, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).policyArchitecture.defenseCostTreatment ? [new Paragraph({
                children: [new TextRun({ text: "Defense Cost Treatment: ", bold: true, size: 24 }), new TextRun({ text: (data as any).policyArchitecture.defenseCostTreatment, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).policyArchitecture.sirAmount ? [new Paragraph({
                children: [new TextRun({ text: "Self-Insured Retention: ", bold: true, size: 24 }), new TextRun({ text: (data as any).policyArchitecture.sirAmount, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).policyArchitecture.coverageTerritory ? [new Paragraph({
                children: [new TextRun({ text: "Coverage Territory: ", bold: true, size: 24 }), new TextRun({ text: (data as any).policyArchitecture.coverageTerritory, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).policyArchitecture.dropDownProvisions ? [new Paragraph({
                children: [new TextRun({ text: "Drop-Down Provisions: ", bold: true, size: 24 }), new TextRun({ text: (data as any).policyArchitecture.dropDownProvisions, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).policyArchitecture.insuredDefinition ? [new Paragraph({
                children: [new TextRun({ text: "Insured Definition: ", bold: true, size: 24 }), new TextRun({ text: (data as any).policyArchitecture.insuredDefinition, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).policyArchitecture.typeEvidence ? [
                new Paragraph({
                  children: [new TextRun({ text: "Type Evidence: ", bold: true, size: 24 }), new TextRun({ text: this.extractTextFromCitationText((data as any).policyArchitecture.typeEvidence, stripCitations), size: 24 })],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).policyArchitecture.muiSchedule ? [
                new Paragraph({
                  text: "MUI Schedule",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...((data as any).policyArchitecture.muiSchedule.homeowners ? [new Paragraph({
                  children: [new TextRun({ text: "Homeowners: ", bold: true, size: 24 }), new TextRun({ text: (data as any).policyArchitecture.muiSchedule.homeowners, size: 24 })],
                  spacing: { after: 50 },
                })] : []),
                ...((data as any).policyArchitecture.muiSchedule.auto ? [new Paragraph({
                  children: [new TextRun({ text: "Auto: ", bold: true, size: 24 }), new TextRun({ text: (data as any).policyArchitecture.muiSchedule.auto, size: 24 })],
                  spacing: { after: 50 },
                })] : []),
                ...((data as any).policyArchitecture.muiSchedule.watercraft ? [new Paragraph({
                  children: [new TextRun({ text: "Watercraft: ", bold: true, size: 24 }), new TextRun({ text: (data as any).policyArchitecture.muiSchedule.watercraft, size: 24 })],
                  spacing: { after: 50 },
                })] : []),
                ...((data as any).policyArchitecture.muiSchedule.other ? [new Paragraph({
                  children: [new TextRun({ text: "Other: ", bold: true, size: 24 }), new TextRun({ text: (data as any).policyArchitecture.muiSchedule.other, size: 24 })],
                  spacing: { after: 50 },
                })] : [])
              ] : []),
              ...((data as any).policyArchitecture.scheduleOfForms ? [new Paragraph({
                children: [new TextRun({ text: "Schedule of Forms: ", bold: true, size: 24 }), new TextRun({ text: (data as any).policyArchitecture.scheduleOfForms, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).policyArchitecture.totalEndorsements != null ? [new Paragraph({
                children: [new TextRun({ text: "Total Endorsements: ", bold: true, size: 24 }), new TextRun({ text: String((data as any).policyArchitecture.totalEndorsements), size: 24 })],
                spacing: { after: 200 },
              })] : [])
            ] : []),

            // Schedule of Forms (homeowners comparison only)
            ...((data as any).scheduleOfForms && Array.isArray((data as any).scheduleOfForms) && (data as any).scheduleOfForms.length > 0 ? [
              new Paragraph({
                text: "Schedule of Forms",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).scheduleOfForms.flatMap((doc: any) => [
                new Paragraph({
                  text: `${doc.carrierName || 'Unknown Carrier'}`,
                  heading: HeadingLevel.HEADING_2,
                }),
                ...((doc.baseForm ? [new Paragraph({
                  children: [new TextRun({ text: "Base Form: ", bold: true, size: 24 }), new TextRun({ text: doc.baseForm, size: 24 })],
                  spacing: { after: 50 },
                })] : [])),
                ...((doc.totalEndorsements != null ? [new Paragraph({
                  children: [new TextRun({ text: "Total Endorsements: ", bold: true, size: 24 }), new TextRun({ text: String(doc.totalEndorsements), size: 24 })],
                  spacing: { after: 50 },
                })] : [])),
                ...(doc.endorsements && doc.endorsements.length > 0 ? [
                  new Paragraph({ children: [new TextRun({ text: "Endorsements:", bold: true, size: 24 })], spacing: { after: 50 } }),
                  ...doc.endorsements.map((e: any) => new Paragraph({
                    children: [new TextRun({ text: `• ${e.formNumber || ''}${e.description ? ' - ' + e.description : ''}${e.classification ? ' (' + e.classification + ')' : ''}`, size: 22 })],
                    spacing: { after: 50 },
                  }))
                ] : []),
                ...(doc.coverageExpanding && doc.coverageExpanding.length > 0 ? [new Paragraph({
                  children: [new TextRun({ text: "Coverage Expanding: ", bold: true, size: 24 }), new TextRun({ text: doc.coverageExpanding.join(', '), size: 24 })],
                  spacing: { after: 50 },
                })] : []),
                ...(doc.coverageRestricting && doc.coverageRestricting.length > 0 ? [new Paragraph({
                  children: [new TextRun({ text: "Coverage Restricting: ", bold: true, size: 24 }), new TextRun({ text: doc.coverageRestricting.join(', '), size: 24 })],
                  spacing: { after: 50 },
                })] : []),
                ...(doc.expectedButMissing && doc.expectedButMissing.length > 0 ? [new Paragraph({
                  children: [new TextRun({ text: "Expected But Missing: ", bold: true, size: 24 }), new TextRun({ text: doc.expectedButMissing.join(', '), size: 24 })],
                  spacing: { after: 50 },
                })] : []),
                ...(doc.informationGaps && doc.informationGaps.length > 0 ? [new Paragraph({
                  children: [new TextRun({ text: "Information Gaps: ", bold: true, size: 24 }), new TextRun({ text: doc.informationGaps.join(', '), size: 24 })],
                  spacing: { after: 100 },
                })] : [])
              ])
            ] : []),

            // Baseline Extraction
            ...((data as any).baselineExtraction && Array.isArray((data as any).baselineExtraction) && (data as any).baselineExtraction.length > 0 ? [
              new Paragraph({
                text: "Baseline Extraction",
                heading: HeadingLevel.HEADING_1,
              }),
              ...(data as any).baselineExtraction.flatMap((doc: any) => {
                const items: any[] = [
                  new Paragraph({
                    text: `${doc.carrierName || 'Unknown Carrier'}${doc.policyType ? ' - ' + doc.policyType : ''}`,
                    heading: HeadingLevel.HEADING_2,
                  })
                ];
                // Common fields for both workflows
                const fields = [
                  ['perOccurrenceLimit', 'Per Occurrence Limit'],
                  ['aggregateLimit', 'Aggregate Limit'],
                  ['sir', 'Self-Insured Retention'],
                  ['annualPremium', 'Annual Premium'],
                  // Homeowners-specific fields
                  ['coverageA', 'Coverage A (Dwelling)'],
                  ['coverageB', 'Coverage B (Other Structures)'],
                  ['coverageC', 'Coverage C (Personal Property)'],
                  ['coverageD', 'Coverage D (Loss of Use)'],
                  ['coverageE', 'Coverage E (Liability)'],
                  ['coverageF', 'Coverage F (Medical)'],
                  ['deductibleAOP', 'Deductible (AOP)'],
                  ['deductibleWind', 'Deductible (Wind)'],
                  ['deductibleNamedStorm', 'Deductible (Named Storm)'],
                  ['deductibleFlood', 'Deductible (Flood)'],
                  ['deductibleWaterBackup', 'Deductible (Water Backup)'],
                  ['otherDeductibles', 'Other Deductibles'],
                  ['premiumBreakdown', 'Premium Breakdown'],
                  ['specialLimits', 'Special Limits'],
                  ['keyEndorsements', 'Key Endorsements'],
                  ['settlementBasis', 'Settlement Basis'],
                  ['muiMet', 'MUI Met'],
                  ['muiShortfall', 'MUI Shortfall'],
                ];
                for (const [key, label] of fields) {
                  if (doc[key]) {
                    items.push(new Paragraph({
                      children: [new TextRun({ text: `${label}: `, bold: true, size: 24 }), new TextRun({ text: String(doc[key]), size: 24 })],
                      spacing: { after: 50 },
                    }));
                  }
                }
                if (doc.exclusions && doc.exclusions.length > 0) {
                  items.push(new Paragraph({
                    children: [new TextRun({ text: "Exclusions: ", bold: true, size: 24 }), new TextRun({ text: doc.exclusions.join(', '), size: 24 })],
                    spacing: { after: 50 },
                  }));
                }
                if (doc.keyFindings) {
                  items.push(new Paragraph({
                    children: [new TextRun({ text: "Key Findings: ", bold: true, size: 24 }), new TextRun({ text: this.extractTextFromCitationText(doc.keyFindings, stripCitations), size: 24 })],
                    spacing: { after: 100 },
                  }));
                }
                return items;
              })
            ] : []),

            // Exposure Profile
            ...((data as any).exposureProfile ? [
              new Paragraph({
                text: "Exposure Profile",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).exposureProfile.trackStatus ? [new Paragraph({
                children: [new TextRun({ text: "Track Status: ", bold: true, size: 24 }), new TextRun({ text: (data as any).exposureProfile.trackStatus, size: 24 })],
                spacing: { after: 100 },
              })] : []),
              ...((data as any).exposureProfile.familyComposition ? [new Paragraph({
                children: [new TextRun({ text: "Family Composition: ", bold: true, size: 24 }), new TextRun({ text: (data as any).exposureProfile.familyComposition, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).exposureProfile.jurisdictionalModifiers ? [new Paragraph({
                children: [new TextRun({ text: "Jurisdictional Modifiers: ", bold: true, size: 24 }), new TextRun({ text: (data as any).exposureProfile.jurisdictionalModifiers, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).exposureProfile.propertyDetails ? [new Paragraph({
                children: [new TextRun({ text: "Property Details: ", bold: true, size: 24 }), new TextRun({ text: (data as any).exposureProfile.propertyDetails, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).exposureProfile.geographicRisks ? [new Paragraph({
                children: [new TextRun({ text: "Geographic Risks: ", bold: true, size: 24 }), new TextRun({ text: (data as any).exposureProfile.geographicRisks, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).exposureProfile.complexityTierAssignment ? [new Paragraph({
                children: [new TextRun({ text: "Complexity Tier: ", bold: true, size: 24 }), new TextRun({ text: (data as any).exposureProfile.complexityTierAssignment, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).exposureProfile.activeCategories && (data as any).exposureProfile.activeCategories.length > 0 ? [
                new Paragraph({
                  text: "Active Exposure Categories",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...this.buildWordTableFromArray(
                  (data as any).exposureProfile.activeCategories,
                  ['category', 'activityLevel', 'underlyingPolicy', 'muiStatus'],
                  stripCitations
                )
              ] : []),
              ...((data as any).exposureProfile.probabilisticFlags && (data as any).exposureProfile.probabilisticFlags.length > 0 ? [
                new Paragraph({
                  children: [new TextRun({ text: "Probabilistic Flags:", bold: true, size: 24 })],
                  spacing: { after: 50 },
                }),
                ...(data as any).exposureProfile.probabilisticFlags.map((flag: string) =>
                  new Paragraph({
                    children: [new TextRun({ text: `• ${flag}`, size: 24 })],
                    spacing: { after: 50 },
                  })
                )
              ] : [])
            ] : []),

            // Drop-Down Matrix (personal umbrella only)
            ...((data as any).dropDownMatrix ? [
              new Paragraph({
                text: "Drop-Down Coverage Matrix",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).dropDownMatrix.trackStatus ? [new Paragraph({
                children: [new TextRun({ text: "Track Status: ", bold: true, size: 24 }), new TextRun({ text: (data as any).dropDownMatrix.trackStatus, size: 24 })],
                spacing: { after: 100 },
              })] : []),
              ...((data as any).dropDownMatrix.entries && (data as any).dropDownMatrix.entries.length > 0 ? [
                ...this.buildWordTableFromArray(
                  (data as any).dropDownMatrix.entries,
                  ['category', 'underlyingStatus', 'umbrellaTreatment', 'matrixOutcome', 'gapTier', 'quantifiedExposure'],
                  stripCitations
                )
              ] : [])
            ] : []),

            // Coverage Analysis (homeowners/umbrella format)
            ...((data as any).coverageAnalysis && ((data as any).responseMetadata?.workflowType === 'homeowners_comparison' || (data as any).responseMetadata?.workflowType === 'personal_umbrella') ? [
              new Paragraph({
                text: "Coverage Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).coverageAnalysis.mode ? [new Paragraph({
                children: [new TextRun({ text: "Analysis Mode: ", bold: true, size: 24 }), new TextRun({ text: (data as any).coverageAnalysis.mode, size: 24 })],
                spacing: { after: 100 },
              })] : []),
              ...((data as any).coverageAnalysis.structuralAssessment ? [new Paragraph({
                children: [new TextRun({ text: "Structural Assessment: ", bold: true, size: 24 }), new TextRun({ text: (data as any).coverageAnalysis.structuralAssessment, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).coverageAnalysis.exclusionMapping ? [new Paragraph({
                children: [new TextRun({ text: "Exclusion Mapping: ", bold: true, size: 24 }), new TextRun({ text: (data as any).coverageAnalysis.exclusionMapping, size: 24 })],
                spacing: { after: 50 },
              })] : []),
              ...((data as any).coverageAnalysis.muiCompliance ? [new Paragraph({
                children: [new TextRun({ text: "MUI Compliance: ", bold: true, size: 24 }), new TextRun({ text: (data as any).coverageAnalysis.muiCompliance, size: 24 })],
                spacing: { after: 100 },
              })] : []),
              // Comparison table
              ...((data as any).coverageAnalysis.comparison && (data as any).coverageAnalysis.comparison.length > 0 ? [
                new Paragraph({
                  text: "Coverage Comparison",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).coverageAnalysis.comparison.flatMap((item: any) => [
                  new Paragraph({
                    children: [
                      new TextRun({ text: `${item.element || ''}`, bold: true, size: 24 }),
                      ...(item.equivalent !== undefined ? [new TextRun({ text: item.equivalent ? ' (Equivalent)' : ' (Different)', size: 22, italics: true })] : []),
                      ...(item.superiorOption ? [new TextRun({ text: ` - Superior: ${item.superiorOption}`, size: 22, italics: true })] : [])
                    ],
                    spacing: { after: 50 },
                  }),
                  new Paragraph({
                    children: [new TextRun({ text: this.extractTextFromCitationText(item.details, stripCitations), size: 24 })],
                    spacing: { after: 100 },
                  })
                ])
              ] : []),
              // Change Detection
              ...((data as any).coverageAnalysis.changeDetection && (data as any).coverageAnalysis.changeDetection.length > 0 ? [
                new Paragraph({
                  text: "Change Detection",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...this.buildWordTableFromArray(
                  (data as any).coverageAnalysis.changeDetection,
                  ['element', 'changeType', 'priorValue', 'renewalValue', 'analysis'],
                  stripCitations
                )
              ] : []),
              // Adequacy Assessment (homeowners only)
              ...((data as any).coverageAnalysis.adequacyAssessment && (data as any).coverageAnalysis.adequacyAssessment.length > 0 ? [
                new Paragraph({
                  text: "Adequacy Assessment",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...this.buildWordTableFromArray(
                  (data as any).coverageAnalysis.adequacyAssessment,
                  ['exposure', 'covered', 'adequacy', 'gapTier'],
                  stripCitations
                )
              ] : [])
            ] : []),

            // Compound Failures
            ...((data as any).compoundFailures ? [
              new Paragraph({
                text: "Compound Failure Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [new TextRun({
                  text: (data as any).compoundFailures.detected ? 'Compound failures DETECTED' : 'No compound failures detected',
                  size: 24,
                  bold: true
                })],
                spacing: { after: 100 },
              }),
              // Failures (homeowners format)
              ...((data as any).compoundFailures.failures && (data as any).compoundFailures.failures.length > 0 ? (data as any).compoundFailures.failures.flatMap((f: any) => [
                new Paragraph({
                  children: [new TextRun({ text: `Pattern: ${f.pattern || ''}`, bold: true, size: 24 }), ...(f.tier ? [new TextRun({ text: ` (${f.tier})`, size: 22, italics: true })] : [])],
                  spacing: { after: 50 },
                }),
                new Paragraph({
                  children: [new TextRun({ text: `Combined Effect: ${f.combinedEffect || ''}`, size: 24 })],
                  spacing: { after: 50 },
                }),
                ...(f.quantifiedImpact ? [new Paragraph({
                  children: [new TextRun({ text: `Quantified Impact: ${f.quantifiedImpact}`, size: 24 })],
                  spacing: { after: 50 },
                })] : []),
                ...(f.components && f.components.length > 0 ? [new Paragraph({
                  children: [new TextRun({ text: `Components: ${f.components.join(', ')}`, size: 22 })],
                  spacing: { after: 100 },
                })] : [])
              ]) : []),
              // Pattern Results (umbrella format)
              ...((data as any).compoundFailures.patternResults && (data as any).compoundFailures.patternResults.length > 0 ? (data as any).compoundFailures.patternResults.flatMap((p: any) => [
                new Paragraph({
                  children: [
                    new TextRun({ text: `Pattern #${p.patternNumber || ''}: ${p.patternName || ''}`, bold: true, size: 24 }),
                    new TextRun({ text: p.detected ? ' - DETECTED' : ' - Not detected', size: 22, italics: true })
                  ],
                  spacing: { after: 50 },
                }),
                ...(p.combinedEffect ? [new Paragraph({
                  children: [new TextRun({ text: `Combined Effect: ${p.combinedEffect}`, size: 24 })],
                  spacing: { after: 50 },
                })] : []),
                ...(p.tier ? [new Paragraph({
                  children: [new TextRun({ text: `Tier: ${p.tier}`, size: 24 })],
                  spacing: { after: 50 },
                })] : []),
                ...(p.quantifiedImpact ? [new Paragraph({
                  children: [new TextRun({ text: `Quantified Impact: ${p.quantifiedImpact}`, size: 24 })],
                  spacing: { after: 100 },
                })] : [])
              ]) : [])
            ] : []),

            // Appearance of Coverage
            ...((data as any).appearanceOfCoverage ? [
              new Paragraph({
                text: "Appearance of Coverage Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [new TextRun({
                  text: (data as any).appearanceOfCoverage.detected ? 'Coverage appearance issues DETECTED' : 'No coverage appearance issues detected',
                  size: 24,
                  bold: true
                })],
                spacing: { after: 100 },
              }),
              ...((data as any).appearanceOfCoverage.alerts && (data as any).appearanceOfCoverage.alerts.length > 0 ? (data as any).appearanceOfCoverage.alerts.flatMap((a: any) => [
                new Paragraph({
                  children: [new TextRun({ text: `Signal: ${a.signal || ''}`, bold: true, size: 24 }), ...(a.tier ? [new TextRun({ text: ` (${a.tier})`, size: 22, italics: true })] : [])],
                  spacing: { after: 50 },
                }),
                ...(a.perceivedCoverage ? [new Paragraph({
                  children: [new TextRun({ text: "Perceived: ", bold: true, size: 24 }), new TextRun({ text: a.perceivedCoverage, size: 24 })],
                  spacing: { after: 50 },
                })] : []),
                ...(a.actualCoverage ? [new Paragraph({
                  children: [new TextRun({ text: "Actual: ", bold: true, size: 24 }), new TextRun({ text: a.actualCoverage, size: 24 })],
                  spacing: { after: 50 },
                })] : []),
                new Paragraph({
                  children: [new TextRun({ text: "Analysis: ", bold: true, size: 24 }), new TextRun({ text: a.analysis || '', size: 24 })],
                  spacing: { after: 100 },
                })
              ]) : [])
            ] : []),

            // Premium Analysis (homeowners/umbrella format)
            ...((data as any).premiumAnalysis && ((data as any).responseMetadata?.workflowType === 'homeowners_comparison' || (data as any).responseMetadata?.workflowType === 'personal_umbrella' || (data as any).baselineExtraction) &&
              // NOT client_renewal_presentation format (which has totalExpiringPremium)
              !(data as any).premiumAnalysis.totalExpiringPremium ? [
              new Paragraph({
                text: "Premium Analysis",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).premiumAnalysis.premiums && (data as any).premiumAnalysis.premiums.length > 0 ? [
                ...this.buildWordTableFromArray(
                  (data as any).premiumAnalysis.premiums,
                  ['carrierName', 'annualPremium', 'premiumPerMillion', 'premiumPerThousandCovA', 'totalAnnualCost', 'exclusionAdjustedValue'],
                  stripCitations
                )
              ] : []),
              ...((data as any).premiumAnalysis.sirImpactAnalysis ? [new Paragraph({
                children: [new TextRun({ text: "SIR Impact Analysis: ", bold: true, size: 24 }), new TextRun({ text: (data as any).premiumAnalysis.sirImpactAnalysis, size: 24 })],
                spacing: { after: 100 },
              })] : []),
              ...((data as any).premiumAnalysis.premiumChangeAnalysis ? [new Paragraph({
                children: [new TextRun({ text: "Premium Change Analysis: ", bold: true, size: 24 }), new TextRun({ text: (data as any).premiumAnalysis.premiumChangeAnalysis, size: 24 })],
                spacing: { after: 100 },
              })] : []),
              ...((data as any).premiumAnalysis.valueAssessment ? [new Paragraph({
                children: [new TextRun({ text: "Value Assessment: ", bold: true, size: 24 }), new TextRun({ text: this.extractTextFromCitationText((data as any).premiumAnalysis.valueAssessment, stripCitations), size: 24 })],
                spacing: { after: 200 },
              })] : [])
            ] : []),

            // Gap Synthesis
            ...((data as any).gapSynthesis ? [
              new Paragraph({
                text: `Gap Synthesis (${(data as any).gapSynthesis.totalGaps || 0} gaps identified)`,
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).gapSynthesis.documentDepthNotice ? [new Paragraph({
                children: [new TextRun({ text: (data as any).gapSynthesis.documentDepthNotice, size: 22, italics: true })],
                spacing: { after: 100 },
              })] : []),
              ...((data as any).gapSynthesis.gaps && (data as any).gapSynthesis.gaps.length > 0 ? (data as any).gapSynthesis.gaps.flatMap((gap: any) => [
                new Paragraph({
                  children: [
                    new TextRun({ text: `${gap.gapId || ''}: `, bold: true, size: 24 }),
                    new TextRun({ text: this.extractTextFromCitationText(gap.description, stripCitations), size: 24 })
                  ],
                  spacing: { after: 50 },
                }),
                new Paragraph({
                  children: [
                    ...(gap.tier ? [new TextRun({ text: `Tier: ${gap.tier}`, bold: true, size: 22 })] : []),
                    ...(gap.gapType ? [new TextRun({ text: `  |  Type: ${gap.gapType}`, size: 22 })] : []),
                    ...(gap.quantifiedExposure ? [new TextRun({ text: `  |  Exposure: ${gap.quantifiedExposure}`, size: 22 })] : []),
                    ...(gap.isCompound ? [new TextRun({ text: '  |  COMPOUND', size: 22, bold: true })] : [])
                  ],
                  spacing: { after: 50 },
                }),
                ...(gap.remediationPath || gap.remediation ? [new Paragraph({
                  children: [new TextRun({ text: "Remediation: ", bold: true, size: 24 }), new TextRun({ text: gap.remediationPath || gap.remediation, size: 24 })],
                  spacing: { after: 100 },
                })] : [])
              ]) : []),
              // Separate Coverage Recommendations (umbrella only)
              ...((data as any).gapSynthesis.separateCoverageRecommendations && (data as any).gapSynthesis.separateCoverageRecommendations.length > 0 ? [
                new Paragraph({
                  text: "Separate Coverage Recommendations",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...this.buildWordTableFromArray(
                  (data as any).gapSynthesis.separateCoverageRecommendations,
                  ['exposure', 'recommendedCoverage', 'urgency', 'estimatedCost'],
                  stripCitations
                )
              ] : [])
            ] : []),

            // Decision Framework
            ...((data as any).decisionFramework ? [
              new Paragraph({
                text: "Decision Framework",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [new TextRun({ text: "Pathway: ", bold: true, size: 24 }), new TextRun({ text: (data as any).decisionFramework.pathway || '', size: 24, bold: true })],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [new TextRun({ text: "Rationale: ", bold: true, size: 24 }), new TextRun({ text: this.extractTextFromCitationText((data as any).decisionFramework.rationale, stripCitations), size: 24 })],
                spacing: { after: 100 },
              }),
              // Per-Document Pathways (homeowners only)
              ...((data as any).decisionFramework.perDocumentPathways && (data as any).decisionFramework.perDocumentPathways.length > 0 ? [
                new Paragraph({
                  text: "Per-Document Pathways",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...this.buildWordTableFromArray(
                  (data as any).decisionFramework.perDocumentPathways,
                  ['carrierName', 'pathway', 'rationale'],
                  stripCitations
                )
              ] : []),
              // Remediation Items
              ...((data as any).decisionFramework.remediationItems && (data as any).decisionFramework.remediationItems.length > 0 ? [
                new Paragraph({
                  text: "Remediation Items",
                  heading: HeadingLevel.HEADING_2,
                }),
                ...(data as any).decisionFramework.remediationItems.flatMap((item: any) => [
                  new Paragraph({
                    children: [
                      new TextRun({ text: `${item.gapId || ''}: ${item.action || ''}`, bold: true, size: 24 }),
                      ...(item.priority ? [new TextRun({ text: ` (${item.priority})`, size: 22, italics: true })] : [])
                    ],
                    spacing: { after: 50 },
                  }),
                  ...(item.responsibleParty ? [new Paragraph({
                    children: [new TextRun({ text: `Responsible: ${item.responsibleParty}`, size: 22 })],
                    spacing: { after: 50 },
                  })] : []),
                  ...(item.estimatedCostImpact ? [new Paragraph({
                    children: [new TextRun({ text: `Estimated Cost Impact: ${item.estimatedCostImpact}`, size: 22 })],
                    spacing: { after: 100 },
                  })] : [])
                ])
              ] : []),
              // Recommendation
              new Paragraph({
                text: "Recommendation",
                heading: HeadingLevel.HEADING_2,
              }),
              new Paragraph({
                children: [new TextRun({
                  text: this.extractTextFromCitationText((data as any).decisionFramework.recommendation, stripCitations),
                  size: 24
                })],
                spacing: { after: 200 },
              })
            ] : []),

            // Blind Spots & Limitations (Portfolio Analyzer)
            ...((data as any).blindSpots && ((data as any).responseMetadata?.workflowType === 'portfolio_analyzer' || (data as any).portfolioStructure || (data as any).scenarioAnalysis) ? [
              new Paragraph({
                text: "Blind Spots & Limitations",
                heading: HeadingLevel.HEADING_1,
              }),
              ...((data as any).blindSpots.missingPolicies && Array.isArray((data as any).blindSpots.missingPolicies) && (data as any).blindSpots.missingPolicies.length > 0 ? [
                new Paragraph({
                  children: [new TextRun({ text: "Missing Policies:", bold: true, size: 24 })],
                  spacing: { after: 50 },
                }),
                ...(data as any).blindSpots.missingPolicies.map((policy: string) =>
                  new Paragraph({
                    children: [new TextRun({ text: `• ${this.extractTextFromCitationText(policy, stripCitations)}`, size: 24 })],
                    spacing: { after: 50 },
                  })
                )
              ] : []),
              ...((data as any).blindSpots.unknownClaimsHistory ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Unknown Claims History: ", bold: true, size: 24 }),
                    new TextRun({ text: this.extractTextFromCitationText((data as any).blindSpots.unknownClaimsHistory, stripCitations), size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).blindSpots.carrierCoordinationUncertainty ? [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Carrier Coordination Uncertainty: ", bold: true, size: 24 }),
                    new TextRun({ text: this.extractTextFromCitationText((data as any).blindSpots.carrierCoordinationUncertainty, stripCitations), size: 24 })
                  ],
                  spacing: { after: 100 },
                })
              ] : []),
              ...((data as any).blindSpots.otherLimitations && Array.isArray((data as any).blindSpots.otherLimitations) && (data as any).blindSpots.otherLimitations.length > 0 ? [
                new Paragraph({
                  children: [new TextRun({ text: "Other Limitations:", bold: true, size: 24 })],
                  spacing: { after: 50 },
                }),
                ...(data as any).blindSpots.otherLimitations.map((limitation: string) =>
                  new Paragraph({
                    children: [new TextRun({ text: `• ${this.extractTextFromCitationText(limitation, stripCitations)}`, size: 24 })],
                    spacing: { after: 50 },
                  })
                )
              ] : [])
            ] : []),

            // Missing-section catch-all: emits workflow-specific sections that
            // were absent from the inline branches above (riskShift,
            // nonStandardProvisions, dataQualityAssessment, insurance_summary,
            // loss_run_analysis v1, renewal_analysis, risk_assessment,
            // researchOverview, policy_analysis policyOverview, year_over_year
            // metaAnalysis). See buildMissingWordSections for the full matrix.
            ...this.buildMissingWordSections(data, stripCitations),

            // Disclaimer - always include (use default if none from AI)
            ...(() => {
              const DEFAULT_DISCLAIMER = 'This analysis is generated by an AI assistant and is intended for use by experienced insurance professionals as a supplementary tool, not as a substitute for their own expert judgment. By using this analysis, you acknowledge and agree to the following limitations:\n\nNot Legal or Professional Advice: This analysis is a form of sophisticated pattern recognition and language processing; it is not legal advice, a definitive coverage determination, or a substitute for the nuanced judgment of a seasoned human professional.\n\nKnowledge Cutoff: The analysis may not reflect the most recent court decisions, regulatory changes, or emerging legal interpretations.\n\nContext is Key: This analysis is strictly limited to the documents and context provided.\n\nAll findings must be independently verified by a qualified professional.';
              const disclaimerText = (data as any).disclaimer ||
                ((data as any).professionalDisclaimer?.text || (typeof (data as any).professionalDisclaimer === 'string' ? (data as any).professionalDisclaimer : null)) ||
                DEFAULT_DISCLAIMER;
              return [
                new Paragraph({
                  text: "Professional Disclaimer",
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  children: [new TextRun({
                    text: disclaimerText,
                    size: 20,
                    italics: true
                  })],
                  spacing: { after: 200 },
                })
              ];
            })()
          ]
        }]
      });

      // Generate and download Word document (browser-compatible)
      const blob = await Packer.toBlob(doc);
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Word export failed:', error);
      throw new Error(`Failed to export Word document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Build Word table from array of objects with specified columns
   */
  private static buildWordTableFromArray(items: any[], columns: string[], stripCitations: boolean): any[] {
    if (!items || items.length === 0) return [];

    // Format column headers for display
    const formatHeader = (col: string) => {
      return col
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, s => s.toUpperCase())
        .trim();
    };

    const headers = columns.map(col => formatHeader(col));
    const columnCount = columns.length;

    const tableRows = [
      // Header row
      new TableRow({
        children: headers.map(header =>
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: header,
                bold: true,
                size: 22
              })]
            })],
            shading: {
              type: ShadingType.SOLID,
              color: "E5E7EB"
            }
          })
        )
      }),
      // Data rows
      ...items.map(item =>
        new TableRow({
          children: columns.map(col => {
            const value = item[col];
            const textValue = this.extractTextFromCitationText(value, stripCitations);
            return new TableCell({
              children: [new Paragraph({
                children: [new TextRun({
                  text: textValue || '',
                  size: 20
                })]
              })]
            });
          })
        })
      )
    ];

    return [
      new Table({
        rows: tableRows,
        width: {
          size: 10000,
          type: WidthType.DXA
        },
        columnWidths: Array(columnCount).fill(Math.floor(10000 / columnCount)),
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
          left: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
          right: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
          insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" }
        }
      }),
      new Paragraph({ text: "" })
    ];
  }

  /**
   * Build Word document matrices
   */
  /**
   * Build Word document children for the YOY Renewal opt-in add-on sections
   * (Schedules, Coverage Details, Compliance & Contractual). The sections
   * live under reportAddOns.* on the saved report — produced by a separate
   * focused Vertex AI call because the base yoy_renewal schema is at
   * Vertex's structured-output complexity ceiling.
   */
  private static buildYoyAddOnsWordSections(data: any, stripCitations: boolean): any[] {
    const addOns: any = data?.reportAddOns;
    if (!addOns) return [];
    const out: any[] = [];

    // -------- Schedules --------
    // Tolerate every shape Gemini has produced for this field.
    let scheduleList: any[] = [];
    if (Array.isArray(addOns.schedules)) {
      scheduleList = addOns.schedules.length > 0 && addOns.schedules.every((s: any) => Array.isArray(s))
        ? addOns.schedules.flat()
        : addOns.schedules;
    } else if (addOns.schedules && Array.isArray(addOns.schedules.schedules)) {
      scheduleList = addOns.schedules.schedules;
    }
    if (scheduleList.length > 0) {
      out.push(new Paragraph({ text: 'Schedules', heading: HeadingLevel.HEADING_1 }));
      for (const sched of scheduleList) {
        if (!sched || !Array.isArray(sched.headers) || !Array.isArray(sched.rows)) continue;
        // Schedule title + source
        out.push(new Paragraph({
          text: sched.scheduleType || 'Schedule',
          heading: HeadingLevel.HEADING_2,
        }));
        if (sched.policyDocName) {
          out.push(new Paragraph({
            children: [new TextRun({ text: `Source: ${sched.policyDocName}`, italics: true, size: 20, color: '666666' })],
            spacing: { after: 100 },
          }));
        }
        // Reuse the matrix builder for the rows
        out.push(...this.buildWordMatrices(
          [{ title: '', headers: sched.headers, rows: sched.rows }],
          stripCitations
        ));
        if (Array.isArray(sched.additionsVsPriorPolicy) && sched.additionsVsPriorPolicy.length > 0) {
          out.push(new Paragraph({
            children: [new TextRun({ text: 'Added vs prior policy:', bold: true, size: 22 })],
            spacing: { before: 100 },
          }));
          for (const a of sched.additionsVsPriorPolicy) {
            out.push(new Paragraph({
              children: [new TextRun({ text: `• ${a}`, size: 22 })],
            }));
          }
        }
        if (Array.isArray(sched.removalsVsPriorPolicy) && sched.removalsVsPriorPolicy.length > 0) {
          out.push(new Paragraph({
            children: [new TextRun({ text: 'Removed vs prior policy:', bold: true, size: 22 })],
            spacing: { before: 100 },
          }));
          for (const r of sched.removalsVsPriorPolicy) {
            out.push(new Paragraph({
              children: [new TextRun({ text: `• ${r}`, size: 22 })],
            }));
          }
        }
        out.push(new Paragraph({ text: '' }));
      }
    }

    // -------- Coverage Details --------
    const cds = addOns.coverageDetails;
    if (cds && typeof cds === 'object') {
      const matrixSubFields: Array<[string, string]> = [
        ['deductiblesAndRetentions', 'Deductibles & Retentions'],
        ['subLimits', 'Sub-Limits'],
        ['enhancementEndorsementLimits', 'Enhancement Endorsement Limits'],
        ['coverageTriggers', 'Coverage Triggers'],
      ];
      const hasMatrixContent = matrixSubFields.some(([k]) => {
        const m = cds[k];
        return m && Array.isArray(m.headers) && Array.isArray(m.rows) && m.rows.length > 0;
      });
      const hasOtherContent = (Array.isArray(cds.exclusionsAdded) && cds.exclusionsAdded.length > 0)
        || (Array.isArray(cds.exclusionsRemoved) && cds.exclusionsRemoved.length > 0)
        || (Array.isArray(cds.exclusionsModified) && cds.exclusionsModified.length > 0)
        || (Array.isArray(cds.keyDefinitions) && cds.keyDefinitions.length > 0)
        || (typeof cds.exclusionsModified === 'string' && cds.exclusionsModified.trim())
        || (typeof cds.keyDefinitions === 'string' && cds.keyDefinitions.trim());
      if (hasMatrixContent || hasOtherContent) {
        out.push(new Paragraph({ text: 'Coverage Details', heading: HeadingLevel.HEADING_1 }));
        for (const [key, label] of matrixSubFields) {
          const m = cds[key];
          if (m && Array.isArray(m.headers) && Array.isArray(m.rows) && m.rows.length > 0) {
            out.push(new Paragraph({ text: label, heading: HeadingLevel.HEADING_2 }));
            out.push(...this.buildWordMatrices(
              [{ title: '', headers: m.headers, rows: m.rows }],
              stripCitations
            ));
          }
        }
        if (Array.isArray(cds.exclusionsAdded) && cds.exclusionsAdded.length > 0) {
          out.push(new Paragraph({ text: 'Exclusions Added', heading: HeadingLevel.HEADING_2 }));
          for (const e of cds.exclusionsAdded) {
            const text = typeof e === 'string' ? e : this.extractTextFromCitationText(e, stripCitations);
            out.push(new Paragraph({ children: [new TextRun({ text: `• ${text}`, size: 22 })] }));
          }
        }
        if (Array.isArray(cds.exclusionsRemoved) && cds.exclusionsRemoved.length > 0) {
          out.push(new Paragraph({ text: 'Exclusions Removed', heading: HeadingLevel.HEADING_2 }));
          for (const e of cds.exclusionsRemoved) {
            const text = typeof e === 'string' ? e : this.extractTextFromCitationText(e, stripCitations);
            out.push(new Paragraph({ children: [new TextRun({ text: `• ${text}`, size: 22 })] }));
          }
        }
        if (Array.isArray(cds.exclusionsModified) && cds.exclusionsModified.length > 0) {
          out.push(new Paragraph({ text: 'Exclusions Modified', heading: HeadingLevel.HEADING_2 }));
          for (const m of cds.exclusionsModified) {
            out.push(new Paragraph({
              children: [new TextRun({ text: m?.exclusion || '', bold: true, size: 22 })],
            }));
            if (m?.expiringLanguage) {
              out.push(new Paragraph({
                children: [
                  new TextRun({ text: 'Expiring: ', bold: true, size: 20 }),
                  new TextRun({ text: this.extractTextFromCitationText(m.expiringLanguage, stripCitations), size: 20 }),
                ],
              }));
            }
            if (m?.renewalLanguage) {
              out.push(new Paragraph({
                children: [
                  new TextRun({ text: 'Renewal: ', bold: true, size: 20 }),
                  new TextRun({ text: this.extractTextFromCitationText(m.renewalLanguage, stripCitations), size: 20 }),
                ],
                spacing: { after: 100 },
              }));
            }
          }
        } else if (typeof cds.exclusionsModified === 'string' && cds.exclusionsModified.trim()) {
          out.push(new Paragraph({ text: 'Exclusions Modified', heading: HeadingLevel.HEADING_2 }));
          out.push(new Paragraph({ children: [new TextRun({ text: cds.exclusionsModified, size: 22 })] }));
        }
        if (Array.isArray(cds.keyDefinitions) && cds.keyDefinitions.length > 0) {
          out.push(new Paragraph({ text: 'Key Definitions', heading: HeadingLevel.HEADING_2 }));
          for (const kd of cds.keyDefinitions) {
            out.push(new Paragraph({
              children: [
                new TextRun({ text: kd?.term || '', bold: true, size: 22 }),
                ...(kd?.materialDifference ? [new TextRun({ text: '  (Material difference)', italics: true, color: 'B45309', size: 20 })] : []),
              ],
            }));
            if (kd?.expiringDefinition) {
              out.push(new Paragraph({
                children: [
                  new TextRun({ text: 'Expiring: ', bold: true, size: 20 }),
                  new TextRun({ text: this.extractTextFromCitationText(kd.expiringDefinition, stripCitations), size: 20 }),
                ],
              }));
            }
            if (kd?.renewalDefinition) {
              out.push(new Paragraph({
                children: [
                  new TextRun({ text: 'Renewal: ', bold: true, size: 20 }),
                  new TextRun({ text: this.extractTextFromCitationText(kd.renewalDefinition, stripCitations), size: 20 }),
                ],
                spacing: { after: 100 },
              }));
            }
          }
        } else if (typeof cds.keyDefinitions === 'string' && cds.keyDefinitions.trim()) {
          out.push(new Paragraph({ text: 'Key Definitions', heading: HeadingLevel.HEADING_2 }));
          out.push(new Paragraph({ children: [new TextRun({ text: cds.keyDefinitions, size: 22 })] }));
        }
      }
    }

    // -------- Compliance & Contractual --------
    const cc = addOns.complianceContractual;
    if (cc && typeof cc === 'object') {
      out.push(new Paragraph({ text: 'Compliance & Contractual', heading: HeadingLevel.HEADING_1 }));
      if (cc.summary) {
        out.push(new Paragraph({
          children: [new TextRun({
            text: typeof cc.summary === 'string' ? cc.summary : this.extractTextFromCitationText(cc.summary, stripCitations),
            size: 24,
          })],
          spacing: { after: 200 },
        }));
      }
      if (!cc.contractFound) {
        out.push(new Paragraph({
          children: [new TextRun({
            text: 'No underlying contract was uploaded. Upload the relevant contract(s) for a full compliance review.',
            italics: true,
            color: '92400E',
            size: 22,
          })],
          spacing: { after: 200 },
        }));
      }
      const ccMatrixFields: Array<[string, string]> = [
        ['coiRequirements', 'COI Requirements vs Policy'],
        ['contractualObligations', 'Contractual Obligations'],
      ];
      for (const [key, label] of ccMatrixFields) {
        const m = cc[key];
        if (m && Array.isArray(m.headers) && Array.isArray(m.rows) && m.rows.length > 0) {
          out.push(new Paragraph({ text: label, heading: HeadingLevel.HEADING_2 }));
          out.push(...this.buildWordMatrices(
            [{ title: '', headers: m.headers, rows: m.rows }],
            stripCitations
          ));
        } else if (typeof m === 'string' && m.trim()) {
          out.push(new Paragraph({ text: label, heading: HeadingLevel.HEADING_2 }));
          out.push(new Paragraph({ children: [new TextRun({ text: m, size: 22 })] }));
        }
      }
    }

    return out;
  }

  /**
   * Emit Word document sections that are required by various workflow schemas
   * but were missing from the inline branches in exportUniversalJsonToWord.
   * Each block gates on workflowType AND/OR a structural signature so we still
   * render the section when Vertex AI drops the workflowType marker.
   *
   * Covers (audit dated 2026-05-31):
   *  - policy_comparison / document_comparison: riskShift, nonStandardProvisions,
   *    dataQualityAssessment
   *  - insurance_summary: whole workflow (named_insured, lines_of_coverage,
   *    narrative, etc.)
   *  - loss_run_analysis (v1, not v3.1/v4.1 analyzer): crossFootCheck,
   *    claimLineItems, categoryAnalysis, root-level analystNotes
   *  - renewal_analysis: renewalRecommendations, marketAnalysis, renewalStrategy
   *  - risk_assessment: riskOverview, riskMatrices, identifiedRisks
   *  - research_analysis: researchOverview
   *  - policy_analysis: policyOverview (coverage_analysis structural gate
   *    misfires when coverageMetrics is absent)
   *  - year_over_year: metaAnalysis (same gate misfire when policyOverview/
   *    coverageMetrics absent)
   */
  private static buildMissingWordSections(data: any, stripCitations: boolean): any[] {
    const out: any[] = [];
    const d = data as any;
    const wf: string = d?.responseMetadata?.workflowType || '';

    // === POLICY/DOCUMENT COMPARISON ===
    const isComparison =
      wf === 'policy_comparison' ||
      wf === 'document_comparison' ||
      !!d.riskShift ||
      !!d.dataQualityAssessment;

    if (isComparison) {
      // Non-Standard Provisions
      if (d.nonStandardProvisions?.provisions?.length) {
        out.push(new Paragraph({ text: 'Non-Standard Provisions', heading: HeadingLevel.HEADING_1 }));
        for (const p of d.nonStandardProvisions.provisions) {
          out.push(new Paragraph({
            text: this.extractTextFromCitationText(p.provision, stripCitations) || 'Provision',
            heading: HeadingLevel.HEADING_2,
          }));
          if (p.location) {
            out.push(new Paragraph({
              children: [
                new TextRun({ text: 'Location: ', bold: true, size: 22 }),
                new TextRun({ text: this.extractTextFromCitationText(p.location, stripCitations), size: 22 }),
              ],
              spacing: { after: 60 },
            }));
          }
          if (p.deviation) {
            out.push(new Paragraph({
              children: [
                new TextRun({ text: 'Deviation: ', bold: true, size: 22 }),
                new TextRun({ text: this.extractTextFromCitationText(p.deviation, stripCitations), size: 22 }),
              ],
              spacing: { after: 60 },
            }));
          }
          if (p.riskAssessment) {
            out.push(new Paragraph({
              children: [
                new TextRun({ text: 'Risk Assessment: ', bold: true, size: 22 }),
                new TextRun({ text: this.extractTextFromCitationText(p.riskAssessment, stripCitations), size: 22 }),
              ],
              spacing: { after: 160 },
            }));
          }
        }
      }

      // Risk-Shift Navigator
      const rs = d.riskShift;
      if (rs) {
        out.push(new Paragraph({ text: 'Risk-Shift Navigator', heading: HeadingLevel.HEADING_1 }));

        if (rs.tldr) {
          out.push(new Paragraph({ text: 'Summary', heading: HeadingLevel.HEADING_2 }));
          const tldrText = typeof rs.tldr === 'string' ? rs.tldr : (rs.tldr?.text || '');
          out.push(new Paragraph({
            children: [new TextRun({ text: this.extractTextFromCitationText(tldrText, stripCitations), size: 24 })],
            spacing: { after: 160 },
          }));
        }

        if (rs.keyTalkingPoints?.length) {
          out.push(new Paragraph({ text: 'Key Talking Points', heading: HeadingLevel.HEADING_2 }));
          rs.keyTalkingPoints.forEach((pt: string, i: number) => {
            out.push(new Paragraph({
              children: [new TextRun({
                text: `${i + 1}. ${this.extractTextFromCitationText(pt, stripCitations)}`,
                size: 22,
              })],
              spacing: { after: 60 },
            }));
          });
        }

        if (rs.detailedComparisonTable?.headers?.length && rs.detailedComparisonTable?.rows?.length) {
          out.push(new Paragraph({ text: 'Detailed Change Analysis', heading: HeadingLevel.HEADING_2 }));
          out.push(...this.buildSimpleWordTable(
            rs.detailedComparisonTable.headers,
            rs.detailedComparisonTable.rows,
            stripCitations,
          ));
        }

        const meta = rs.metaAnalysis;
        if (meta) {
          out.push(new Paragraph({ text: 'Meta-Analysis & Strategic Insights', heading: HeadingLevel.HEADING_2 }));

          if (meta.suspiciousAbsences?.length) {
            out.push(new Paragraph({ text: 'Suspicious Absences', heading: HeadingLevel.HEADING_3 }));
            meta.suspiciousAbsences.forEach((s: string) => {
              out.push(new Paragraph({
                children: [new TextRun({
                  text: `• ${this.extractTextFromCitationText(s, stripCitations)}`,
                  size: 22,
                })],
                spacing: { after: 40 },
              }));
            });
          }

          if (meta.interconnectivityAnalysis?.length) {
            out.push(new Paragraph({ text: 'Interconnectivity Analysis', heading: HeadingLevel.HEADING_3 }));
            for (const item of meta.interconnectivityAnalysis) {
              if (item.interaction) {
                out.push(new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText(item.interaction, stripCitations),
                    bold: true,
                    size: 22,
                  })],
                  spacing: { after: 40 },
                }));
              }
              if (item.explanation) {
                out.push(new Paragraph({
                  children: [new TextRun({
                    text: this.extractTextFromCitationText(item.explanation, stripCitations),
                    size: 22,
                  })],
                  spacing: { after: 120 },
                }));
              }
            }
          }

          if (meta.strategicActionability?.length) {
            out.push(new Paragraph({ text: 'Strategic Actionability', heading: HeadingLevel.HEADING_3 }));
            for (const a of meta.strategicActionability) {
              out.push(new Paragraph({
                children: [
                  new TextRun({ text: `[${a.priority || ''}] `, bold: true, size: 22 }),
                  new TextRun({
                    text: this.extractTextFromCitationText(a.action, stripCitations),
                    bold: true,
                    size: 22,
                  }),
                ],
                spacing: { after: 40 },
              }));
              if (a.rationale) {
                out.push(new Paragraph({
                  children: [
                    new TextRun({ text: 'Rationale: ', italics: true, size: 22 }),
                    new TextRun({
                      text: this.extractTextFromCitationText(a.rationale, stripCitations),
                      size: 22,
                    }),
                  ],
                  spacing: { after: 120 },
                }));
              }
            }
          }

          if (meta.blindSpotAcknowledgment) {
            out.push(new Paragraph({ text: 'Blind Spot Acknowledgment', heading: HeadingLevel.HEADING_3 }));
            if (meta.blindSpotAcknowledgment.text) {
              out.push(new Paragraph({
                children: [new TextRun({
                  text: this.extractTextFromCitationText(meta.blindSpotAcknowledgment.text, stripCitations),
                  size: 22,
                })],
                spacing: { after: 80 },
              }));
            }
            if (meta.blindSpotAcknowledgment.missingDocuments?.length) {
              out.push(new Paragraph({
                children: [new TextRun({ text: 'Missing Documents:', bold: true, size: 22 })],
                spacing: { after: 40 },
              }));
              meta.blindSpotAcknowledgment.missingDocuments.forEach((doc: string) => {
                out.push(new Paragraph({
                  children: [new TextRun({
                    text: `• ${this.extractTextFromCitationText(doc, stripCitations)}`,
                    size: 22,
                  })],
                  spacing: { after: 40 },
                }));
              });
            }
          }

          if (meta.professionalDisclaimer) {
            out.push(new Paragraph({
              children: [new TextRun({
                text: this.extractTextFromCitationText(meta.professionalDisclaimer, stripCitations),
                italics: true,
                size: 20,
              })],
              spacing: { after: 200 },
            }));
          }
        }
      }

      // Data Quality Assessment
      const dq = d.dataQualityAssessment;
      if (dq) {
        out.push(new Paragraph({ text: 'Data Quality Assessment', heading: HeadingLevel.HEADING_1 }));
        if (dq.completenessScore) {
          out.push(new Paragraph({
            children: [
              new TextRun({ text: 'Completeness Score: ', bold: true, size: 22 }),
              new TextRun({
                text: this.extractTextFromCitationText(dq.completenessScore, stripCitations),
                size: 22,
              }),
            ],
            spacing: { after: 120 },
          }));
        }
        for (const [label, key] of [
          ['Missing Elements', 'missingElements'],
          ['Assumptions Made', 'assumptionsMade'],
          ['Recommended Follow-Up', 'recommendedFollowUp'],
        ] as const) {
          const arr = dq[key];
          if (arr?.length) {
            out.push(new Paragraph({ text: label, heading: HeadingLevel.HEADING_2 }));
            arr.forEach((item: string) => {
              out.push(new Paragraph({
                children: [new TextRun({
                  text: `• ${this.extractTextFromCitationText(item, stripCitations)}`,
                  size: 22,
                })],
                spacing: { after: 40 },
              }));
            });
          }
        }
      }
    }

    // === INSURANCE SUMMARY ===
    const isInsuranceSummary =
      wf === 'insurance_summary' ||
      (d.named_insured && Array.isArray(d.lines_of_coverage));
    if (isInsuranceSummary) {
      out.push(new Paragraph({ text: 'Insurance Summary', heading: HeadingLevel.HEADING_1 }));
      if (d.named_insured) {
        out.push(new Paragraph({
          children: [
            new TextRun({ text: 'Named Insured: ', bold: true, size: 24 }),
            new TextRun({ text: String(d.named_insured), size: 24 }),
          ],
          spacing: { after: 80 },
        }));
      }
      if (d.doi_address) {
        out.push(new Paragraph({
          children: [
            new TextRun({ text: 'Address: ', bold: true, size: 24 }),
            new TextRun({ text: String(d.doi_address), size: 24 }),
          ],
          spacing: { after: 80 },
        }));
      }
      if (d.summary_period?.start || d.summary_period?.end) {
        out.push(new Paragraph({
          children: [
            new TextRun({ text: 'Summary Period: ', bold: true, size: 24 }),
            new TextRun({
              text: `${d.summary_period.start || ''} – ${d.summary_period.end || ''}`,
              size: 24,
            }),
          ],
          spacing: { after: 80 },
        }));
      }
      if (typeof d.total_premium === 'number') {
        out.push(new Paragraph({
          children: [
            new TextRun({ text: 'Total Premium: ', bold: true, size: 24 }),
            new TextRun({ text: `$${d.total_premium.toLocaleString()}`, size: 24 }),
          ],
          spacing: { after: 200 },
        }));
      }

      if (Array.isArray(d.lines_of_coverage) && d.lines_of_coverage.length) {
        out.push(new Paragraph({ text: 'Lines of Coverage', heading: HeadingLevel.HEADING_2 }));
        for (const line of d.lines_of_coverage) {
          out.push(new Paragraph({
            text: line.line || 'Coverage Line',
            heading: HeadingLevel.HEADING_3,
          }));
          const facts: Array<[string, any]> = [
            ['Carrier', line.carrier],
            ['Policy #', line.policy_number],
            ['Effective', line.effective_date],
            ['Expiration', line.expiration_date],
            ['Premium', typeof line.premium === 'number' ? `$${line.premium.toLocaleString()}` : line.premium],
            ['Commission', typeof line.commission === 'number' ? `$${line.commission.toLocaleString()}` : line.commission],
          ];
          for (const [label, v] of facts) {
            if (v !== undefined && v !== null && v !== '') {
              out.push(new Paragraph({
                children: [
                  new TextRun({ text: `${label}: `, bold: true, size: 22 }),
                  new TextRun({ text: String(v), size: 22 }),
                ],
                spacing: { after: 40 },
              }));
            }
          }
          for (const [label, key] of [
            ['Limits', 'limits'],
            ['Deductibles', 'deductibles'],
          ] as const) {
            const arr = line[key];
            if (Array.isArray(arr) && arr.length) {
              out.push(new Paragraph({
                children: [new TextRun({ text: `${label}:`, bold: true, size: 22 })],
                spacing: { after: 40 },
              }));
              arr.forEach((lv: any) => {
                out.push(new Paragraph({
                  children: [new TextRun({
                    text: `  • ${lv.label || ''}: ${lv.value || ''}`,
                    size: 22,
                  })],
                  spacing: { after: 40 },
                }));
              });
            }
          }
          for (const [label, key] of [
            ['Key Endorsements', 'key_endorsements'],
            ['Notable Exclusions', 'notable_exclusions'],
            ['Source Documents', 'source_documents'],
          ] as const) {
            const arr = line[key];
            if (Array.isArray(arr) && arr.length) {
              out.push(new Paragraph({
                children: [new TextRun({ text: `${label}:`, bold: true, size: 22 })],
                spacing: { after: 40 },
              }));
              arr.forEach((s: string) => {
                out.push(new Paragraph({
                  children: [new TextRun({ text: `  • ${s}`, size: 22 })],
                  spacing: { after: 40 },
                }));
              });
            }
          }
          out.push(new Paragraph({ text: '' }));
        }
      }

      if (d.narrative) {
        out.push(new Paragraph({ text: 'Narrative', heading: HeadingLevel.HEADING_2 }));
        out.push(new Paragraph({
          children: [new TextRun({ text: String(d.narrative), size: 24 })],
          spacing: { after: 200 },
        }));
      }
    }

    // === LOSS RUN ANALYSIS v1 (analyzer v3.1/v4.1 are already wired) ===
    // Analyzer family has documentManifest at root; v1 lacks it.
    const isLossRunV1 =
      (wf === 'loss_run_analysis' && !d.documentManifest) ||
      (!d.documentManifest && !wf && (d.crossFootCheck || (Array.isArray(d.claimLineItems) && d.claimLineItems.length)));
    if (isLossRunV1) {
      if (d.crossFootCheck) {
        const cf = d.crossFootCheck;
        out.push(new Paragraph({ text: 'Cross-Foot Check', heading: HeadingLevel.HEADING_1 }));
        for (const [label, key] of [
          ['Paid Sum', 'paidSum'],
          ['Reserve Sum', 'reserveSum'],
          ['Calculated Total', 'calculatedTotal'],
          ['Incurred Sum', 'incurredSum'],
          ['Variance', 'variance'],
        ] as const) {
          if (cf[key]) {
            out.push(new Paragraph({
              children: [
                new TextRun({ text: `${label}: `, bold: true, size: 22 }),
                new TextRun({ text: String(cf[key]), size: 22 }),
              ],
              spacing: { after: 40 },
            }));
          }
        }
        if (typeof cf.isBalanced === 'boolean') {
          out.push(new Paragraph({
            children: [
              new TextRun({ text: 'Balanced: ', bold: true, size: 22 }),
              new TextRun({
                text: cf.isBalanced ? 'Yes' : 'No',
                size: 22,
                color: cf.isBalanced ? '047857' : 'B91C1C',
              }),
            ],
            spacing: { after: 160 },
          }));
        }
      }

      if (Array.isArray(d.claimLineItems) && d.claimLineItems.length) {
        out.push(new Paragraph({ text: 'Claim Line Items', heading: HeadingLevel.HEADING_1 }));
        out.push(...this.buildWordTableFromArray(d.claimLineItems, [
          'claimNumber', 'lossDate', 'claimant', 'description',
          'paid', 'reserve', 'incurred', 'status', 'category', 'severity',
        ], stripCitations));
      }

      if (Array.isArray(d.categoryAnalysis) && d.categoryAnalysis.length) {
        out.push(new Paragraph({ text: 'Category Analysis', heading: HeadingLevel.HEADING_1 }));
        out.push(...this.buildWordTableFromArray(d.categoryAnalysis, [
          'category', 'claimCount', 'totalIncurred', 'percentageOfTotal',
        ], stripCitations));
      }

      if (typeof d.analystNotes === 'string' && d.analystNotes.trim()) {
        out.push(new Paragraph({ text: 'Analyst Notes', heading: HeadingLevel.HEADING_1 }));
        out.push(new Paragraph({
          children: [new TextRun({ text: d.analystNotes, size: 22, italics: true })],
          spacing: { after: 200 },
        }));
      }
    }

    // === RENEWAL ANALYSIS ===
    const isRenewal =
      wf === 'renewal_analysis' ||
      !!d.renewalRecommendations || !!d.marketAnalysis || !!d.renewalStrategy;
    if (isRenewal) {
      if (d.renewalRecommendations) {
        out.push(new Paragraph({ text: 'Renewal Recommendations', heading: HeadingLevel.HEADING_1 }));
        if (d.renewalRecommendations.summary) {
          out.push(new Paragraph({
            children: [new TextRun({
              text: this.extractTextFromCitationText(d.renewalRecommendations.summary, stripCitations),
              size: 24,
              italics: true,
            })],
            spacing: { after: 160 },
          }));
        }
        if (Array.isArray(d.renewalRecommendations.recommendations)) {
          d.renewalRecommendations.recommendations.forEach((r: any, i: number) => {
            out.push(new Paragraph({
              text: `${i + 1}. ${r.recommendation || r.category || 'Recommendation'}`,
              heading: HeadingLevel.HEADING_2,
            }));
            for (const [label, key] of [
              ['Priority', 'priority'],
              ['Category', 'category'],
              ['Rationale', 'rationale'],
              ['Expected Outcome', 'expectedOutcome'],
              ['Market Context', 'marketContext'],
            ] as const) {
              if (r[key]) {
                out.push(new Paragraph({
                  children: [
                    new TextRun({ text: `${label}: `, bold: true, size: 22 }),
                    new TextRun({
                      text: this.extractTextFromCitationText(r[key], stripCitations),
                      size: 22,
                    }),
                  ],
                  spacing: { after: 60 },
                }));
              }
            }
          });
        }
      }

      if (d.marketAnalysis) {
        out.push(new Paragraph({ text: 'Market Analysis', heading: HeadingLevel.HEADING_1 }));
        const ma = d.marketAnalysis;
        if (ma.marketConditions) {
          out.push(new Paragraph({ text: 'Market Conditions', heading: HeadingLevel.HEADING_2 }));
          if (ma.marketConditions.assessment) {
            out.push(new Paragraph({
              children: [new TextRun({
                text: this.extractTextFromCitationText(ma.marketConditions.assessment, stripCitations),
                size: 22,
              })],
              spacing: { after: 100 },
            }));
          }
          if (ma.marketConditions.trends?.length) {
            out.push(new Paragraph({
              children: [new TextRun({ text: 'Trends:', bold: true, size: 22 })],
              spacing: { after: 40 },
            }));
            ma.marketConditions.trends.forEach((t: string) => {
              out.push(new Paragraph({
                children: [new TextRun({
                  text: `• ${this.extractTextFromCitationText(t, stripCitations)}`,
                  size: 22,
                })],
                spacing: { after: 40 },
              }));
            });
          }
        }
        if (ma.competitivePosition) {
          out.push(new Paragraph({ text: 'Competitive Position', heading: HeadingLevel.HEADING_2 }));
          if (ma.competitivePosition.assessment) {
            out.push(new Paragraph({
              children: [new TextRun({
                text: this.extractTextFromCitationText(ma.competitivePosition.assessment, stripCitations),
                size: 22,
              })],
              spacing: { after: 100 },
            }));
          }
          for (const [label, key] of [
            ['Advantages', 'advantages'],
            ['Concerns', 'concerns'],
          ] as const) {
            const arr = ma.competitivePosition[key];
            if (arr?.length) {
              out.push(new Paragraph({
                children: [new TextRun({ text: `${label}:`, bold: true, size: 22 })],
                spacing: { after: 40 },
              }));
              arr.forEach((s: string) => {
                out.push(new Paragraph({
                  children: [new TextRun({
                    text: `• ${this.extractTextFromCitationText(s, stripCitations)}`,
                    size: 22,
                  })],
                  spacing: { after: 40 },
                }));
              });
            }
          }
        }
      }

      if (d.renewalStrategy) {
        out.push(new Paragraph({ text: 'Renewal Strategy', heading: HeadingLevel.HEADING_1 }));
        const rsy = d.renewalStrategy;
        if (rsy.overallStrategy) {
          out.push(new Paragraph({
            children: [
              new TextRun({ text: 'Overall Strategy: ', bold: true, size: 24 }),
              new TextRun({
                text: this.extractTextFromCitationText(rsy.overallStrategy, stripCitations),
                size: 24,
              }),
            ],
            spacing: { after: 100 },
          }));
        }
        if (rsy.timeline) {
          out.push(new Paragraph({
            children: [
              new TextRun({ text: 'Timeline: ', bold: true, size: 24 }),
              new TextRun({
                text: this.extractTextFromCitationText(rsy.timeline, stripCitations),
                size: 24,
              }),
            ],
            spacing: { after: 100 },
          }));
        }
        for (const [label, key] of [
          ['Key Actions', 'keyActions'],
          ['Risk Considerations', 'riskConsiderations'],
        ] as const) {
          const arr = rsy[key];
          if (arr?.length) {
            out.push(new Paragraph({
              children: [new TextRun({ text: `${label}:`, bold: true, size: 24 })],
              spacing: { after: 40 },
            }));
            arr.forEach((s: string) => {
              out.push(new Paragraph({
                children: [new TextRun({
                  text: `• ${this.extractTextFromCitationText(s, stripCitations)}`,
                  size: 22,
                })],
                spacing: { after: 40 },
              }));
            });
          }
        }
      }
    }

    // === RISK ASSESSMENT ===
    const isRiskAssessment =
      wf === 'risk_assessment' ||
      !!d.riskOverview || !!d.identifiedRisks || !!d.riskMatrices;
    if (isRiskAssessment) {
      if (d.riskOverview) {
        const ro = d.riskOverview;
        out.push(new Paragraph({ text: 'Risk Overview', heading: HeadingLevel.HEADING_1 }));
        for (const [label, key] of [
          ['Overall Risk Level', 'overallRiskLevel'],
          ['Risk Tolerance Level', 'riskToleranceLevel'],
          ['Assessment Scope', 'assessmentScope'],
          ['Methodology', 'methodology'],
        ] as const) {
          if (ro[key]) {
            out.push(new Paragraph({
              children: [
                new TextRun({ text: `${label}: `, bold: true, size: 22 }),
                new TextRun({ text: String(ro[key]), size: 22 }),
              ],
              spacing: { after: 60 },
            }));
          }
        }
        if (ro.primaryRiskCategories?.length) {
          out.push(new Paragraph({
            children: [new TextRun({ text: 'Primary Risk Categories:', bold: true, size: 22 })],
            spacing: { after: 40 },
          }));
          ro.primaryRiskCategories.forEach((c: string) => {
            out.push(new Paragraph({
              children: [new TextRun({ text: `• ${c}`, size: 22 })],
              spacing: { after: 40 },
            }));
          });
        }
      }

      if (Array.isArray(d.riskMatrices) && d.riskMatrices.length) {
        out.push(new Paragraph({ text: 'Risk Matrices', heading: HeadingLevel.HEADING_1 }));
        out.push(...this.buildWordMatrices(d.riskMatrices, stripCitations));
      }

      if (Array.isArray(d.identifiedRisks) && d.identifiedRisks.length) {
        out.push(new Paragraph({ text: 'Identified Risks', heading: HeadingLevel.HEADING_1 }));
        d.identifiedRisks.forEach((r: any, i: number) => {
          out.push(new Paragraph({
            text: `${i + 1}. ${r.riskName || r.riskId || 'Risk'}`,
            heading: HeadingLevel.HEADING_2,
          }));
          for (const [label, key] of [
            ['Category', 'category'],
            ['Description', 'description'],
            ['Probability', 'probability'],
            ['Impact', 'impact'],
            ['Risk Level', 'riskLevel'],
          ] as const) {
            if (r[key]) {
              out.push(new Paragraph({
                children: [
                  new TextRun({ text: `${label}: `, bold: true, size: 22 }),
                  new TextRun({ text: String(r[key]), size: 22 }),
                ],
                spacing: { after: 50 },
              }));
            }
          }
          for (const [label, key] of [
            ['Current Controls', 'currentControls'],
            ['Recommended Actions', 'recommendedActions'],
          ] as const) {
            const arr = r[key];
            if (arr?.length) {
              out.push(new Paragraph({
                children: [new TextRun({ text: `${label}:`, bold: true, size: 22 })],
                spacing: { after: 40 },
              }));
              arr.forEach((c: string) => {
                out.push(new Paragraph({
                  children: [new TextRun({ text: `• ${c}`, size: 22 })],
                  spacing: { after: 40 },
                }));
              });
            }
          }
        });
      }
    }

    // === RESEARCH OVERVIEW ===
    if (
      d.researchOverview &&
      (wf === 'research_analysis' || wf === 'research' || !wf)
    ) {
      const ov = d.researchOverview;
      out.push(new Paragraph({ text: 'Research Overview', heading: HeadingLevel.HEADING_1 }));
      if (ov.researchScope) {
        out.push(new Paragraph({
          children: [
            new TextRun({ text: 'Scope: ', bold: true, size: 22 }),
            new TextRun({ text: String(ov.researchScope), size: 22 }),
          ],
          spacing: { after: 60 },
        }));
      }
      if (ov.methodology) {
        out.push(new Paragraph({
          children: [
            new TextRun({ text: 'Methodology: ', bold: true, size: 22 }),
            new TextRun({ text: String(ov.methodology), size: 22 }),
          ],
          spacing: { after: 60 },
        }));
      }
      for (const [label, key] of [
        ['Key Objectives', 'keyObjectives'],
        ['Data Sources', 'dataSourcesSummary'],
        ['Limitations', 'limitations'],
      ] as const) {
        const arr = ov[key];
        if (arr?.length) {
          out.push(new Paragraph({
            children: [new TextRun({ text: `${label}:`, bold: true, size: 22 })],
            spacing: { after: 40 },
          }));
          arr.forEach((s: string) => {
            out.push(new Paragraph({
              children: [new TextRun({ text: `• ${s}`, size: 22 })],
              spacing: { after: 40 },
            }));
          });
        }
      }
    }

    // === POLICY OVERVIEW (policy_analysis only — coverage_analysis already
    // emits this when coverageMetrics is present). ===
    if (d.policyOverview && !d.coverageMetrics && (wf === 'policy_analysis' || !wf)) {
      const po = d.policyOverview;
      out.push(new Paragraph({ text: 'Policy Overview', heading: HeadingLevel.HEADING_1 }));
      for (const [label, key] of [
        ['Policy Type', 'policyType'],
        ['Carrier', 'carrier'],
        ['Insured', 'insured'],
        ['Effective Date', 'effectiveDate'],
        ['Expiration Date', 'expirationDate'],
        ['Premium', 'premium'],
        ['Policy Form', 'policyForm'],
      ] as const) {
        if (po[key]) {
          out.push(new Paragraph({
            children: [
              new TextRun({ text: `${label}: `, bold: true, size: 22 }),
              new TextRun({
                text: this.extractTextFromCitationText(po[key], stripCitations),
                size: 22,
              }),
            ],
            spacing: { after: 50 },
          }));
        }
      }
    }

    // === STANDALONE metaAnalysis (year_over_year, etc.) ===
    // The coverage_analysis branch already emits metaAnalysis when
    // policyOverview && coverageMetrics are present. Risk-Shift's nested
    // metaAnalysis is rendered above. Catch the year_over_year case here.
    if (
      d.metaAnalysis &&
      !d.policyOverview &&
      !d.coverageMetrics &&
      !d.riskShift
    ) {
      const m = d.metaAnalysis;
      out.push(new Paragraph({ text: 'Meta-Analysis', heading: HeadingLevel.HEADING_1 }));
      if (m.general) {
        out.push(new Paragraph({
          children: [new TextRun({
            text: this.extractTextFromCitationText(m.general, stripCitations),
            size: 24,
          })],
          spacing: { after: 160 },
        }));
      }
      if (m.antiPatterns) {
        out.push(new Paragraph({
          text: m.antiPatterns.title || 'Anti-Patterns',
          heading: HeadingLevel.HEADING_2,
        }));
        out.push(new Paragraph({
          children: [new TextRun({
            text: this.extractTextFromCitationText(m.antiPatterns.analysis || '', stripCitations),
            size: 22,
          })],
          spacing: { after: 160 },
        }));
      }
      if (m.blindSpotAcknowledgment) {
        out.push(new Paragraph({
          text: m.blindSpotAcknowledgment.title || 'Blind Spot Acknowledgment',
          heading: HeadingLevel.HEADING_2,
        }));
        out.push(new Paragraph({
          children: [new TextRun({
            text: this.extractTextFromCitationText(m.blindSpotAcknowledgment.acknowledgment || '', stripCitations),
            size: 22,
          })],
          spacing: { after: 160 },
        }));
      }
      if (m.aiConfidenceMandate) {
        out.push(new Paragraph({ text: 'AI Confidence Mandate', heading: HeadingLevel.HEADING_2 }));
        out.push(new Paragraph({
          children: [new TextRun({
            text: this.extractTextFromCitationText(m.aiConfidenceMandate, stripCitations),
            size: 22,
          })],
          spacing: { after: 160 },
        }));
      }
      if (m.professionalDisclaimer) {
        out.push(new Paragraph({
          children: [new TextRun({
            text: this.extractTextFromCitationText(m.professionalDisclaimer, stripCitations),
            italics: true,
            size: 20,
          })],
          spacing: { after: 200 },
        }));
      }
    }

    return out;
  }

  /** Render a {headers: string[], rows: string[][]} table in docx. */
  private static buildSimpleWordTable(
    headers: string[],
    rows: string[][],
    stripCitations: boolean,
  ): any[] {
    const columnCount = headers.length || 1;
    const tableRows = [
      new TableRow({
        children: headers.map((h) =>
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: h || '', bold: true, size: 22 })],
            })],
            shading: { type: ShadingType.SOLID, color: 'E5E7EB' },
          })
        ),
      }),
      ...rows.map((row) =>
        new TableRow({
          children: (Array.isArray(row) ? row : []).map((cell) =>
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({
                  text: this.extractTextFromCitationText(cell, stripCitations) || '',
                  size: 20,
                })],
              })],
            })
          ),
        })
      ),
    ];
    return [
      new Table({
        rows: tableRows,
        width: { size: 10000, type: WidthType.DXA },
        columnWidths: Array(columnCount).fill(Math.floor(10000 / columnCount)),
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: 'E5E7EB' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: 'E5E7EB' },
          left: { style: BorderStyle.SINGLE, size: 1, color: 'E5E7EB' },
          right: { style: BorderStyle.SINGLE, size: 1, color: 'E5E7EB' },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: 'E5E7EB' },
          insideVertical: { style: BorderStyle.SINGLE, size: 1, color: 'E5E7EB' },
        },
      }),
      new Paragraph({ text: '' }),
    ];
  }

  private static buildWordMatrices(matrices: any[], stripCitations: boolean): any[] {
    return matrices.map(matrix => {
      const columnCount = matrix.headers?.length || 4;

      // Normalize each row. Three shapes possible:
      //   1. plain array        [cell, cell, ...]
      //   2. plain object       { "Header": cell, ... }
      //   3. cells-array        { cells: [{column, value, citations}, ...] }   (yoy_renewal add-ons + policy_comparison)
      // Convert shape 3 → shape 2 so the existing render paths work.
      const normalizedRows = (matrix.rows || []).map((row: any) => {
        if (row && typeof row === 'object' && !Array.isArray(row) && Array.isArray(row.cells)) {
          const dict: Record<string, any> = {};
          for (const c of row.cells) {
            if (c && typeof c === 'object' && typeof c.column === 'string') {
              dict[c.column] = c.value ?? '';
            }
          }
          return dict;
        }
        return row;
      });

      const tableRows = [
        // Header row
        new TableRow({
          children: matrix.headers.map((header: string) =>
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({
                  text: header,
                  bold: true,
                  size: 22 // 11pt font for headers
                })]
              })],
              shading: {
                type: ShadingType.SOLID,
                color: "E5E7EB"
              }
            })
          )
        }),
        // Data rows — use normalized rows + always index by header so column
        // order matches the header row exactly (Object.values() ordering is
        // insertion-order so unreliable across object-row producers).
        ...normalizedRows.map((row: any) =>
          new TableRow({
            children: Array.isArray(row)
              ? row.map(cell => new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({
                      text: this.extractTextFromCitationText(cell, stripCitations),
                      size: 20
                    })]
                  })]
                }))
              : (row && typeof row === 'object'
                  ? matrix.headers.map((header: string) => {
                      const value = row[header]
                        ?? row[header.toLowerCase()]
                        ?? row[header.toLowerCase().replace(/\s+/g, '').replace(/[^\w]/g, '')]
                        ?? '';
                      return new TableCell({
                        children: [new Paragraph({
                          children: [new TextRun({
                            text: this.extractTextFromCitationText(value, stripCitations),
                            size: 20
                          })]
                        })]
                      });
                    })
                  : matrix.headers.map(() => new TableCell({ children: [new Paragraph({ text: '' })] }))
                )
          })
        )
      ];

      return [
        new Paragraph({
          text: matrix.title || matrix.name,
          heading: HeadingLevel.HEADING_2,
        }),
        ...(matrix.description ? [new Paragraph({
          children: [new TextRun({ text: matrix.description, italics: true })],
          spacing: { after: 100 },
        })] : []),
        new Table({
          rows: tableRows,
          width: {
            size: 10000, // Use full page width in DXA units
            type: WidthType.DXA
          },
          columnWidths: Array(columnCount).fill(Math.floor(10000 / columnCount)),
          borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
            left: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
            right: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
            insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" }
          }
        }),
        new Paragraph({ text: "" }) // Empty line after table
      ];
    }).flat();
  }

  /**
   * Extract plain text from CitationText objects
   */
  private static extractTextFromCitationText(content: any, stripCitations: boolean): string {
    // Handle null/undefined
    if (!content) {
      return '';
    }
    
    // Handle strings
    if (typeof content === 'string') {
      return stripCitations ? content.replace(/\[CITE:\s*"[^"]+"\]/g, '') : content;
    }
    
    // Handle CitationText objects with text property
    if (content && typeof content === 'object' && content.text) {
      return content.text;
    }
    
    // Handle research data objects with value property
    if (content && typeof content === 'object' && content.value) {
      return String(content.value);
    }
    
    // Handle objects with content property
    if (content && typeof content === 'object' && content.content) {
      return this.extractTextFromCitationText(content.content, stripCitations);
    }
    
    // Handle objects with description property
    if (content && typeof content === 'object' && content.description) {
      return this.extractTextFromCitationText(content.description, stripCitations);
    }
    
    // Handle objects with finding property
    if (content && typeof content === 'object' && content.finding) {
      return this.extractTextFromCitationText(content.finding, stripCitations);
    }
    
    // Handle objects with observation property (common in findings)
    if (content && typeof content === 'object' && (content as any).observation) {
      return this.extractTextFromCitationText((content as any).observation, stripCitations);
    }
    
    // Debug unknown object structure
    if (typeof content === 'object') {
      console.warn('🔍 Unknown object structure in extractTextFromCitationText:', {
        type: typeof content,
        keys: Object.keys(content),
        content: content
      });
      
      // Try to find any text-like property
      const textKeys = ['text', 'content', 'description', 'finding', 'observation', 'message', 'value'];
      for (const key of textKeys) {
        if (content[key] && typeof content[key] === 'string') {
          console.log(`📝 Found text in property '${key}': "${content[key]}"`);
          return content[key];
        }
        // Also check nested text properties
        if (content[key] && typeof content[key] === 'object' && content[key].text) {
          console.log(`📝 Found text in nested property '${key}.text': "${content[key].text}"`);
          return content[key].text;
        }
      }
    }
    
    return String(content);
  }

  // Export research report to PDF using backend service for text-based PDFs
  static async exportResearchToPDF(reportData: any, companyName: string, options: ExportOptions = {}): Promise<void> {
    try {
      console.log('🔄 Starting research PDF export via backend service...');
      
      // Get auth token
      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      // Call backend PDF export service
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.avery.cloud'}/api/research/export-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reportData,
          companyName
        })
      });
      
      if (!response.ok) {
        throw new Error(`PDF export failed: ${response.status} ${response.statusText}`);
      }
      
      // Get the PDF blob
      const pdfBlob = await response.blob();
      
      // Generate filename
      const filename = options.filename || `research-analysis-${companyName.replace(/\s+/g, '-')}.pdf`;
      
      // Download the PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('✅ Research PDF export completed successfully');
      
    } catch (error) {
      console.error('❌ Research PDF export failed:', error);
      throw error;
    }
  }

  /**
   * MAIN PDF EXPORT METHOD - Export structured JSON reports to text-based PDF
   * 
   * This is the primary export method used by loss run analysis and other structured reports.
   * It creates a text-based PDF where users can select and copy text.
   * 
   * SECTIONS HANDLED:
   * - Executive Summary
   * - Analysis Matrices (with proper {value, citations} object handling)
   * - Key Findings
   * - Risk Management Decision (Loss Run Analysis)
   * - Trend Analysis (Loss Run Analysis)
   * - Benchmarking Insights (Loss Run Analysis)
   * - Cost-Benefit Analysis (Loss Run Analysis)
   * - Data Quality Assessment (Loss Run Analysis)
   * 
   * DATA PROCESSING:
   * - Handles {value, citations} objects in matrix cells
   * - Extracts text from CitationText objects
   * - Processes nested recommendation structures
   * - Formats tables, headings, and bullet points
   * 
   * @param data The structured JSON report data from UniversalJsonRenderer
   * @param metadata Report metadata (title, date, etc.)
   * @param options Export options (filename, page orientation, etc.)
   */
  static async exportUniversalJsonToTextPDF(data: UniversalJsonResponse, metadata: any, options: ExportOptions = {}): Promise<void> {
    console.log('🔄 Starting text-based PDF export...');
    const anyData = data as any;

    console.log('📊 EXPORT DATA CHECK - All sections:', {
      workflowType: data.responseMetadata?.workflowType,
      allTopLevelKeys: Object.keys(data),
      // Core sections (all workflows)
      hasResponseMetadata: !!data.responseMetadata,
      hasExecutiveSummary: !!data.executiveSummary,
      hasAnalysisMatrices: !!data.analysisMatrices,
      matricesCount: data.analysisMatrices?.length || 0,
      hasKeyFindings: !!data.keyFindings,
      hasRecommendations: !!data.recommendations,
      recommendationsCount: data.recommendations?.length || 0,
      hasDataQualityAssessment: !!data.dataQualityAssessment,
      // Policy comparison specific
      hasNonStandardProvisions: !!anyData.nonStandardProvisions,
      nonStandardCount: anyData.nonStandardProvisions?.provisions?.length || 0,
      hasStrategicConsiderations: !!anyData.strategicConsiderations,
      strategicCount: anyData.strategicConsiderations?.length || 0,
      hasRiskMitigationStrategies: !!anyData.riskMitigationStrategies,
      riskMitigationCount: anyData.riskMitigationStrategies?.length || 0,
      hasRiskShift: !!anyData.riskShift,
      riskShiftKeys: anyData.riskShift ? Object.keys(anyData.riskShift) : [],
      // Loss run specific
      hasRiskManagementMatrix: !!data.riskManagementMatrix,
      hasTrendAnalysis: !!data.trendAnalysis,
      hasBenchmarkingInsights: !!data.benchmarkingInsights,
      hasCostBenefitAnalysis: !!data.costBenefitAnalysis,
    });

    // === POLICY COMPARISON DEBUG ===
    if (anyData.riskShift) {
      console.log('🔄 RISK SHIFT NAVIGATOR DEBUG:', {
        hasTldr: !!anyData.riskShift.tldr,
        tldrType: typeof anyData.riskShift.tldr,
        hasKeyTalkingPoints: !!anyData.riskShift.keyTalkingPoints,
        keyTalkingPointsCount: anyData.riskShift.keyTalkingPoints?.length || 0,
        hasDetailedComparisonTable: !!anyData.riskShift.detailedComparisonTable,
        tableHeaders: anyData.riskShift.detailedComparisonTable?.headers,
        tableRowsCount: anyData.riskShift.detailedComparisonTable?.rows?.length || 0,
        hasMetaAnalysis: !!anyData.riskShift.metaAnalysis,
        metaAnalysisKeys: anyData.riskShift.metaAnalysis ? Object.keys(anyData.riskShift.metaAnalysis) : [],
      });
    }

    // === INSURANCE REQUIREMENTS DEBUG ===
    const irData = data as any;
    console.log('📋 INSURANCE REQUIREMENTS DEBUG:', {
      workflowType: data.responseMetadata?.workflowType,
      isInsuranceRequirements: data.responseMetadata?.workflowType === 'insurance_requirements',
      hasComplianceMatrix: !!irData.complianceMatrix,
      complianceMatrixLength: irData.complianceMatrix?.length || 0,
      hasExecutiveSummary: !!irData.executiveSummary,
      hasOverallStatus: !!irData.executiveSummary?.overallStatus,
      overallStatusType: typeof irData.executiveSummary?.overallStatus,
      overallStatusHasText: !!irData.executiveSummary?.overallStatus?.text,
      hasRecommendation: !!irData.executiveSummary?.recommendation,
      hasInterconnectedAnalysis: !!irData.interconnectedAnalysis,
      hasDecisionPathway: !!irData.decisionPathway,
      hasBlindSpotAcknowledgement: !!irData.blindSpotAcknowledgement,
      hasConfidenceAssessment: !!irData.confidenceAssessment,
      hasProfessionalDisclaimer: !!irData.professionalDisclaimer,
      sampleExecutiveSummary: JSON.stringify(irData.executiveSummary)?.substring(0, 500)
    });

    // Create PDF directly without html2canvas to get selectable text
    const pdf = new jsPDF({
      orientation: options.pageOrientation || 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Page dimensions
    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const margin = 20; // 20mm margin
    const contentWidth = pageWidth - (2 * margin);
    let currentY = margin;
    const lineHeight = 6; // Line height in mm

    // Helper function to check if we need a new page
    const checkNewPage = (): void => {
      if (currentY > pageHeight - margin - 20) {
        pdf.addPage();
        currentY = margin;
      }
    };

    // Helper function to add text with word wrapping
    const addText = (text: string, x: number, fontSize: number = 12): void => {
      pdf.setFontSize(fontSize);
      pdf.setTextColor(0, 0, 0); // Ensure text is black
      const lines = pdf.splitTextToSize(text, contentWidth);
      
      for (let i = 0; i < lines.length; i++) {
        checkNewPage();
        pdf.text(lines[i], x, currentY);
        currentY += lineHeight;
      }
      
      currentY += 3; // Extra spacing after text block
    };

    // Helper function to add a heading
    const addHeading = (text: string, level: number = 1): void => {
      checkNewPage();
      const fontSize = level === 1 ? 18 : level === 2 ? 16 : 14;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(fontSize);
      pdf.setTextColor(0, 0, 0); // Ensure text is black
      pdf.text(text, margin, currentY);
      currentY += fontSize/2 + 8; // More spacing after headings
      pdf.setFont('helvetica', 'normal'); // Reset to normal
    };

    // Helper function to add a table with proper cell sizing and text wrapping
    const addTable = (headers: string[], rows: any[][]): void => {
      // Calculate optimal column widths based on content
      const cellPadding = 4;
      const minColWidth = 25; // Minimum column width in mm
      const maxColWidth = 60; // Maximum column width in mm
      
      // Calculate column widths based on header text length
      const colWidths = headers.map(header => {
        const headerWidth = Math.max(header.length * 2, minColWidth);
        return Math.min(headerWidth, maxColWidth);
      });
      
      // Adjust column widths to fit page width
      const totalWidth = colWidths.reduce((sum, width) => sum + width, 0);
      if (totalWidth > contentWidth) {
        const scaleFactor = contentWidth / totalWidth;
        colWidths.forEach((width, i) => {
          colWidths[i] = width * scaleFactor;
        });
      }
      
      // Calculate row height based on content (minimum 10mm for readability)
      const baseRowHeight = 10;
      
      // Check if table headers fit on current page
      if (currentY + baseRowHeight > pageHeight - margin) {
        pdf.addPage();
        currentY = margin;
      }
      
      // Draw headers
      pdf.setDrawColor(200, 200, 200);
      
      let xPos = margin;
      // Draw header borders only (no background)
      for (let i = 0; i < headers.length; i++) {
        pdf.rect(xPos, currentY, colWidths[i], baseRowHeight, 'S'); // Border only
        xPos += colWidths[i];
      }
      
      // Draw header text
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0); // Black text
      
      xPos = margin;
      for (let i = 0; i < headers.length; i++) {
        // Wrap header text if needed
        const headerLines = pdf.splitTextToSize(headers[i], colWidths[i] - cellPadding);
        const lineHeight = 3;
        const startY = currentY + 4;
        
        headerLines.forEach((line: string, lineIndex: number) => {
          pdf.text(line, xPos + 2, startY + (lineIndex * lineHeight));
        });
        
        xPos += colWidths[i];
      }
      currentY += baseRowHeight;
      
      // Draw rows
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0); // Ensure text is black
      
      for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
        const row = rows[rowIndex];
        
        // Calculate the height needed for this row based on text wrapping
        let maxRowHeight = baseRowHeight;
        const cellContents: string[][] = [];
        
        for (let i = 0; i < row.length; i++) {
          let cellText = '';
          
          if (typeof row[i] === 'string') {
            cellText = row[i];
          } else if (typeof row[i] === 'object' && row[i] !== null) {
            if (row[i].text) cellText = row[i].text;
            else if (row[i].value) cellText = row[i].value;
            else cellText = String(row[i]);
          } else {
            cellText = String(row[i] || '');
          }
          
          // Wrap text to fit column width
          const wrappedLines = pdf.splitTextToSize(cellText, colWidths[i] - cellPadding);
          cellContents[i] = wrappedLines;
          
          // Calculate height needed for this cell
          const cellHeight = Math.max(baseRowHeight, wrappedLines.length * 4 + 2);
          maxRowHeight = Math.max(maxRowHeight, cellHeight);
        }
        
        // Check if row fits on current page
        if (currentY + maxRowHeight > pageHeight - margin) {
          pdf.addPage();
          currentY = margin;
        }
        
        // Draw row cells
        xPos = margin;
        for (let i = 0; i < row.length; i++) {
          // Draw cell border
          pdf.rect(xPos, currentY, colWidths[i], maxRowHeight, 'S');
          
          // Draw cell text
          const lines = cellContents[i];
          const lineHeight = 4;
          const startY = currentY + 4;
          
          lines.forEach((line: string, lineIndex: number) => {
            if (startY + (lineIndex * lineHeight) < currentY + maxRowHeight - 2) {
              pdf.text(line, xPos + 2, startY + (lineIndex * lineHeight));
            }
          });
          
          xPos += colWidths[i];
        }
        
        currentY += maxRowHeight;
      }
      
      currentY += 15; // More spacing after table for better separation
    };

    try {
      // Title
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(20);
      const title = options.customHeader || this.getWorkflowTypeTitle(data.responseMetadata.workflowType);
      pdf.text(title, margin, currentY);
      currentY += 15;

      // Generated date
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, currentY);
      currentY += 10;

      // === TABLE OF CONTENTS (for loss run analysis) ===
      if (data.responseMetadata?.workflowType === 'loss_run_analysis') {
        const anyData = data as any;
        addHeading('Table of Contents', 2);
        pdf.setFontSize(11);
        const tocItems: string[] = [];
        if (data.executiveSummary?.paragraphs?.length) tocItems.push('1. Executive Summary');
        if (anyData.crossFootCheck) tocItems.push('2. Financial Cross-Foot Verification');
        if (anyData.categoryAnalysis?.length) tocItems.push('3. Category Analysis');
        if (anyData.severityAnalysis?.length) tocItems.push('4. Severity Analysis');
        if (anyData.topClaims?.length) tocItems.push('5. Top Claims');
        if (anyData.riskHeatMap) tocItems.push('6. Risk Heat Map');
        if (data.analysisMatrices?.length) tocItems.push('7. Analysis Matrices');
        if (anyData.keyFindings) tocItems.push('8. Key Findings');
        if (anyData.claimLineItems?.length) tocItems.push('9. All Claim Line Items');
        if (anyData.dataQuality) tocItems.push('10. Data Validation');
        if (anyData.analystNotes) tocItems.push('11. Analyst Notes');

        for (const item of tocItems) {
          pdf.text(item, margin, currentY);
          currentY += 5;
        }
        currentY += 10;
      }

      // === EXECUTIVE SUMMARY ===
      // Processes paragraphs with text and citation support
      if (data.executiveSummary?.paragraphs && data.executiveSummary.paragraphs.length > 0) {
        addHeading('Executive Summary', 2);
        
        for (const paragraph of data.executiveSummary.paragraphs) {
          let content = '';
          if (typeof paragraph.content === 'string') {
            content = paragraph.content;
          } else if (paragraph.content && typeof paragraph.content === 'object') {
            content = (paragraph.content as any).text || '';
          }
          
          if (content) {
            addText(content, margin, 12);
          }
        }
      }

      // === ANALYSIS MATRICES ===
      // Processes tables with proper {value, citations} object handling
      // This is critical for loss run analysis matrices that use object cells
      if (data.analysisMatrices && data.analysisMatrices.length > 0) {
        for (const matrix of data.analysisMatrices) {
          addHeading(matrix.title, 2);
          
          if (matrix.headers && matrix.rows && matrix.rows.length > 0) {
            // Convert rows to string arrays for table display
            const tableRows = matrix.rows.map(row => {
              if (Array.isArray(row)) {
                return row.map(cell => {
                  if (typeof cell === 'string') return cell;
                  if (typeof cell === 'object' && cell !== null) {
                    if (cell.text) return cell.text;
                    if (cell.value) return cell.value;
                  }
                  return String(cell || '');
                });
              } else {
                return matrix.headers.map(header => {
                  const value = (row as any)[header];
                  if (typeof value === 'string') return value;
                  if (typeof value === 'object' && value !== null) {
                    if (value.value) return value.value;
                    if (value.text) return value.text;
                  }
                  return String(value || '');
                });
              }
            });
            
            addTable(matrix.headers, tableRows);
          }

          console.log(`✅ Matrix processed: ${matrix.title}`, {
            headersCount: matrix.headers?.length || 0,
            rowsCount: matrix.rows?.length || 0
          });
        }
      }

      // === KEY FINDINGS ===
      // Handles both structured and simple finding objects
      // Supports nested observation/implication/recommendation structure
      if (data.keyFindings) {
        console.log('📝 Processing Key Findings section:', data.keyFindings.findings?.length || 0, 'findings');
        addHeading('Key Findings', 2);
        
        if (data.keyFindings.summary) {
          const summary = typeof data.keyFindings.summary === 'string' ? 
            data.keyFindings.summary : (data.keyFindings.summary as any)?.text || '';
          if (summary) {
            addText(summary, margin, 12);
          }
        }
        
        if (data.keyFindings.findings && data.keyFindings.findings.length > 0) {
          for (const finding of data.keyFindings.findings) {
            addHeading(`${finding.category}`, 3);
            
            if (typeof finding.finding === 'string') {
              addText(finding.finding, margin, 11);
            } else if (finding.finding && typeof finding.finding === 'object') {
              const findingObj = finding.finding as any;
              if (findingObj.observation) {
                const obs = typeof findingObj.observation === 'string' ? 
                  findingObj.observation : findingObj.observation.text;
                if (obs) addText(`Observation: ${obs}`, margin, 11);
              }
              if (findingObj.implication) {
                const imp = typeof findingObj.implication === 'string' ? 
                  findingObj.implication : findingObj.implication.text;
                if (imp) addText(`Implication: ${imp}`, margin, 11);
              }
              if (findingObj.recommendation) {
                const rec = typeof findingObj.recommendation === 'string' ?
                  findingObj.recommendation : findingObj.recommendation.text;
                if (rec) addText(`Recommendation: ${rec}`, margin, 11);
              }
              if (findingObj.businessImpact) {
                const biz = typeof findingObj.businessImpact === 'string' ?
                  findingObj.businessImpact : findingObj.businessImpact.text;
                if (biz) addText(`Business Impact: ${biz}`, margin, 11);
              }
            }

            const details = `Impact: ${finding.impact || 'N/A'} | Confidence: ${finding.confidence || 'N/A'} | Priority: ${finding.priority || 'N/A'}${(finding as any).isNonStandard ? ' | Non-Standard' : ''}`;
            addText(details, margin, 10);
          }
        }
      }

      // === RECOMMENDATIONS ===
      // Processes recommendation arrays with priority and action/rationale pairs
      // Extracts text from both string and CitationText objects
      if (data.recommendations && data.recommendations.length > 0) {
        console.log('📝 Processing Recommendations section:', data.recommendations.length, 'items');
        addHeading('Recommendations', 2);

        for (const rec of data.recommendations) {
          addHeading(`${rec.category} (${rec.priority} Priority)`, 3);

          const action = typeof rec.action === 'string' ? rec.action :
                        (rec.action as any)?.text || 'No action specified';
          const rationale = typeof rec.rationale === 'string' ? rec.rationale :
                          (rec.rationale as any)?.text || 'No rationale provided';

          addText(`Action: ${action}`, margin, 11);
          addText(`Rationale: ${rationale}`, margin, 11);
          if (rec.timeline) addText(`Timeline: ${rec.timeline}`, margin, 11);
          if ((rec as any).estimatedImpact) addText(`Estimated Impact: ${(rec as any).estimatedImpact}`, margin, 11);
        }
      }

      // === RISK MANAGEMENT MATRIX (Loss Run Analysis) ===
      // Dedicated section for loss run analysis recommendations
      // Handles keyObservation, businessImpact, actionableRecommendation structure
      if (data.riskManagementMatrix?.recommendations?.length) {
        addHeading(data.riskManagementMatrix.title || 'Risk Management Decision', 2);
        
        for (const rec of data.riskManagementMatrix.recommendations) {
          addHeading(rec.riskCategory || 'Recommendation', 3);
          
          if (rec.keyObservation?.text) {
            addText(`Key Observation: ${rec.keyObservation.text}`, margin, 11);
          }
          if (rec.businessImpact?.text) {
            addText(`Business Impact: ${rec.businessImpact.text}`, margin, 11);
          }
          if (rec.actionableRecommendation?.text) {
            addText(`Recommendation: ${rec.actionableRecommendation.text}`, margin, 11);
          }
          if (rec.implementationSteps?.length) {
            addText('Implementation Steps:', margin, 11);
            rec.implementationSteps.forEach((step: string, idx: number) => {
              addText(`  ${idx + 1}. ${step}`, margin + 5, 10);
            });
          }
          if (rec.expectedOutcome) {
            addText(`Expected Outcome: ${rec.expectedOutcome}`, margin, 11);
          }
          if (rec.priorityLevel || rec.timeline || rec.estimatedCost) {
            addText(`Priority: ${rec.priorityLevel || 'N/A'} | Timeline: ${rec.timeline || 'N/A'} | Cost: ${rec.estimatedCost || 'N/A'}`, margin, 10);
          }
        }
      }

      // === TREND ANALYSIS (Loss Run Analysis) ===
      // Analyzes frequency, severity, and loss ratio trends over time
      // Includes emerging risks identification and mitigation strategies
      if (data.trendAnalysis) {
        addHeading('Trend Analysis', 2);
        
        if (data.trendAnalysis.overallTrend) {
          addText(`Overall Trend: ${data.trendAnalysis.overallTrend}`, margin, 12);
        }
        
        if (data.trendAnalysis.keyMetrics) {
          const metrics = data.trendAnalysis.keyMetrics;
          if (metrics.frequencyTrend) {
            addHeading('Frequency Trend', 3);
            addText(`Direction: ${metrics.frequencyTrend.direction}`, margin, 11);
            addText(`Change: ${metrics.frequencyTrend.percentageChange || 'N/A'}`, margin, 11);
            if (metrics.frequencyTrend.analysis) {
              addText(metrics.frequencyTrend.analysis, margin, 10);
            }
          }
          if (metrics.severityTrend) {
            addHeading('Severity Trend', 3);
            addText(`Direction: ${metrics.severityTrend.direction}`, margin, 11);
            addText(`Change: ${metrics.severityTrend.percentageChange || 'N/A'}`, margin, 11);
            if (metrics.severityTrend.analysis) {
              addText(metrics.severityTrend.analysis, margin, 10);
            }
          }
          if (metrics.lossRatioTrend) {
            addHeading('Loss Ratio Trend', 3);
            addText(`Current: ${metrics.lossRatioTrend.currentRatio || 'N/A'}`, margin, 11);
            addText(`Historical Average: ${metrics.lossRatioTrend.historicalAverage || 'N/A'}`, margin, 11);
            if (metrics.lossRatioTrend.analysis) {
              addText(metrics.lossRatioTrend.analysis, margin, 10);
            }
          }
        }
        
        if (data.trendAnalysis.emergingRisks?.length) {
          addHeading('Emerging Risks', 3);
          for (const risk of data.trendAnalysis.emergingRisks) {
            addText(`• ${risk.risk}`, margin, 11);
            if (risk.indicators?.length) {
              addText(`  Indicators: ${risk.indicators.join(', ')}`, margin + 5, 10);
            }
            if (risk.potentialImpact) {
              addText(`  Impact: ${risk.potentialImpact}`, margin + 5, 10);
            }
            if (risk.mitigationStrategy) {
              addText(`  Mitigation: ${risk.mitigationStrategy}`, margin + 5, 10);
            }
          }
        }
      }

      // === BENCHMARKING INSIGHTS (Loss Run Analysis) ===
      // Compares performance against industry and internal benchmarks
      // Identifies best/worst performing areas and improvement opportunities
      if (data.benchmarkingInsights) {
        addHeading('Benchmarking Insights', 2);
        
        if (data.benchmarkingInsights.industryComparison) {
          addHeading('Industry Comparison', 3);
          const industry = data.benchmarkingInsights.industryComparison;
          
          if (industry.performanceVsBenchmark) {
            addText(`Performance vs Benchmark: ${industry.performanceVsBenchmark}`, margin, 11);
          }
          if (industry.keyDifferentiators?.length) {
            addText('Key Differentiators:', margin, 11);
            industry.keyDifferentiators.forEach((diff: string) => {
              addText(`• ${diff}`, margin + 5, 10);
            });
          }
          if (industry.improvementOpportunities?.length) {
            addText('Improvement Opportunities:', margin, 11);
            industry.improvementOpportunities.forEach((opp: string) => {
              addText(`• ${opp}`, margin + 5, 10);
            });
          }
        }
        
        if (data.benchmarkingInsights.internalComparison) {
          addHeading('Internal Comparison', 3);
          const internal = data.benchmarkingInsights.internalComparison;
          
          if (internal.bestPerformingAreas?.length) {
            addText('Best Performing Areas:', margin, 11);
            internal.bestPerformingAreas.forEach((area: string) => {
              addText(`• ${area}`, margin + 5, 10);
            });
          }
          if (internal.worstPerformingAreas?.length) {
            addText('Worst Performing Areas:', margin, 11);
            internal.worstPerformingAreas.forEach((area: string) => {
              addText(`• ${area}`, margin + 5, 10);
            });
          }
          if (internal.lessonsLearned?.length) {
            addText('Lessons Learned:', margin, 11);
            internal.lessonsLearned.forEach((lesson: string) => {
              addText(`• ${lesson}`, margin + 5, 10);
            });
          }
        }
      }

      // === COST-BENEFIT ANALYSIS (Loss Run Analysis) ===
      // Financial analysis of recommendations with ROI calculations
      // Includes implementation costs, savings, payback periods, and complexity assessment
      if (data.costBenefitAnalysis?.length) {
        addHeading('Cost-Benefit Analysis', 2);
        
        for (const analysis of data.costBenefitAnalysis) {
          addHeading(analysis.recommendation || 'Analysis', 3);
          
          if (analysis.implementationCost) {
            addText(`Implementation Cost: ${analysis.implementationCost}`, margin, 11);
          }
          if (analysis.potentialSavings) {
            addText(`Potential Savings: ${analysis.potentialSavings}`, margin, 11);
          }
          if (analysis.paybackPeriod) {
            addText(`Payback Period: ${analysis.paybackPeriod}`, margin, 11);
          }
          if (analysis.implementationComplexity) {
            addText(`Implementation Complexity: ${analysis.implementationComplexity}`, margin, 11);
          }
          if (analysis.nonFinancialBenefits?.length) {
            addText('Non-Financial Benefits:', margin, 11);
            analysis.nonFinancialBenefits.forEach((benefit: string) => {
              addText(`• ${benefit}`, margin + 5, 10);
            });
          }
        }
      }

      // === DATA QUALITY ASSESSMENT (Loss Run Analysis) ===
      // Evaluates completeness and reliability of source data
      // Flexible format: supports both loss run and policy comparison structures
      // Handles dataGaps/missingElements and recommendedDataCollection/recommendedFollowUp
      if (data.dataQualityAssessment) {
        console.log('📝 Processing Data Quality Assessment section');
        addHeading('Data Quality Assessment', 2);
        const dqa = data.dataQualityAssessment as any;
        
        if (dqa.completenessScore) {
          addText(`Completeness Score: ${dqa.completenessScore}/100`, margin, 11);
        }
        if (dqa.confidenceInAnalysis) {
          addText(`Confidence in Analysis: ${dqa.confidenceInAnalysis}`, margin, 11);
        }
        
        const dataGaps = dqa.dataGaps || dqa.missingElements || [];
        if (dataGaps.length > 0) {
          addText(dqa.dataGaps ? 'Data Gaps:' : 'Missing Elements:', margin, 11);
          dataGaps.forEach((gap: string) => {
            addText(`• ${gap}`, margin + 5, 10);
          });
        }
        
        if (dqa.assumptionsMade?.length) {
          addText('Assumptions Made:', margin, 11);
          dqa.assumptionsMade.forEach((assumption: string) => {
            addText(`• ${assumption}`, margin + 5, 10);
          });
        }
        
        const recommendations = dqa.recommendedDataCollection || dqa.recommendedFollowUp || [];
        if (recommendations.length > 0) {
          addText(dqa.recommendedDataCollection ? 'Recommended Data Collection:' : 'Recommended Follow-up:', margin, 11);
          recommendations.forEach((rec: string) => {
            addText(`• ${rec}`, margin + 5, 10);
          });
        }
      }

      // === LOSS RUN SPECIFIC SECTIONS ===
      const anyData = data as any;

      // === CROSS-FOOT CHECK (Loss Run Schema) ===
      if (anyData.crossFootCheck) {
        addHeading('Financial Cross-Foot Verification', 2);
        const cfc = anyData.crossFootCheck;

        const crossFootRows = [
          ['Paid Sum', cfc.paidSum || '$0.00'],
          ['Reserve Sum', cfc.reserveSum || '$0.00'],
          ['Calculated Total', cfc.calculatedTotal || '$0.00'],
          ['Incurred Sum', cfc.incurredSum || '$0.00'],
          ['Variance', cfc.variance || '$0.00'],
          ['Status', cfc.isBalanced ? 'Balanced ✓' : 'Discrepancy ✗']
        ];
        addTable(['Metric', 'Value'], crossFootRows);
      }

      // === CATEGORY ANALYSIS (Loss Run Schema) ===
      if (anyData.categoryAnalysis?.length) {
        addHeading('Category Analysis', 2);
        const catRows = anyData.categoryAnalysis.map((cat: any) => [
          cat.category || '-',
          cat.claimCount || '0',
          cat.totalIncurred || '$0.00',
          cat.percentageOfTotal ? `${cat.percentageOfTotal}%` : '-'
        ]);
        addTable(['Category', 'Claims', 'Total Incurred', '% of Total'], catRows);
      }

      // === SEVERITY ANALYSIS (Loss Run Schema) ===
      if (anyData.severityAnalysis?.length) {
        addHeading('Severity Analysis', 2);
        const sevRows = anyData.severityAnalysis.map((sev: any) => [
          sev.severity || '-',
          sev.claimCount || '0',
          sev.totalIncurred || '$0.00',
          sev.percentageOfTotal ? `${sev.percentageOfTotal}%` : '-'
        ]);
        addTable(['Severity', 'Claims', 'Total Incurred', '% of Total'], sevRows);
      }

      // === TOP CLAIMS (Loss Run Schema) ===
      if (anyData.topClaims?.length) {
        addHeading(`Top ${anyData.topClaims.length} Largest Claims`, 2);
        const topRows = anyData.topClaims.map((claim: any) => [
          claim.rank || '-',
          claim.claimNumber || '-',
          claim.lossDate || '-',
          claim.status || '-',
          claim.incurred || '$0.00',
          (claim.description || '-').substring(0, 50) + ((claim.description?.length > 50) ? '...' : '')
        ]);
        addTable(['Rank', 'Claim #', 'Loss Date', 'Status', 'Incurred', 'Description'], topRows);
      }

      // === RISK HEAT MAP (Loss Run Schema) ===
      if (anyData.riskHeatMap) {
        addHeading('Risk Heat Map', 2);
        const rhm = anyData.riskHeatMap;

        if (rhm.highPriorityConcerns?.length) {
          addText('High Priority Concerns:', margin, 11);
          rhm.highPriorityConcerns.forEach((concern: string) => {
            addText(`• ${concern}`, margin + 5, 10);
          });
          currentY += 5;
        }

        addText(`Frequency Trend: ${rhm.frequencyTrend || 'N/A'}`, margin, 11);
        addText(`Severity Trend: ${rhm.severityTrend || 'N/A'}`, margin, 11);
        addText(`Primary Risk Driver: ${rhm.primaryRiskDriver || 'N/A'}`, margin, 11);
      }

      // === CLAIM LINE ITEMS (Loss Run Schema) ===
      if (anyData.claimLineItems?.length) {
        addHeading(`All Claim Line Items (${anyData.claimLineItems.length} items)`, 2);

        // Limit to first 50 claims in PDF to prevent excessive length
        const claimsToShow = anyData.claimLineItems.slice(0, 50);
        const claimRows = claimsToShow.map((claim: any) => [
          claim.claimNumber || '-',
          claim.lossDate || '-',
          claim.status || '-',
          claim.category || '-',
          claim.paid || '$0.00',
          claim.reserve || '$0.00',
          claim.incurred || '$0.00'
        ]);
        addTable(['Claim #', 'Loss Date', 'Status', 'Category', 'Paid', 'Reserve', 'Incurred'], claimRows);

        if (anyData.claimLineItems.length > 50) {
          addText(`Note: Showing first 50 of ${anyData.claimLineItems.length} claims. View full report in app for complete list.`, margin, 10);
        }
      }

      // === DATA QUALITY (Loss Run Schema) ===
      if (anyData.dataQuality) {
        addHeading('Data Validation', 2);
        const dq = anyData.dataQuality;

        const dqRows = [
          ['All Rows Extracted', dq.allRowsExtracted ? 'Yes ✓' : 'No ✗'],
          ['Suffixes Preserved', dq.suffixesPreserved ? 'Yes ✓' : 'No ✗'],
          ['Financial Cross-Foot Valid', dq.financialCrossFootValid ? 'Yes ✓' : 'No ✗']
        ];
        if (dq.dateFormatConsistent !== undefined) {
          dqRows.push(['Date Format Consistent', dq.dateFormatConsistent ? 'Yes ✓' : 'No ✗']);
        }
        if (dq.statusValuesMapped !== undefined) {
          dqRows.push(['Status Values Mapped', dq.statusValuesMapped ? 'Yes ✓' : 'No ✗']);
        }
        addTable(['Validation Check', 'Result'], dqRows);

        if (dq.issues?.length) {
          addText('Data Quality Issues:', margin, 11);
          dq.issues.forEach((issue: string) => {
            addText(`• ${issue}`, margin + 5, 10);
          });
        }
      }

      // === ANALYST NOTES (Loss Run Schema) ===
      if (anyData.analystNotes && typeof anyData.analystNotes === 'string' && anyData.analystNotes.trim()) {
        addHeading('Analyst Notes', 2);
        addText(anyData.analystNotes, margin, 11);
      }

      // === INSURANCE REQUIREMENTS v2.0 ===
      if (data.responseMetadata?.workflowType === 'insurance_requirements' || anyData.complianceMatrix || anyData.executiveSummary?.overallStatus) {
        console.log('📄 Processing Insurance Requirements sections for PDF...', {
          condition1_workflowType: data.responseMetadata?.workflowType === 'insurance_requirements',
          condition2_complianceMatrix: !!anyData.complianceMatrix,
          condition3_overallStatus: !!anyData.executiveSummary?.overallStatus
        });

        // Executive Summary with Overall Status - handle multiple formats
        if (anyData.executiveSummary) {
          addHeading('Executive Summary', 1);
          const es = anyData.executiveSummary;

          // Handle overallStatus in multiple formats with logging
          let overallText = '';
          if (es.overallStatus) {
            if (typeof es.overallStatus === 'string') {
              overallText = es.overallStatus;
            } else if (es.overallStatus.text) {
              overallText = es.overallStatus.text;
            } else if (typeof es.overallStatus === 'object') {
              // Fallback: stringify if it's an unexpected object format
              console.warn('⚠️ Unexpected overallStatus format:', es.overallStatus);
              overallText = JSON.stringify(es.overallStatus);
            }
          } else if (es.summary) {
            // Legacy v1 format fallback
            overallText = typeof es.summary === 'string' ? es.summary : es.summary?.text || '';
          }

          if (overallText) {
            addText(overallText, margin, 11);
          } else {
            console.warn('⚠️ No overallStatus or summary text found in executiveSummary. Keys available:', Object.keys(es));
          }

          if (es.recommendation) {
            addText(`Recommendation: ${es.recommendation}`, margin, 11);
          }

          if (es.recommendationRationale) {
            const rationaleText = typeof es.recommendationRationale === 'string'
              ? es.recommendationRationale
              : es.recommendationRationale?.text || '';
            if (rationaleText) addText(`Rationale: ${rationaleText}`, margin, 11);
          }

          if (es.criticalDeficiencies?.length) {
            addHeading('Critical Deficiencies', 2);
            es.criticalDeficiencies.forEach((def: any, idx: number) => {
              const defText = typeof def === 'string' ? def : def?.text || '';
              if (defText) addText(`${idx + 1}. ${defText}`, margin, 11);
            });
          }
        } else {
          console.warn('⚠️ No executiveSummary found in insurance_requirements data');
        }

        // Compliance Matrix
        if (anyData.complianceMatrix?.length) {
          console.log('📊 Processing Compliance Matrix:', anyData.complianceMatrix.length, 'items');
          addHeading('Compliance Matrix', 1);
          for (const item of anyData.complianceMatrix) {
            addHeading(`Requirement ${item.requirementNumber || ''}: ${item.complianceStatus || ''}`, 2);
            if (item.requirement) addText(`Requirement: ${item.requirement}`, margin, 11);
            if (item.policyProvision) addText(`Policy Provision: ${item.policyProvision}`, margin, 11);
            if (item.rationale) {
              const rationaleText = typeof item.rationale === 'string' ? item.rationale : item.rationale?.text || '';
              if (rationaleText) addText(`Rationale: ${rationaleText}`, margin, 11);
            }
            if (item.actionRequired) addText(`Action Required: ${item.actionRequired}`, margin, 11);
          }
        }

        // Interconnected Analysis
        if (anyData.interconnectedAnalysis) {
          console.log('🔗 Processing Interconnected Analysis');
          addHeading('Interconnected Risk Analysis', 1);
          const ia = anyData.interconnectedAnalysis;

          if (ia.operatingModelIdentified) {
            addHeading('Operating Model', 2);
            const omText = typeof ia.operatingModelIdentified === 'string'
              ? ia.operatingModelIdentified : ia.operatingModelIdentified?.text || '';
            if (omText) addText(omText, margin, 11);
          }

          if (ia.deficiencySynthesis?.length) {
            addHeading('Deficiency Synthesis', 2);
            ia.deficiencySynthesis.forEach((def: any, idx: number) => {
              const defText = typeof def === 'string' ? def : def?.text || '';
              if (defText) addText(`${idx + 1}. ${defText}`, margin, 11);
            });
          }

          if (ia.riskEscalations?.length) {
            addHeading('Risk Escalations', 2);
            for (const esc of ia.riskEscalations) {
              addText(`Deficiency: ${esc.deficiency || ''}`, margin, 11);
              addText(`Original Risk: ${esc.originalRisk || ''} → Compound Risk: ${esc.compoundRisk || ''}`, margin, 11);
              if (esc.reasoning) addText(`Reasoning: ${esc.reasoning}`, margin, 10);
            }
          }

          if (ia.coveragePhilosophyAssessment) {
            addHeading('Coverage Philosophy Assessment', 2);
            const cpText = typeof ia.coveragePhilosophyAssessment === 'string'
              ? ia.coveragePhilosophyAssessment : ia.coveragePhilosophyAssessment?.text || '';
            if (cpText) addText(cpText, margin, 11);
          }

          if (ia.synthesisConclusion) {
            addHeading('Synthesis Conclusion', 2);
            const scText = typeof ia.synthesisConclusion === 'string'
              ? ia.synthesisConclusion : ia.synthesisConclusion?.text || '';
            if (scText) addText(scText, margin, 11);
          }
        }

        // Decision Pathway
        if (anyData.decisionPathway) {
          console.log('🛤️ Processing Decision Pathway');
          addHeading('Decision Pathway', 1);
          const dp = anyData.decisionPathway;
          addText(`Pathway: ${dp.pathway || ''} - ${dp.pathwayName || ''}`, margin, 11);
          if (dp.rationale) {
            const dpRationale = typeof dp.rationale === 'string' ? dp.rationale : dp.rationale?.text || '';
            if (dpRationale) addText(`Rationale: ${dpRationale}`, margin, 11);
          }
          if (dp.specificActions?.length) {
            addHeading('Specific Actions', 2);
            dp.specificActions.forEach((action: any, idx: number) => {
              const actionText = typeof action === 'string' ? action : action?.text || '';
              if (actionText) addText(`${idx + 1}. ${actionText}`, margin, 11);
            });
          }
        }

        // Blind Spot Acknowledgement
        if (anyData.blindSpotAcknowledgement) {
          console.log('🔍 Processing Blind Spot Acknowledgement');
          addHeading('Limitations & Blind Spots', 1);
          const bsa = anyData.blindSpotAcknowledgement;

          if (bsa.documentCompletenessAssessment) {
            const dcaText = typeof bsa.documentCompletenessAssessment === 'string'
              ? bsa.documentCompletenessAssessment : bsa.documentCompletenessAssessment?.text || '';
            if (dcaText) addText(`Document Completeness: ${dcaText}`, margin, 11);
          }
          if (bsa.limitationsDueToMissingInfo) {
            const lmiText = typeof bsa.limitationsDueToMissingInfo === 'string'
              ? bsa.limitationsDueToMissingInfo : bsa.limitationsDueToMissingInfo?.text || '';
            if (lmiText) addText(`Limitations: ${lmiText}`, margin, 11);
          }
          if (bsa.missingDocuments?.length) {
            addText('Missing Documents:', margin, 11);
            bsa.missingDocuments.forEach((doc: string) => addText(`• ${doc}`, margin + 5, 10));
          }
          if (bsa.caveat) addText(`Caveat: ${bsa.caveat}`, margin, 10);
        }

        // Confidence Assessment
        if (anyData.confidenceAssessment) {
          console.log('📈 Processing Confidence Assessment');
          addHeading('Confidence Assessment', 1);
          addText(`Confidence Level: ${anyData.confidenceAssessment.percentage || ''}`, margin, 11);
          if (anyData.confidenceAssessment.sourceOfConfidence) {
            const socText = typeof anyData.confidenceAssessment.sourceOfConfidence === 'string'
              ? anyData.confidenceAssessment.sourceOfConfidence : anyData.confidenceAssessment.sourceOfConfidence?.text || '';
            if (socText) addText(`Source: ${socText}`, margin, 11);
          }
        }

        // Professional Disclaimer
        if (anyData.professionalDisclaimer) {
          console.log('⚖️ Processing Professional Disclaimer');
          addHeading('Professional Disclaimer', 1);
          const pdText = typeof anyData.professionalDisclaimer === 'string'
            ? anyData.professionalDisclaimer : anyData.professionalDisclaimer?.text || '';
          if (pdText) addText(pdText, margin, 10);
        }

        console.log('✅ Finished processing Insurance Requirements sections');
      }

      // === COMMERCIAL LINES EXECUTIVE RENEWAL ===
      if (data.responseMetadata?.workflowType === 'commercial_lines_exec_renewal' || anyData.executiveRenewalSummary || anyData.riskIntelligenceReport) {
        console.log('📄 Processing Commercial Lines Exec Renewal sections for PDF...');

        // Executive Renewal Summary
        if (anyData.executiveRenewalSummary) {
          const ers = anyData.executiveRenewalSummary;
          addHeading('Executive Renewal Summary', 1);

          // Handle simple string format
          if (typeof ers === 'string') {
            addText(ers, margin, 11);
          } else if (ers.text) {
            // Handle RichText format
            addText(ers.text, margin, 11);
          } else {
            // Handle complex object format
            if (ers.executiveSummaryOfChanges) {
              addHeading('Summary of Changes', 2);
              const escText = typeof ers.executiveSummaryOfChanges === 'string'
                ? ers.executiveSummaryOfChanges : ers.executiveSummaryOfChanges?.text || '';
              if (escText) addText(escText, margin, 11);
            }

            if (ers.keyChanges?.length) {
              addHeading('Key Changes', 2);
              ers.keyChanges.forEach((kc: any, idx: number) => {
                addText(`${idx + 1}. ${kc.change || ''}`, margin, 11);
                if (kc.impact) addText(`   Impact: ${kc.impact}`, margin, 10);
                if (kc.priority) addText(`   Priority: ${kc.priority}`, margin, 10);
              });
            }

            if (ers.programTrajectory) {
              addHeading('Program Trajectory', 2);
              addText(`Assessment: ${ers.programTrajectory.assessment || ''}`, margin, 11);
              if (ers.programTrajectory.rationale) addText(ers.programTrajectory.rationale, margin, 11);
            }

            if (ers.renewalProgramMatrix?.length) {
              addHeading('Renewal Program Matrix', 2);
              for (const rpm of ers.renewalProgramMatrix) {
                addText(`${rpm.coverageLine || ''} - ${rpm.carrier || ''}`, margin, 11);
                if (rpm.expiringLimit || rpm.renewalLimit) {
                  addText(`  Limits: ${rpm.expiringLimit || 'N/A'} → ${rpm.renewalLimit || 'N/A'}`, margin, 10);
                }
                if (rpm.expiringPremium || rpm.renewalPremium) {
                  addText(`  Premium: ${rpm.expiringPremium || 'N/A'} → ${rpm.renewalPremium || 'N/A'} (${rpm.premiumChangePercent || ''})`, margin, 10);
                }
                if (rpm.keyChanges) addText(`  Key Changes: ${rpm.keyChanges}`, margin, 10);
              }
            }

            if (ers.totalProgramSummary) {
              addHeading('Total Program Summary', 2);
              const tps = ers.totalProgramSummary;
              addText(`Total Premium: ${tps.expiringTotalPremium || 'N/A'} → ${tps.renewalTotalPremium || 'N/A'} (${tps.totalChangePercent || ''})`, margin, 11);
            }
          }
        }

        // Risk Intelligence Report
        if (anyData.riskIntelligenceReport) {
          const rir = anyData.riskIntelligenceReport;
          addHeading('Risk Intelligence Report', 1);

          if (rir.criticalPathPriorities?.length) {
            addHeading('Critical Path Priorities', 2);
            for (const cpp of rir.criticalPathPriorities) {
              addText(`#${cpp.priorityRank || ''} ${cpp.domain || ''} (${cpp.priorityLevel || cpp.priorityScore || ''})`, margin, 11);
              if (cpp.whyCritical) addText(`  Why Critical: ${cpp.whyCritical}`, margin, 10);
              if (cpp.immediateAction) addText(`  Immediate Action: ${cpp.immediateAction}`, margin, 10);
            }
          }

          if (rir.comprehensiveRiskAssessment?.length) {
            addHeading('Comprehensive Risk Assessment', 2);
            for (const cra of rir.comprehensiveRiskAssessment) {
              addText(`${cra.domain || ''} - ${cra.inherentRiskLevel || ''}`, margin, 11);
              if (cra.rationale) addText(`  Rationale: ${cra.rationale}`, margin, 10);
              if (cra.connectedRisks) addText(`  Connected Risks: ${cra.connectedRisks}`, margin, 10);
            }
          }

          if (rir.coverageApplicabilityAnalysis) {
            addHeading('Coverage Applicability Analysis', 2);
            const caa = rir.coverageApplicabilityAnalysis;

            if (caa.coverageGapsIdentified?.length) {
              addText('Coverage Gaps:', margin, 11);
              for (const gap of caa.coverageGapsIdentified) {
                addText(`• ${gap.riskDomain || ''}: ${gap.gapType || ''} (${gap.priorityScore || ''})`, margin + 5, 10);
              }
            }
          }

          if (rir.riskMitigationFramework?.length) {
            addHeading('Risk Mitigation Framework', 2);
            for (const rmf of rir.riskMitigationFramework) {
              addText(`${rmf.topic || ''} (${rmf.priority || ''})`, margin, 11);
              if (rmf.talkingPoints) addText(`  Talking Points: ${rmf.talkingPoints}`, margin, 10);
              if (rmf.recommendedActions) addText(`  Actions: ${rmf.recommendedActions}`, margin, 10);
            }
          }

          // Research Methodology
          if (rir.researchMethodology) {
            const rm = rir.researchMethodology;
            addHeading('Research Methodology & Limitations', 2);

            if (rm.overallConfidence) addText(`Overall Confidence: ${rm.overallConfidence}`, margin, 11);

            if (rm.insuranceDocumentQuality && typeof rm.insuranceDocumentQuality === 'object') {
              addText('Insurance Document Quality:', margin, 11);
              if (rm.insuranceDocumentQuality.expiringPolicyCompleteness)
                addText(`  Expiring Policy: ${rm.insuranceDocumentQuality.expiringPolicyCompleteness}`, margin, 10);
              if (rm.insuranceDocumentQuality.renewalDocumentCompleteness)
                addText(`  Renewal Document: ${rm.insuranceDocumentQuality.renewalDocumentCompleteness}`, margin, 10);
              if (rm.insuranceDocumentQuality.overallDocumentConfidence)
                addText(`  Document Confidence: ${rm.insuranceDocumentQuality.overallDocumentConfidence}`, margin, 10);
            }

            if (rm.limitations?.length) {
              addText('Analysis Limitations:', margin, 11);
              rm.limitations.forEach((lim: string) => addText(`• ${lim}`, margin + 5, 10));
            }

            if (rm.recommendationsForImprovement?.length) {
              addText('Recommendations for Improvement:', margin, 11);
              rm.recommendationsForImprovement.forEach((rec: string) => addText(`• ${rec}`, margin + 5, 10));
            }
          }
        }

        // === HOMEOWNERS COMPARISON & PERSONAL UMBRELLA ===
        if (data.responseMetadata?.workflowType === 'homeowners_comparison' || data.responseMetadata?.workflowType === 'personal_umbrella' || (anyData.baselineExtraction && anyData.gapSynthesis)) {
          console.log('📄 Processing Homeowners/Umbrella sections for PDF...');

          // Executive Summary (tldr, keyFindings, criticalActions, overallAssessment)
          if (anyData.executiveSummary?.tldr) {
            addHeading('Executive Summary', 1);
            addText(this.extractTextFromCitationText(anyData.executiveSummary.tldr, true), margin, 11);

            if (anyData.executiveSummary.keyFindings?.length) {
              addHeading('Key Findings', 2);
              anyData.executiveSummary.keyFindings.forEach((f: string, i: number) => {
                addText(`${i + 1}. ${this.extractTextFromCitationText(f, true)}`, margin, 11);
              });
            }

            if (anyData.executiveSummary.criticalActions?.length) {
              addHeading('Critical Actions', 2);
              anyData.executiveSummary.criticalActions.forEach((a: string, i: number) => {
                addText(`${i + 1}. ${this.extractTextFromCitationText(a, true)}`, margin, 11);
              });
            }

            if (anyData.executiveSummary.overallAssessment) {
              addHeading('Overall Assessment', 2);
              addText(this.extractTextFromCitationText(anyData.executiveSummary.overallAssessment, true), margin, 11);
            }
          }

          // Analysis Header
          if (anyData.analysisHeader) {
            addHeading('Analysis Overview', 1);
            const ah = anyData.analysisHeader;
            if (ah.namedInsured) addText(`Named Insured: ${ah.namedInsured}`, margin, 11);
            if (ah.propertyAddress) addText(`Property Address: ${ah.propertyAddress}`, margin, 11);
            if (ah.analysisDate) addText(`Analysis Date: ${ah.analysisDate}`, margin, 11);
            if (ah.track) addText(`Analysis Track: ${ah.track}`, margin, 11);
            if (ah.mode) addText(`Mode: ${ah.mode}`, margin, 11);
            if (ah.umbrellaType) addText(`Umbrella Type: ${ah.umbrellaType}`, margin, 11);
            if (ah.complexityTier) addText(`Complexity Tier: ${ah.complexityTier}`, margin, 11);
            if (ah.geographicRiskProfile) addText(`Geographic Risk Profile: ${ah.geographicRiskProfile}`, margin, 11);
            if (ah.documentsAnalyzed) addText(`Documents: ${ah.documentsAnalyzed.join(', ')}`, margin, 10);
          }

          // Document Classification
          if (anyData.documentClassification?.length) {
            addHeading('Document Classification', 1);
            const dcRows = anyData.documentClassification.map((d: any) => [
              d.fileName || '-', d.documentType || '-', d.carrierName || '-',
              d.keyDates || d.effectiveDate || '-', d.namedInsured || '-'
            ]);
            addTable(['File Name', 'Type', 'Carrier', 'Dates', 'Named Insured'], dcRows);
          }

          // Mode Rationale
          if (anyData.modeRationale) {
            addHeading('Mode Rationale', 1);
            addText(this.extractTextFromCitationText(anyData.modeRationale, true), margin, 11);
          }

          // Temporal Verification
          if (anyData.temporalVerification) {
            addHeading('Temporal Verification', 1);
            addText(`Status: ${anyData.temporalVerification.status || ''}`, margin, 11);
            if (anyData.temporalVerification.dateSequence) addText(`Date Sequence: ${anyData.temporalVerification.dateSequence}`, margin, 11);
            if (anyData.temporalVerification.temporalClassification) addText(`Classification: ${anyData.temporalVerification.temporalClassification}`, margin, 11);
            if (anyData.temporalVerification.conflicts?.length) addText(`Conflicts: ${anyData.temporalVerification.conflicts.join(', ')}`, margin, 11);
          }

          // Policy Architecture (personal umbrella only)
          if (anyData.policyArchitecture) {
            addHeading('Policy Architecture', 1);
            const pa = anyData.policyArchitecture;
            if (pa.policyType) addText(`Policy Type: ${pa.policyType}`, margin, 11);
            if (pa.defenseCostTreatment) addText(`Defense Cost Treatment: ${pa.defenseCostTreatment}`, margin, 11);
            if (pa.sirAmount) addText(`Self-Insured Retention: ${pa.sirAmount}`, margin, 11);
            if (pa.coverageTerritory) addText(`Coverage Territory: ${pa.coverageTerritory}`, margin, 11);
            if (pa.dropDownProvisions) addText(`Drop-Down Provisions: ${pa.dropDownProvisions}`, margin, 11);
            if (pa.insuredDefinition) addText(`Insured Definition: ${pa.insuredDefinition}`, margin, 10);
            if (pa.typeEvidence) addText(`Type Evidence: ${this.extractTextFromCitationText(pa.typeEvidence, true)}`, margin, 10);
            if (pa.muiSchedule) {
              addHeading('MUI Schedule', 2);
              if (pa.muiSchedule.homeowners) addText(`Homeowners: ${pa.muiSchedule.homeowners}`, margin, 11);
              if (pa.muiSchedule.auto) addText(`Auto: ${pa.muiSchedule.auto}`, margin, 11);
              if (pa.muiSchedule.watercraft) addText(`Watercraft: ${pa.muiSchedule.watercraft}`, margin, 11);
              if (pa.muiSchedule.other) addText(`Other: ${pa.muiSchedule.other}`, margin, 11);
            }
            if (pa.scheduleOfForms) addText(`Schedule of Forms: ${pa.scheduleOfForms}`, margin, 10);
            if (pa.totalEndorsements != null) addText(`Total Endorsements: ${pa.totalEndorsements}`, margin, 10);
          }

          // Schedule of Forms (homeowners comparison only)
          if (anyData.scheduleOfForms?.length) {
            addHeading('Schedule of Forms', 1);
            for (const doc of anyData.scheduleOfForms) {
              addHeading(`${doc.carrierName || 'Unknown Carrier'}`, 2);
              if (doc.baseForm) addText(`Base Form: ${doc.baseForm}`, margin, 11);
              if (doc.totalEndorsements != null) addText(`Total Endorsements: ${doc.totalEndorsements}`, margin, 11);
              if (doc.endorsements?.length) {
                addText('Endorsements:', margin, 11);
                doc.endorsements.forEach((e: any) => {
                  addText(`  • ${e.formNumber || ''}${e.description ? ' - ' + e.description : ''}${e.classification ? ' (' + e.classification + ')' : ''}`, margin, 10);
                });
              }
              if (doc.coverageExpanding?.length) addText(`Coverage Expanding: ${doc.coverageExpanding.join(', ')}`, margin, 10);
              if (doc.coverageRestricting?.length) addText(`Coverage Restricting: ${doc.coverageRestricting.join(', ')}`, margin, 10);
              if (doc.expectedButMissing?.length) addText(`Expected But Missing: ${doc.expectedButMissing.join(', ')}`, margin, 10);
              if (doc.informationGaps?.length) addText(`Information Gaps: ${doc.informationGaps.join(', ')}`, margin, 10);
            }
          }

          // Baseline Extraction
          if (anyData.baselineExtraction?.length) {
            addHeading('Baseline Extraction', 1);
            for (const doc of anyData.baselineExtraction) {
              addHeading(`${doc.carrierName || 'Unknown'}${doc.policyType ? ' - ' + doc.policyType : ''}`, 2);
              const fields: [string, string][] = [
                ['perOccurrenceLimit', 'Per Occurrence Limit'], ['aggregateLimit', 'Aggregate Limit'],
                ['sir', 'Self-Insured Retention'], ['annualPremium', 'Annual Premium'],
                ['coverageA', 'Coverage A (Dwelling)'], ['coverageB', 'Coverage B (Other Structures)'],
                ['coverageC', 'Coverage C (Personal Property)'], ['coverageD', 'Coverage D (Loss of Use)'],
                ['coverageE', 'Coverage E (Liability)'], ['coverageF', 'Coverage F (Medical)'],
                ['deductibleAOP', 'Deductible (AOP)'], ['deductibleWind', 'Deductible (Wind)'],
                ['deductibleNamedStorm', 'Deductible (Named Storm)'], ['deductibleFlood', 'Deductible (Flood)'],
                ['deductibleWaterBackup', 'Deductible (Water Backup)'], ['otherDeductibles', 'Other Deductibles'],
                ['premiumBreakdown', 'Premium Breakdown'], ['specialLimits', 'Special Limits'],
                ['keyEndorsements', 'Key Endorsements'], ['settlementBasis', 'Settlement Basis'],
                ['muiMet', 'MUI Met'], ['muiShortfall', 'MUI Shortfall'],
              ];
              for (const [key, label] of fields) {
                if (doc[key]) addText(`${label}: ${doc[key]}`, margin, 11);
              }
              if (doc.exclusions?.length) addText(`Exclusions: ${doc.exclusions.join(', ')}`, margin, 10);
              if (doc.keyFindings) addText(`Key Findings: ${this.extractTextFromCitationText(doc.keyFindings, true)}`, margin, 10);
            }
          }

          // Exposure Profile
          if (anyData.exposureProfile) {
            addHeading('Exposure Profile', 1);
            const ep = anyData.exposureProfile;
            if (ep.trackStatus) addText(`Track Status: ${ep.trackStatus}`, margin, 11);
            if (ep.familyComposition) addText(`Family Composition: ${ep.familyComposition}`, margin, 11);
            if (ep.jurisdictionalModifiers) addText(`Jurisdictional Modifiers: ${ep.jurisdictionalModifiers}`, margin, 11);
            if (ep.propertyDetails) addText(`Property Details: ${ep.propertyDetails}`, margin, 11);
            if (ep.geographicRisks) addText(`Geographic Risks: ${ep.geographicRisks}`, margin, 11);
            if (ep.complexityTierAssignment) addText(`Complexity Tier: ${ep.complexityTierAssignment}`, margin, 11);
            if (ep.activeCategories?.length) {
              addHeading('Active Exposure Categories', 2);
              const acRows = ep.activeCategories.map((c: any) => [
                c.category || '-', c.activityLevel || '-', c.underlyingPolicy || '-', c.muiStatus || '-'
              ]);
              addTable(['Category', 'Activity Level', 'Underlying Policy', 'MUI Status'], acRows);
            }
            if (ep.probabilisticFlags?.length) {
              addText('Probabilistic Flags:', margin, 11);
              ep.probabilisticFlags.forEach((f: string) => addText(`  • ${f}`, margin, 10));
            }
          }

          // Drop-Down Matrix (personal umbrella only)
          if (anyData.dropDownMatrix) {
            addHeading('Drop-Down Coverage Matrix', 1);
            if (anyData.dropDownMatrix.trackStatus) addText(`Track Status: ${anyData.dropDownMatrix.trackStatus}`, margin, 11);
            if (anyData.dropDownMatrix.entries?.length) {
              const ddRows = anyData.dropDownMatrix.entries.map((e: any) => [
                e.category || '-', e.underlyingStatus || '-', e.umbrellaTreatment || '-',
                e.matrixOutcome || '-', e.gapTier || '-', e.quantifiedExposure || '-'
              ]);
              addTable(['Category', 'Underlying Status', 'Umbrella Treatment', 'Outcome', 'Gap Tier', 'Exposure'], ddRows);
            }
          }

          // Coverage Analysis
          if (anyData.coverageAnalysis) {
            addHeading('Coverage Analysis', 1);
            const ca = anyData.coverageAnalysis;
            if (ca.mode) addText(`Analysis Mode: ${ca.mode}`, margin, 11);
            if (ca.structuralAssessment) addText(`Structural Assessment: ${ca.structuralAssessment}`, margin, 11);
            if (ca.exclusionMapping) addText(`Exclusion Mapping: ${ca.exclusionMapping}`, margin, 11);
            if (ca.muiCompliance) addText(`MUI Compliance: ${ca.muiCompliance}`, margin, 11);

            if (ca.comparison?.length) {
              addHeading('Coverage Comparison', 2);
              for (const item of ca.comparison) {
                addText(`${item.element || ''}${item.equivalent !== undefined ? (item.equivalent ? ' (Equivalent)' : ' (Different)') : ''}${item.superiorOption ? ' - Superior: ' + item.superiorOption : ''}`, margin, 11);
                addText(this.extractTextFromCitationText(item.details, true), margin + 5, 10);
              }
            }

            if (ca.changeDetection?.length) {
              addHeading('Change Detection', 2);
              const cdRows = ca.changeDetection.map((c: any) => [
                c.element || '-', c.changeType || '-', c.priorValue || '-', c.renewalValue || '-', c.analysis || '-'
              ]);
              addTable(['Element', 'Change Type', 'Prior', 'Renewal', 'Analysis'], cdRows);
            }

            if (ca.adequacyAssessment?.length) {
              addHeading('Adequacy Assessment', 2);
              const aaRows = ca.adequacyAssessment.map((a: any) => [
                a.exposure || '-', a.covered || '-', a.adequacy || '-', a.gapTier || '-'
              ]);
              addTable(['Exposure', 'Covered', 'Adequacy', 'Gap Tier'], aaRows);
            }
          }

          // Compound Failures
          if (anyData.compoundFailures) {
            addHeading('Compound Failure Analysis', 1);
            addText(anyData.compoundFailures.detected ? 'Compound failures DETECTED' : 'No compound failures detected', margin, 11);

            if (anyData.compoundFailures.failures?.length) {
              for (const f of anyData.compoundFailures.failures) {
                addText(`Pattern: ${f.pattern || ''} (${f.tier || ''})`, margin, 11);
                addText(`  Combined Effect: ${f.combinedEffect || ''}`, margin, 10);
                if (f.quantifiedImpact) addText(`  Impact: ${f.quantifiedImpact}`, margin, 10);
                if (f.components?.length) addText(`  Components: ${f.components.join(', ')}`, margin, 10);
              }
            }

            if (anyData.compoundFailures.patternResults?.length) {
              for (const p of anyData.compoundFailures.patternResults) {
                addText(`Pattern #${p.patternNumber || ''}: ${p.patternName || ''} - ${p.detected ? 'DETECTED' : 'Not detected'}`, margin, 11);
                if (p.combinedEffect) addText(`  Combined Effect: ${p.combinedEffect}`, margin, 10);
                if (p.tier) addText(`  Tier: ${p.tier}`, margin, 10);
                if (p.quantifiedImpact) addText(`  Impact: ${p.quantifiedImpact}`, margin, 10);
              }
            }
          }

          // Appearance of Coverage
          if (anyData.appearanceOfCoverage) {
            addHeading('Appearance of Coverage Analysis', 1);
            addText(anyData.appearanceOfCoverage.detected ? 'Coverage appearance issues DETECTED' : 'No coverage appearance issues detected', margin, 11);

            if (anyData.appearanceOfCoverage.alerts?.length) {
              for (const a of anyData.appearanceOfCoverage.alerts) {
                addText(`Signal: ${a.signal || ''} (${a.tier || ''})`, margin, 11);
                if (a.perceivedCoverage) addText(`  Perceived: ${a.perceivedCoverage}`, margin, 10);
                if (a.actualCoverage) addText(`  Actual: ${a.actualCoverage}`, margin, 10);
                addText(`  Analysis: ${a.analysis || ''}`, margin, 10);
              }
            }
          }

          // Premium Analysis
          if (anyData.premiumAnalysis && !anyData.premiumAnalysis.totalExpiringPremium) {
            addHeading('Premium Analysis', 1);
            if (anyData.premiumAnalysis.premiums?.length) {
              const pRows = anyData.premiumAnalysis.premiums.map((p: any) => [
                p.carrierName || '-', p.annualPremium || '-',
                p.premiumPerMillion || p.premiumPerThousandCovA || '-',
                p.totalAnnualCost || '-', p.exclusionAdjustedValue || '-'
              ]);
              addTable(['Carrier', 'Annual Premium', 'Rate Metric', 'Total Cost', 'Exclusion-Adjusted'], pRows);
            }
            if (anyData.premiumAnalysis.sirImpactAnalysis) addText(`SIR Impact: ${anyData.premiumAnalysis.sirImpactAnalysis}`, margin, 11);
            if (anyData.premiumAnalysis.premiumChangeAnalysis) addText(`Premium Change: ${anyData.premiumAnalysis.premiumChangeAnalysis}`, margin, 11);
            if (anyData.premiumAnalysis.valueAssessment) addText(`Value Assessment: ${this.extractTextFromCitationText(anyData.premiumAnalysis.valueAssessment, true)}`, margin, 11);
          }

          // Gap Synthesis
          if (anyData.gapSynthesis) {
            addHeading(`Gap Synthesis (${anyData.gapSynthesis.totalGaps || 0} gaps)`, 1);
            if (anyData.gapSynthesis.documentDepthNotice) addText(anyData.gapSynthesis.documentDepthNotice, margin, 10);

            if (anyData.gapSynthesis.gaps?.length) {
              for (const gap of anyData.gapSynthesis.gaps) {
                addText(`${gap.gapId || ''}: ${this.extractTextFromCitationText(gap.description, true)}`, margin, 11);
                const meta = [gap.tier, gap.gapType, gap.quantifiedExposure, gap.isCompound ? 'COMPOUND' : null].filter(Boolean).join(' | ');
                if (meta) addText(`  ${meta}`, margin, 10);
                if (gap.remediationPath || gap.remediation) addText(`  Remediation: ${gap.remediationPath || gap.remediation}`, margin, 10);
              }
            }

            if (anyData.gapSynthesis.separateCoverageRecommendations?.length) {
              addHeading('Separate Coverage Recommendations', 2);
              const scRows = anyData.gapSynthesis.separateCoverageRecommendations.map((r: any) => [
                r.exposure || '-', r.recommendedCoverage || '-', r.urgency || '-', r.estimatedCost || '-'
              ]);
              addTable(['Exposure', 'Recommended Coverage', 'Urgency', 'Est. Cost'], scRows);
            }
          }

          // Decision Framework
          if (anyData.decisionFramework) {
            addHeading('Decision Framework', 1);
            addText(`Pathway: ${anyData.decisionFramework.pathway || ''}`, margin, 11);
            addText(`Rationale: ${this.extractTextFromCitationText(anyData.decisionFramework.rationale, true)}`, margin, 11);

            if (anyData.decisionFramework.perDocumentPathways?.length) {
              addHeading('Per-Document Pathways', 2);
              const pdpRows = anyData.decisionFramework.perDocumentPathways.map((p: any) => [
                p.carrierName || p.docId || '-', p.pathway || '-', p.rationale || '-'
              ]);
              addTable(['Carrier', 'Pathway', 'Rationale'], pdpRows);
            }

            if (anyData.decisionFramework.remediationItems?.length) {
              addHeading('Remediation Items', 2);
              for (const item of anyData.decisionFramework.remediationItems) {
                addText(`${item.gapId || ''}: ${item.action || ''} (${item.priority || ''})`, margin, 11);
                if (item.responsibleParty) addText(`  Responsible: ${item.responsibleParty}`, margin, 10);
                if (item.estimatedCostImpact) addText(`  Est. Cost: ${item.estimatedCostImpact}`, margin, 10);
              }
            }

            addHeading('Recommendation', 2);
            addText(this.extractTextFromCitationText(anyData.decisionFramework.recommendation, true), margin, 11);
          }

          console.log('✅ Finished processing Homeowners/Umbrella sections');
        }

        // === CLIENT RENEWAL PRESENTATION ===
        if (data.responseMetadata?.workflowType === 'client_renewal_presentation' || (anyData.coverageHighlights && anyData.restrictiveEndorsements)) {
          console.log('📄 Processing Client Renewal Presentation sections for PDF...');

          // Company Context
          if (anyData.companyContext) {
            addHeading('Company Context', 1);
            const ctx = anyData.companyContext;
            if (ctx.entityName) addText(`Entity: ${ctx.entityName}`, margin, 11);
            if (ctx.industrySector) addText(`Industry: ${ctx.industrySector}`, margin, 11);
            if (ctx.primaryBusiness) addText(`Primary Business: ${ctx.primaryBusiness}`, margin, 11);
            if (ctx.keyOperations) addText(`Key Operations: ${ctx.keyOperations}`, margin, 10);
            if (ctx.clientBase) addText(`Client Base: ${ctx.clientBase}`, margin, 10);
            if (ctx.geographicFootprint) addText(`Geographic Footprint: ${ctx.geographicFootprint}`, margin, 10);
            if (ctx.scaleIndicators) addText(`Scale: ${ctx.scaleIndicators}`, margin, 10);
            if (ctx.riskManagementSophistication) addText(`Risk Management Sophistication: ${ctx.riskManagementSophistication}`, margin, 10);
          }

          // Document Inventory
          if (anyData.documentInventory?.length) {
            addHeading('Document Inventory', 1);
            const invRows = anyData.documentInventory.map((doc: any) => [
              doc.documentType || '-',
              doc.policyLine || '-',
              doc.carrier || '-',
              doc.policyPeriod || '-',
              doc.completenessLevel || '-'
            ]);
            addTable(['Document Type', 'Policy Line', 'Carrier', 'Period', 'Completeness'], invRows);
          }

          // Program Overview
          if (anyData.programOverview) {
            addHeading('Program Overview', 1);
            const po = anyData.programOverview;
            if (po.totalPolicies) addText(`Total Policies: ${po.totalPolicies}`, margin, 11);
            if (po.totalPremium) addText(`Total Premium: ${po.totalPremium}`, margin, 11);
            if (po.expiringPremium) addText(`Expiring Premium: ${po.expiringPremium}`, margin, 11);
            if (po.renewalPremium) addText(`Renewal Premium: ${po.renewalPremium}`, margin, 11);
            if (po.premiumChange) addText(`Premium Change: ${po.premiumChange} (${po.premiumChangePercent || ''})`, margin, 11);
            if (po.carriersInvolved) addText(`Carriers: ${po.carriersInvolved}`, margin, 11);
            if (po.programComplexity) addText(`Complexity: ${po.programComplexity}`, margin, 11);
            if (po.overallAssessment) {
              addHeading('Overall Assessment', 2);
              addText(po.overallAssessment, margin, 10);
            }
          }

          // Coverage Highlights
          if (anyData.coverageHighlights?.length) {
            addHeading('Coverage Highlights', 1);
            for (const cov of anyData.coverageHighlights) {
              addText(`${cov.coverageName || ''} - ${cov.carrier || ''}`, margin, 11);
              if (cov.limits || cov.deductible) {
                addText(`  Limits: ${cov.limits || 'N/A'} | Deductible: ${cov.deductible || 'N/A'}`, margin, 10);
              }
              if (cov.premium) addText(`  Premium: ${cov.premium}`, margin, 10);
              if (cov.whatItProtects) addText(`  Protection: ${cov.whatItProtects}`, margin, 10);
              if (cov.changeFromExpiring) addText(`  Change: ${cov.changeFromExpiring}`, margin, 10);
              if (cov.changeAssessment) addText(`  Assessment: ${cov.changeAssessment}`, margin, 10);
            }
          }

          // Restrictive Endorsements
          if (anyData.restrictiveEndorsements) {
            const re = anyData.restrictiveEndorsements;
            addHeading('Restrictive Endorsements', 1);

            if (re.subcontractorWarningPresent) {
              addText('⚠️ SUBCONTRACTOR WARNING PRESENT', margin, 11);
              if (re.subcontractorWarningDetails) addText(`  Details: ${re.subcontractorWarningDetails}`, margin, 10);
            }

            if (re.criticalRestrictions?.length) {
              addHeading('Critical Restrictions', 2);
              for (const cr of re.criticalRestrictions) {
                addText(`• ${cr.restrictionName || ''} (${cr.priority || ''})`, margin, 11);
                if (cr.whatIsNotCovered) addText(`  Not Covered: ${cr.whatIsNotCovered}`, margin, 10);
                if (cr.businessImpact) addText(`  Business Impact: ${cr.businessImpact}`, margin, 10);
              }
            }

            if (re.importantRestrictions?.length) {
              addHeading('Important Restrictions', 2);
              for (const ir of re.importantRestrictions) {
                addText(`• ${ir.restrictionName || ''}`, margin, 11);
                if (ir.whatIsNotCovered) addText(`  Not Covered: ${ir.whatIsNotCovered}`, margin, 10);
              }
            }

            if (re.positiveEndorsements?.length) {
              addHeading('Positive Endorsements', 2);
              for (const pe of re.positiveEndorsements) {
                addText(`• ${pe.endorsementName || ''}`, margin, 11);
                if (pe.whatItAdds) addText(`  Adds: ${pe.whatItAdds}`, margin, 10);
                if (pe.businessBenefit) addText(`  Benefit: ${pe.businessBenefit}`, margin, 10);
              }
            }
          }

          // Premium Analysis
          if (anyData.premiumAnalysis) {
            addHeading('Premium Analysis', 1);
            const pa = anyData.premiumAnalysis;
            if (pa.totalExpiringPremium) addText(`Expiring Premium: ${pa.totalExpiringPremium}`, margin, 11);
            if (pa.totalRenewalPremium) addText(`Renewal Premium: ${pa.totalRenewalPremium}`, margin, 11);
            if (pa.totalChange) addText(`Change: ${pa.totalChange} (${pa.totalChangePercent || ''})`, margin, 11);
            if (pa.primaryDrivers) addText(`Primary Drivers: ${pa.primaryDrivers}`, margin, 10);
            if (pa.marketContext) addText(`Market Context: ${pa.marketContext}`, margin, 10);
            if (pa.valueAssessment) addText(`Value Assessment: ${pa.valueAssessment}`, margin, 10);

            if (pa.policyBreakdown?.length) {
              addHeading('Policy Breakdown', 2);
              const pbRows = pa.policyBreakdown.map((pb: any) => [
                pb.coverageName || '-',
                pb.expiringPremium || '-',
                pb.renewalPremium || '-',
                pb.changePercent || '-',
                pb.driver || '-'
              ]);
              addTable(['Coverage', 'Expiring', 'Renewal', 'Change %', 'Driver'], pbRows);
            }
          }

          // Coverage Changes
          if (anyData.coverageChanges?.length) {
            addHeading('Coverage Changes', 1);
            for (const cc of anyData.coverageChanges) {
              addText(`${cc.coverageName || ''} - ${cc.changeType || ''}`, margin, 11);
              if (cc.changeDescription) addText(`  Change: ${cc.changeDescription}`, margin, 10);
              if (cc.expiringValue || cc.renewalValue) {
                addText(`  ${cc.expiringValue || 'N/A'} → ${cc.renewalValue || 'N/A'}`, margin, 10);
              }
              if (cc.clientImpact) addText(`  Client Impact: ${cc.clientImpact}`, margin, 10);
              if (cc.assessment) addText(`  Assessment: ${cc.assessment}`, margin, 10);
            }
          }

          // Red Flags
          if (anyData.redFlags?.length) {
            addHeading('Red Flags', 1);
            for (const rf of anyData.redFlags) {
              addText(`⚠️ ${rf.category || ''}: ${rf.description || ''}`, margin, 11);
              if (rf.clientTranslation) addText(`  Client Impact: ${rf.clientTranslation}`, margin, 10);
              if (rf.severity) addText(`  Severity: ${rf.severity}`, margin, 10);
              if (rf.actionNeeded) addText(`  Action: ${rf.actionNeeded}`, margin, 10);
            }
          }

          // Positive Signals
          if (anyData.positiveSignals?.length) {
            addHeading('Positive Signals', 1);
            for (const ps of anyData.positiveSignals) {
              addText(`✓ ${ps.category || ''}: ${ps.description || ''}`, margin, 11);
              if (ps.clientBenefit) addText(`  Benefit: ${ps.clientBenefit}`, margin, 10);
            }
          }

          // Action Items
          if (anyData.actionItems?.length) {
            addHeading('Action Items', 1);
            for (const ai of anyData.actionItems) {
              addText(`[${ai.priority || ''}] ${ai.action || ''}`, margin, 11);
              if (ai.category) addText(`  Category: ${ai.category}`, margin, 10);
              if (ai.deadline) addText(`  Deadline: ${ai.deadline}`, margin, 10);
              if (ai.responsibleParty) addText(`  Responsible: ${ai.responsibleParty}`, margin, 10);
              if (ai.details) addText(`  Details: ${ai.details}`, margin, 10);
            }
          }

          // Conversation Points
          if (anyData.conversationPoints?.length) {
            addHeading('Conversation Points', 1);
            for (const cp of anyData.conversationPoints) {
              addText(`Topic: ${cp.topic || ''}`, margin, 11);
              if (cp.context) addText(`  Context: ${cp.context}`, margin, 10);
              if (cp.considerations) addText(`  Considerations: ${cp.considerations}`, margin, 10);
              if (cp.questionsToAsk) addText(`  Questions: ${cp.questionsToAsk}`, margin, 10);
            }
          }

          // Glossary
          if (anyData.glossary?.length) {
            addHeading('Glossary', 1);
            for (const term of anyData.glossary) {
              addText(`${term.term || ''}:`, margin, 11);
              if (term.definition) addText(`  ${term.definition}`, margin, 10);
              if (term.whyItMatters) addText(`  Why It Matters: ${term.whyItMatters}`, margin, 10);
            }
          }

          console.log('✅ Finished processing Client Renewal Presentation sections');
        }

        // Confidence and Limitations
        if (anyData.confidenceAndLimitations) {
          addHeading('Confidence & Limitations', 1);
          const cal = anyData.confidenceAndLimitations;
          // Handle simple string format
          if (typeof cal === 'string') {
            addText(`Analysis Confidence: ${cal}`, margin, 11);
          } else {
            // Handle object format
            if (cal.overallConfidence) addText(`Overall Confidence: ${cal.overallConfidence}`, margin, 11);
            if (cal.percentage) addText(`Confidence: ${cal.percentage}`, margin, 11);
            if (cal.limitations?.length) {
              addText('Limitations:', margin, 11);
              cal.limitations.forEach((lim: string) => addText(`• ${lim}`, margin + 5, 10));
            }
          }
        }

        // Professional Disclaimer
        if (anyData.professionalDisclaimer) {
          addHeading('Professional Disclaimer', 1);
          const pdText = typeof anyData.professionalDisclaimer === 'string'
            ? anyData.professionalDisclaimer : anyData.professionalDisclaimer?.text || '';
          if (pdText) addText(pdText, margin, 10);
        }
      }

      // === POLICY COMPARISON SPECIFIC SECTIONS ===
      console.log('📄 Processing Policy Comparison sections...');

      // === NON-STANDARD PROVISIONS ===
      if (anyData.nonStandardProvisions?.provisions?.length) {
        console.log('📝 Processing Non-Standard Provisions:', anyData.nonStandardProvisions.provisions.length, 'items');
        addHeading('Non-Standard Provisions', 2);

        for (const provision of anyData.nonStandardProvisions.provisions) {
          addHeading(provision.provision || 'Provision', 3);
          if (provision.location) addText(`Location: ${provision.location}`, margin, 11);
          if (provision.deviation) addText(`Deviation: ${provision.deviation}`, margin, 11);
          if (provision.riskAssessment) addText(`Risk Assessment: ${provision.riskAssessment}`, margin, 11);
        }
      }

      // === STRATEGIC CONSIDERATIONS ===
      if (anyData.strategicConsiderations?.length) {
        console.log('📝 Processing Strategic Considerations:', anyData.strategicConsiderations.length, 'items');
        addHeading('Strategic Considerations', 2);

        for (const consideration of anyData.strategicConsiderations) {
          addHeading(consideration.consideration || 'Consideration', 3);
          if (consideration.analysis) addText(`Analysis: ${consideration.analysis}`, margin, 11);

          if (consideration.options?.length) {
            addText('Options:', margin, 11);
            consideration.options.forEach((option: string, idx: number) => {
              addText(`  ${idx + 1}. ${option}`, margin + 5, 10);
            });
          }

          if (consideration.recommendedApproach) {
            addText(`Recommended Approach: ${consideration.recommendedApproach}`, margin, 11);
          }
        }
      }

      // === RISK MITIGATION STRATEGIES ===
      if (anyData.riskMitigationStrategies?.length) {
        console.log('📝 Processing Risk Mitigation Strategies:', anyData.riskMitigationStrategies.length, 'items');
        addHeading('Risk Mitigation Strategies', 2);

        for (const strategy of anyData.riskMitigationStrategies) {
          addHeading(strategy.risk || 'Risk', 3);
          if (strategy.currentExposure) addText(`Current Exposure: ${strategy.currentExposure}`, margin, 11);
          if (strategy.strategy) addText(`Strategy: ${strategy.strategy}`, margin, 11);
          if (strategy.implementation) addText(`Implementation: ${strategy.implementation}`, margin, 11);
          if (strategy.expectedReduction) addText(`Expected Reduction: ${strategy.expectedReduction}`, margin, 11);

          if (strategy.alternativeOptions?.length) {
            addText('Alternative Options:', margin, 11);
            strategy.alternativeOptions.forEach((option: string, idx: number) => {
              addText(`  ${idx + 1}. ${option}`, margin + 5, 10);
            });
          }
        }
      }

      // === RISK SHIFT NAVIGATOR ===
      if (anyData.riskShift) {
        console.log('📝 Processing Risk Shift Navigator:', Object.keys(anyData.riskShift));
        addHeading('Risk Shift Navigator', 2);
        const riskShift = anyData.riskShift;

        // TL;DR Summary
        if (riskShift.tldr) {
          addHeading('Summary (TL;DR)', 3);
          const tldrText = typeof riskShift.tldr === 'string' ? riskShift.tldr : riskShift.tldr?.text || '';
          if (tldrText) addText(tldrText, margin, 11);
        }

        // Key Talking Points
        if (riskShift.keyTalkingPoints?.length) {
          addHeading('Key Talking Points', 3);
          riskShift.keyTalkingPoints.forEach((point: string, idx: number) => {
            addText(`${idx + 1}. ${point}`, margin, 11);
          });
        }

        // Detailed Comparison Table
        if (riskShift.detailedComparisonTable?.headers && riskShift.detailedComparisonTable?.rows) {
          addHeading('Detailed Comparison', 3);
          addTable(riskShift.detailedComparisonTable.headers, riskShift.detailedComparisonTable.rows);
        }

        // Meta Analysis
        if (riskShift.metaAnalysis) {
          const meta = riskShift.metaAnalysis;

          // Suspicious Absences
          if (meta.suspiciousAbsences?.length) {
            addHeading('Suspicious Absences', 3);
            meta.suspiciousAbsences.forEach((absence: string) => {
              addText(`• ${absence}`, margin, 11);
            });
          }

          // Interconnectivity Analysis
          if (meta.interconnectivityAnalysis?.length) {
            addHeading('Interconnectivity Analysis', 3);
            for (const item of meta.interconnectivityAnalysis) {
              addText(`${item.interaction || ''}`, margin, 11);
              if (item.explanation) addText(`  Explanation: ${item.explanation}`, margin + 5, 10);
            }
          }

          // Strategic Actionability
          if (meta.strategicActionability?.length) {
            addHeading('Strategic Actions', 3);
            for (const action of meta.strategicActionability) {
              addText(`[${action.priority || ''}] ${action.action || ''}`, margin, 11);
              if (action.rationale) addText(`  Rationale: ${action.rationale}`, margin + 5, 10);
            }
          }

          // Blind Spot Acknowledgment
          if (meta.blindSpotAcknowledgment) {
            addHeading('Blind Spot Acknowledgment', 3);
            const bsText = meta.blindSpotAcknowledgment.text || '';
            if (bsText) addText(bsText, margin, 11);

            if (meta.blindSpotAcknowledgment.missingDocuments?.length) {
              addText('Missing Documents:', margin, 11);
              meta.blindSpotAcknowledgment.missingDocuments.forEach((doc: string) => {
                addText(`• ${doc}`, margin + 5, 10);
              });
            }
          }

          // Professional Disclaimer
          if (meta.professionalDisclaimer) {
            addHeading('Professional Disclaimer', 3);
            addText(meta.professionalDisclaimer, margin, 10);
          }
        }

        // Log confirmation of Risk Shift processing
        console.log('✅ Section processed: Risk Shift Navigator', {
          tldrRendered: !!riskShift.tldr,
          keyTalkingPointsCount: riskShift.keyTalkingPoints?.length || 0,
          tableRowsCount: riskShift.detailedComparisonTable?.rows?.length || 0,
          metaAnalysisKeys: riskShift.metaAnalysis ? Object.keys(riskShift.metaAnalysis) : []
        });
      }

      // === DOCUMENT COMPARISON SECTIONS (for policy_comparison workflow) ===
      if (anyData.documentComparisonSections?.length) {
        addHeading('Document Comparison Analysis', 2);

        for (const section of anyData.documentComparisonSections) {
          addHeading(section.sectionTitle || section.title || 'Section', 3);

          if (section.summary) {
            const summaryText = typeof section.summary === 'string' ? section.summary : section.summary?.text || '';
            if (summaryText) addText(summaryText, margin, 11);
          }

          if (section.comparisonPoints?.length) {
            for (const point of section.comparisonPoints) {
              addText(`• ${point.aspect || ''}:`, margin, 11);
              if (point.document1Value) addText(`  Document 1: ${point.document1Value}`, margin + 5, 10);
              if (point.document2Value) addText(`  Document 2: ${point.document2Value}`, margin + 5, 10);
              if (point.assessment) addText(`  Assessment: ${point.assessment}`, margin + 5, 10);
            }
          }

          if (section.differences?.length) {
            addText('Key Differences:', margin, 11);
            section.differences.forEach((diff: any) => {
              const diffText = typeof diff === 'string' ? diff : diff?.text || diff?.description || '';
              if (diffText) addText(`  • ${diffText}`, margin + 5, 10);
            });
          }

          if (section.implications?.length) {
            addText('Implications:', margin, 11);
            section.implications.forEach((imp: any) => {
              const impText = typeof imp === 'string' ? imp : imp?.text || '';
              if (impText) addText(`  • ${impText}`, margin + 5, 10);
            });
          }
        }
      }

      // Professional Disclaimer - always include at end of every report
      const DEFAULT_DISCLAIMER = 'This analysis is generated by an AI assistant and is intended for use by experienced insurance professionals as a supplementary tool, not as a substitute for their own expert judgment. By using this analysis, you acknowledge and agree to the following limitations:\n\nNot Legal or Professional Advice: This analysis is a form of sophisticated pattern recognition and language processing; it is not legal advice, a definitive coverage determination, or a substitute for the nuanced judgment of a seasoned human professional.\n\nKnowledge Cutoff: The analysis may not reflect the most recent court decisions, regulatory changes, or emerging legal interpretations.\n\nContext is Key: This analysis is strictly limited to the documents and context provided.\n\nAll findings must be independently verified by a qualified professional.';
      const disclaimerText = anyData.disclaimer ||
        (typeof anyData.professionalDisclaimer === 'string' ? anyData.professionalDisclaimer : anyData.professionalDisclaimer?.text) ||
        DEFAULT_DISCLAIMER;
      addHeading('Professional Disclaimer', 1);
      addText(disclaimerText, margin, 10);

      // Generate filename
      const filename = options.filename || `${this.getWorkflowTypeTitle(data.responseMetadata.workflowType)}-${new Date().toISOString().split('T')[0]}.pdf`;

      // Save PDF
      pdf.save(filename);

      console.log('✅ Text-based PDF export completed successfully');
      
    } catch (error) {
      console.error('❌ Text-based PDF export failed:', error);
      throw error;
    }
  }
}